import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Snackbar,
  Checkbox,
  TablePagination,
  InputAdornment,
  FormControlLabel,
  RadioGroup,
  Radio,
  Fade,
  Grow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Done as DoneIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  CreditCard as CreditCardIcon,
  Print as PrintIcon,
  Sync as SyncIcon,
  Warehouse as WarehouseIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  PendingActions as PendingActionsIcon,
  Inventory2 as Inventory2Icon,
  Search as SearchIcon,
  ReceiptLong as ReceiptLongIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import EInvoiceSendModal from '../../accounting/invoice/EInvoiceSendModal';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../../accounting/shared/StatsCard';
import { formatShortDate } from '../../../utils/dateUtils';
import N11PrintModal from './N11PrintModal';
import TrendyolPrintModal from './TrendyolPrintModal';
import HepsiburadaPrintModal from './HepsiburadaPrintModal';
import TrendyolCargoStickerModal from './TrendyolCargoStickerModal';
import OrderDetailsModal from './OrderDetailsModal';
import N11OrderDetailsModal from './N11OrderDetailsModal';
import HepsiburadaOrderDetailsModal from './HepsiburadaOrderDetailsModal';
import TrendyolOrderDetailsModal from './TrendyolOrderDetailsModal';
import TrendyolWebhookSettingsModal from './TrendyolWebhookSettingsModal';
import { useTrendyolWebhookMonitor, type TrendyolWebhookEvent } from '../hooks/useTrendyolWebhookMonitor';
import { ecommerceApi, hepsiburadaApi, hepsijetApi, mainApi, mngCargoApi, n11Api, trendyolMpApi, pazaramaApi, pttavmApi, einvoiceApi, accountingApi } from '../../../config/apiClient';
import WmsTransferModal from './WmsTransferModal';
import { orderTransferService, type TransferStatus } from '../../wms/services/orderTransferService';
import { smartWarehouseService } from '../../wms/services/smartWarehouseService';
import { useTranslation } from 'react-i18next';
import {
  buildOrderStatusToastMessage,
  resolveMarketplaceStatusFlowColor,
} from '../utils/marketplaceStatusLabels';
import { NETAPI_URL, EINVOICE_API, ACCOUNTING_API } from '../../../config/api';
import { infiniteRetryPolicy, startResilientConnection } from '../../../utils/signalRConnection';
import InvoiceDisplayModal from '../../accounting/invoice/InvoiceDisplayModal';
import type { InvoiceData } from '../../accounting/invoice/InvoiceDisplayModal';
// GTS_SUNUM_MOCK
import { isDemoCompany, isDemoOrder, persistDemoOrderStatus } from '../demo/demoMode';
import { getDemoMarketplaceOrders } from '../demo/demoMarketplaceOrders';

/**
 * Trendyol webhook izleyicisi (GET /api/TrendyolWebhook/recent) yalnizca yerel/dev
 * backend'de vardir; canli backend (aquasoft.com.tr) bu endpoint'i Production'da
 * 404 dondurur. Bu yuzden izleyiciyi SADECE trendyolMpApi yerel bir adrese
 * baktiginda acariz; aksi halde bos yere 4 sn'de bir 404 log'u uretmesin.
 */
const TRENDYOL_MP_IS_LOCAL_BACKEND = /localhost|127\.0\.0\.1/i.test(
  trendyolMpApi.defaults.baseURL ?? '',
);


const PLATFORM_BUTTONS = [
  { id: 'all',         label: 'Tüm Siparişler', short: 'T',   bg: '#546e7a', fg: '#ffffff' },
  { id: 'website',     label: 'E-Ticaret',       short: 'E',   bg: '#1976d2', fg: '#ffffff' },
  { id: 'trendyol',    label: 'Trendyol',        short: 'TY',  bg: '#f57c00', fg: '#ffffff' },
  { id: 'hepsiburada', label: 'Hepsiburada',     short: 'HB',  bg: '#ff6f00', fg: '#ffffff' },
  { id: 'n11',         label: 'N11',             short: 'N11', bg: '#8e24aa', fg: '#ffffff' },
  { id: 'pazarama',    label: 'Pazarama',        short: 'PZ',  bg: '#e6007e', fg: '#ffffff' },
  { id: 'pttavm',      label: 'PttAVM',          short: 'PTT', bg: '#e30613', fg: '#ffffff' },
] as const;

/** Ayni siparis/paket icin birden fazla kanal (SignalR + poll + backup) tek yenilemeye duser. */
const ORDERS_REFRESH_COOLDOWN_MS = 2500;
const TRENDYOL_SIGNALR_GRACE_MS = 3500;

const TRENDYOL_ACTION_FALLBACK_BASE_URLS = [
  process.env.REACT_APP_TRENDYOLMPAPI_URL,
  process.env.REACT_APP_TRENDYOLAPI_URL,
  'https://trendyolmp.aquasoft.com.tr',
  'https://gtstrendyol.aquasoft.com.tr',
  'http://localhost:5018',
].filter((url, index, arr): url is string => !!url && arr.indexOf(url) === index);

async function postTrendyolMpWithFallback(endpoint: string, payload: Record<string, unknown>) {
  try {
    return await trendyolMpApi.post(endpoint, payload);
  } catch (error: any) {
    const shouldTryFallback =
      error?.code === 'ERR_NETWORK' ||
      error?.message?.includes?.('Network Error') ||
      error?.response?.status === 404;

    if (!shouldTryFallback) {
      throw error;
    }

    let lastError = error;
    for (const baseURL of TRENDYOL_ACTION_FALLBACK_BASE_URLS) {
      try {
        return await trendyolMpApi.request({ method: 'POST', url: endpoint, data: payload, baseURL });
      } catch (fallbackError) {
        lastError = fallbackError;
      }
    }
    throw lastError;
  }
}

interface OrderItem {
  productId: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
}

interface ShippingAddress {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  district: string;
  phone: string;
  title: string;
}

interface PaymentInfo {
  method: string;
  status: string;
  dekontId?: string;
  transactionId?: string;
  paymentDate?: string;
}

interface Order {
  id: string;
  orderId: string;
  orderNumber: string;
  memberId: string;
  memberName?: string;
  memberEmail?: string;
  date: string;
  updatedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  status: 'received' | 'pending' | 'paid' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'cancel_requested';
  totalAmount: number;
  // TRY dışı mağazada sipariş anındaki kur snapshot'ı ve çekilen TL karşılığı
  appliedExchangeRate?: number | null;
  totalAmountTl?: number | null;
  /** Sipariş tutarlarının para birimi (null = eski kayıt → TL varsayılır) */
  currency?: string | null;
  itemsTotal?: number; // N11 siparişleri için sadece ürün fiyatları toplamı
  items: OrderItem[];
  shippingAddress?: ShippingAddress | any; // N11 için farklı yapı
  billingAddress?: any; // N11 fatura adresi
  paymentInfo?: PaymentInfo;
  trackingNumber?: string;
  shippingCompany?: string;
  /** Firmalar arası B2B sevk irsaliyesi (Waybills.Id) — irsaliye ekranına gitmek için */
  waybillId?: string | null;
  /** Firmalar arası B2B sevk irsaliyesi numarası (sevkiyatta otomatik kesilir; B2C'de boş) */
  waybillNumber?: string | null;
  /** Firmalar arası B2B siparişi mi (aktif cari/üye bağlantısı var) */
  isInterCompanyB2B?: boolean;
  /** B2B mal kabul (irsaliye onaylama): null | Approved | ApprovedWithDiscrepancy */
  receiptStatus?: string | null;
  hasReceiptDiscrepancy?: boolean;
  receiptConfirmedAt?: string | null;
  // ParamPOS Bilgileri
  dekontId?: string;
  islemId?: string;
  paramPosData?: string; // JSON string
  cancelRequest?: {
    reason: string;
    requestDate: string;
    requestedBy: string;
    adminResponse?: {
      rejected?: boolean;
      reason: string;
      rejectedAt?: string;
      respondedAt?: string;
      rejectedBy?: string;
    };
  };
  // N11 özel alanları
  marketplace?: string;
  n11OrderId?: string;
  n11Status?: string;
  customerName?: string;
  customerEmail?: string;
  isGuest?: boolean;
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  eInvoiceStatus?: string | null;
  eInvoiceEttn?: string | null;
  paymentBreakdownMode?: string | null;
  accountId?: number | null;
  shipmentPackageStatus?: string;
  packageHistories?: Array<{
    createdDate: number;
    status: string;
  }>;
  marketplaceLastModified?: string;
  marketplaceImportedAt?: string;
  cargoInfo?: {
    cargoSenderNumber?: string;
    cargoTrackingNumber?: string;
    cargoTrackingLink?: string;
    shipmentCompanyId?: string;
    cargoProviderName?: string;
    shipmentMethod?: number;
  };
}

interface Return {
  id: string;
  marketplace: string;
  claimReturnId: string;
  /** Tutarın para birimi (yalnız site iadelerinde dolar; null/boş = TL) */
  currency?: string | null;
  /** TRY dışı siparişte kur snapshot'ından hesaplanan TL karşılığı */
  amountTl?: number | null;
  claimId?: string;
  claimItemIds?: string[];
  orderNumber: string;
  productName: string;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  quantity: number;
  unitPrice: number;
  finalPrice: number;
  status: string;
  executer: string;
  requestDate: string;
  returnReasonType: string;
  returnReasonDescription: string;
  trackingNumber?: string;
  sellerTrackingNumber?: string;
  shipmentCompany?: string;
  campaignNumber?: string;
  productId: string;
  skuId?: string;
  attributesNames?: string;
  deliveryFeeType?: string;
  shipmentMethod?: string;
  paymentDate?: string;
  cancelledDate?: string;
  // ── Site (website) iadelerine özgü alanlar ──
  /** Ters kayıt para iadesi yöntemi: 'balance' | 'provider' (işlendiyse dolar) */
  refundMode?: string | null;
  /** İade faturası kesildiyse Id (idempotency/görsel bilgi) */
  returnInvoiceId?: string | null;
  /** Gider pusulası: null = takip yok, 'pending' = bekliyor, 'issued' = girildi */
  expenseVoucherStatus?: string | null;
  expenseVoucherNo?: string | null;
  /** Sipariş fatura bilgisi: unvan / VKN-TCKN / e-Fatura mükellefiyet beyanı */
  billingTitle?: string | null;
  billingTaxNumber?: string | null;
  billingEInvoiceTaxpayer?: boolean | null;
  /** true: vitrin iptal talebi (cancel_requested) — İADE TALEPLERİ listesine düşen sentetik kayıt */
  isCancelRequest?: boolean;
  /** Bağlı ecommerce sipariş id (iptal talebi onay/red için) */
  sourceOrderId?: string;
  /** İptal talebinin oluşturulma zamanı (ISO) */
  cancelRequestedAt?: string;
  /** Siparişin oluşturulma zamanı (ISO) */
  orderCreatedAt?: string;
}

interface OrdersManagementProps {
  companyId: string | null;
  /** Ön muhasebe sipariş sayfası gibi gömülü kullanımda dış boşluğu kapatır. */
  embedded?: boolean;
}

interface MemberData {
  firstName?: string;
  lastName?: string;
  email?: string;
  orders?: Record<string, any>;
}

interface OrderData {
  orderId?: string;
  orderNumber?: string;
  date?: string;
  status?: 'received' | 'pending' | 'paid' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled' | 'cancel_requested';
  totalAmount?: number;
  items?: OrderItem[];
  shippingAddress?: ShippingAddress;
  paymentInfo?: PaymentInfo;
  trackingNumber?: string;
  shippingCompany?: string;
  cancelRequest?: {
    reason: string;
    requestDate: string;
    requestedBy: string;
    adminResponse?: {
      rejected?: boolean;
      reason: string;
      rejectedAt?: string;
      respondedAt?: string;
      rejectedBy?: string;
    };
  };
}

const parseVariantData = (rawVariantData: any): Record<string, any> => {
  if (!rawVariantData) return {};

  if (typeof rawVariantData === 'string') {
    try {
      const parsed = JSON.parse(rawVariantData);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  return rawVariantData && typeof rawVariantData === 'object' ? rawVariantData : {};
};

const firstNonEmptyText = (...values: any[]): string | null => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (!text) continue;
    if (/^n11-item-idx-/i.test(text) || /^idx-\d+$/i.test(text)) continue;
    return text;
  }
  return null;
};

const extractN11LineIdFromVariantId = (variantId: any): string | null => {
  if (typeof variantId !== 'string') return null;
  const match = variantId.trim().match(/^n11-item-(.+)$/i);
  if (!match?.[1] || /^idx-/i.test(match[1])) return null;
  return match[1];
};

const parseMarketplaceItems = (rawItems: any): any[] => {
  if (!rawItems) return [];

  let items = rawItems;
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(items)) {
    if (typeof items === 'object') {
      items = [items];
    } else {
      return [];
    }
  }

  return items.map((item: any) => {
    const variantData = parseVariantData(item.variantData);
    const fromVariantId = extractN11LineIdFromVariantId(item.variantId || item.VariantId || variantData.variantId);
    const resolvedLineId = firstNonEmptyText(
      item.n11OrderLineId,
      item.n11LineId,
      item.orderLineId,
      item.lineId,
      item.lineItemId,
      item.lineitemId,
      variantData.n11OrderLineId,
      variantData.n11LineId,
      variantData.orderLineId,
      variantData.lineId,
      variantData.lineItemId,
      fromVariantId
    );

    return {
      productId: item.productId || item.id || item.productMainId || '',
      name: item.name || item.productName || item.productTitle || 'Ürün',
      image: item.productImage || item.imageUrl || item.image || '/api/placeholder/80/80',
      price: Number(item.unitPrice ?? item.price ?? item.salePrice ?? 0),
      quantity: Number(item.quantity ?? 1),
      lineId: resolvedLineId,
      lineItemId: item.lineItemId || item.lineitemId || variantData.lineItemId || resolvedLineId,
      n11LineId: firstNonEmptyText(
        item.n11LineId,
        variantData.n11LineId,
        variantData.lineId,
        variantData.orderLineId,
        resolvedLineId
      ),
      n11OrderLineId: firstNonEmptyText(
        item.n11OrderLineId,
        item.orderLineId,
        variantData.n11OrderLineId,
        variantData.orderLineId,
        variantData.lineId,
        resolvedLineId
      ),
      n11StockCode: item.n11StockCode || item.stockCode || item.sku || null,
      stockCode: item.stockCode || item.sku || null,
      barcode: item.barcode || null
    };
  });
};

const extractN11RawItems = (order: any): any[] => {
  if (!order || typeof order !== 'object') return [];

  const directCandidates = [
    order.items,
    order.lines,
    order.orderItems,
    order.orderItemList,
    order.lineItems,
    order.lineItemList,
    order.shipmentPackageItems,
    order.shipmentPackageItemList,
    order.packageItems,
    order.packageItemList,
    order.products
  ];

  for (const candidate of directCandidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  // Bazı N11 payload'larında kalemler package geçmişleri içinde gelebiliyor.
  if (Array.isArray(order.packageHistories)) {
    const nested: any[] = [];
    for (const history of order.packageHistories) {
      if (!history || typeof history !== 'object') continue;
      const historyCandidates = [
        history.items,
        history.lines,
        history.orderItems,
        history.orderItemList,
        history.shipmentPackageItems,
        history.shipmentPackageItemList
      ];
      for (const candidate of historyCandidates) {
        if (Array.isArray(candidate) && candidate.length > 0) {
          nested.push(...candidate);
        }
      }
    }
    if (nested.length > 0) return nested;
  }

  return [];
};

const normalizeOrderStatus = (status?: string): Order['status'] => {
  const raw = (status || '').trim().toLowerCase();
  const normalized = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

  if (['received', 'order received', 'sipariş alındı', 'siparis alindi', 'created', 'awaiting'].includes(normalized)) return 'received';
  if (['pending', 'awaiting approval', 'onay bekleyen', 'onay bekliyor', 'paketlenecek'].includes(normalized)) return 'pending';
  if (['confirmed', 'preparing', 'hazırlanıyor', 'hazirlaniyor', 'packed', 'gönderime hazır', 'gonderime hazir'].includes(normalized)) return 'confirmed';
  if (['shipped', 'kargoda', 'kargo'].includes(normalized)) return 'shipped';
  if (['delivered', 'teslim edildi'].includes(normalized)) return 'delivered';
  if (
    ['cancel requested', 'cancel request', 'cancelrequested', 'iptal talebi', 'iptal talebi inceleniyor'].includes(normalized) ||
    (normalized.includes('cancel') && normalized.includes('request')) ||
    (normalized.includes('iptal') && normalized.includes('talep'))
  ) return 'cancel_requested';
  if (
    ['cancelled', 'canceled', 'cancel', 'iptal edildi', 'iptal'].includes(normalized) ||
    (normalized.includes('cancel') && !normalized.includes('request')) ||
    (normalized.includes('iptal') && !normalized.includes('talep'))
  ) return 'cancelled';
  if (['paid', 'ödendi', 'odendi'].includes(normalized)) return 'paid';

  return 'received';
};


const isTerminalCancelledStatus = (status?: string): boolean => {
  const normalized = String(status || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  return [
    'cancelled',
    'canceled',
    'cancelled by customer',
    'cancelled by merchant',
    'cancelled by sap',
    'iptal edildi'
  ].includes(normalized);
};

const resolveOrderMarketplaceStatus = (order: Order): string =>
  String(
    (order as any).shipmentPackageStatus ||
    (order as any).n11Status ||
    (order as any).marketplaceStatus ||
    order.status ||
    ''
  );

const resolveMarketplaceToastSeverity = (
  status?: string,
  options?: { isNew?: boolean; isCancelled?: boolean }
): 'success' | 'error' | 'warning' | 'info' => {
  if (options?.isCancelled) return 'warning';
  const flow = resolveMarketplaceStatusFlowColor(status, options);
  if (flow === 'delivered') return 'success';
  if (flow === 'cancelled' || flow === 'cancel_requested' || flow === 'return_requests') return 'warning';
  return 'info';
};

const findFirstStringByKeys = (input: any, keys: string[]): string | null => {
  if (input === null || input === undefined) {
    return null;
  }

  if (Array.isArray(input)) {
    for (const item of input) {
      const found = findFirstStringByKeys(item, keys);
      if (found) return found;
    }
    return null;
  }

  if (typeof input !== 'object') {
    return null;
  }

  for (const [key, value] of Object.entries(input)) {
    if (keys.some((k) => k.toLowerCase() === key.toLowerCase()) && typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  for (const value of Object.values(input)) {
    const found = findFirstStringByKeys(value, keys);
    if (found) return found;
  }

  return null;
};

const normalizeMarketplaceSource = (marketplace?: string): string => {
  const normalized = (marketplace || '').trim().toLowerCase();
  if (!normalized) return 'website';
  return normalized;
};

const getMarketplaceVisualInfo = (marketplace?: string, t?: (key: string) => string) => {
  const source = normalizeMarketplaceSource(marketplace);

  switch (source) {
    case 'n11':
      return {
        name: 'N11',
        color: '#7b1fa2',
        bgColor: '#f3e5f5',
        hasLogo: true,
        logoSrc: '/assets/logoHighResNew.png'
      };
    case 'trendyol':
      return {
        name: 'Trendyol',
        color: '#f57c00',
        bgColor: '#fff3e0',
        hasLogo: false
      };
    case 'hepsiburada':
      return {
        name: 'Hepsiburada',
        color: '#ff6000',
        bgColor: '#fff3eb',
        hasLogo: false
      };
    case 'pazarama':
      return {
        name: 'Pazarama',
        color: '#e6007e',
        bgColor: '#fde6f3',
        hasLogo: false
      };
    case 'pttavm':
      return {
        name: 'PTTAVM',
        color: '#0072bc',
        bgColor: '#e9f5ff',
        hasLogo: false
      };
    case 'website':
      return {
        name: t ? t('ecommerceOrders.marketplaces.ecommerce') : 'E-Ticaret',
        color: '#1976d2',
        bgColor: '#e3f2fd',
        hasLogo: false
      };
    default:
      return {
        name: source.toUpperCase(),
        color: '#757575',
        bgColor: '#f5f5f5',
        hasLogo: false
      };
  }
};

const OrdersManagement: React.FC<OrdersManagementProps> = ({ companyId, embedded = false }) => {
  const { t } = useTranslation('salesSystem');
  const [activeTab, setActiveTab] = useState(0);
  const [orders, setOrders] = useState<Order[]>([]);
  const [returns, setReturns] = useState<Return[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showN11OrderModal, setShowN11OrderModal] = useState(false);
  const [showHepsiburadaOrderModal, setShowHepsiburadaOrderModal] = useState(false);
  const [showTrendyolOrderModal, setShowTrendyolOrderModal] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingCompany, setShippingCompany] = useState('');
  const [hepsiburadaCargoCompanies, setHepsiburadaCargoCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [orderToShip, setOrderToShip] = useState<Order | null>(null);
  const [shipmentConfirmOpen, setShipmentConfirmOpen] = useState(false);
  const [orderToSendShipment, setOrderToSendShipment] = useState<Order | null>(null);
  const orderToSendShipmentRef = useRef<Order | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Orphan ödemeler: Success + !IsConsumed (ops görünürlüğü)
  type OrphanPayment = {
    id: number;
    merchantOid: string;
    provider?: string;
    amount: number;
    currency?: string;
    userEmail?: string | null;
    ageMinutes: number;
    reconcileAttemptCount?: number;
  };
  const mapOrphanPayment = (raw: any): OrphanPayment => ({
    id: Number(raw?.id ?? raw?.Id ?? 0),
    merchantOid: String(raw?.merchantOid ?? raw?.MerchantOid ?? ''),
    provider: raw?.provider ?? raw?.Provider,
    amount: Number(raw?.amount ?? raw?.Amount ?? 0),
    currency: raw?.currency ?? raw?.Currency,
    userEmail: raw?.userEmail ?? raw?.UserEmail ?? null,
    ageMinutes: Number(raw?.ageMinutes ?? raw?.AgeMinutes ?? 0),
    reconcileAttemptCount: Number(raw?.reconcileAttemptCount ?? raw?.ReconcileAttemptCount ?? 0)
  });
  const [orphanPayments, setOrphanPayments] = useState<OrphanPayment[]>([]);
  const [retryingOrphanOid, setRetryingOrphanOid] = useState<string | null>(null);

  // Admin iptal yönetimi state'leri
  const [adminCancelModalOpen, setAdminCancelModalOpen] = useState(false);
  const [adminCancelReason, setAdminCancelReason] = useState('');
  const [orderToAdminCancel, setOrderToAdminCancel] = useState<Order | null>(null);
  const [adminCancelSubmitting, setAdminCancelSubmitting] = useState(false);

  // İade onaylama state'i
  const [approvingReturn, setApprovingReturn] = useState<string | null>(null);

  // İade reddetme state'leri
  const [rejectReturnModalOpen, setRejectReturnModalOpen] = useState(false);
  const [returnToReject, setReturnToReject] = useState<Return | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingReturn, setRejectingReturn] = useState(false);

  // İade/iptal detay popup (soft animasyon)
  const [returnDetailOpen, setReturnDetailOpen] = useState(false);
  const [returnDetailItem, setReturnDetailItem] = useState<Return | null>(null);

  // Site iadesi onay diyaloğu: ters kayıt seçenekleri (iade yöntemi, kargo iadesi,
  // e-Fatura mükellefi müşteri iade faturası) — pazaryeri iadelerinde kullanılmaz
  const [siteApproveDialogOpen, setSiteApproveDialogOpen] = useState(false);
  const [siteApproveTarget, setSiteApproveTarget] = useState<Return | null>(null);
  const [siteRefundMode, setSiteRefundMode] = useState<'balance' | 'provider'>('balance');
  const [siteRefundShipping, setSiteRefundShipping] = useState(false);
  const [siteCustomerIssuesInvoice, setSiteCustomerIssuesInvoice] = useState(false);
  const [siteApproveSaving, setSiteApproveSaving] = useState(false);
  // GİB mükellef sorgulama (VKN/TCKN e-Fatura kullanıcısı mı?)
  const [mukellefQuerying, setMukellefQuerying] = useState(false);
  const [mukellefResult, setMukellefResult] = useState<string | null>(null);
  // Sorgu başarılıysa karar GİB'den gelir; checkbox kilitlenir (elle hata önlenir)
  const [mukellefVerified, setMukellefVerified] = useState(false);

  // Site iadesi gider pusulası girişi (e-Arşiv/bireysel müşteri iadesi)
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherTarget, setVoucherTarget] = useState<Return | null>(null);
  const [voucherNo, setVoucherNo] = useState('');
  const [voucherSaving, setVoucherSaving] = useState(false);

  // Pazaryeri sipariş senkronizasyonu state'leri
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [webhookSettingsOpen, setWebhookSettingsOpen] = useState(false);
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [selectedPrintOrder, setSelectedPrintOrder] = useState<Order | null>(null);
  const [trendyolPrintModalOpen, setTrendyolPrintModalOpen] = useState(false);
  const [selectedTrendyolPrintOrder, setSelectedTrendyolPrintOrder] = useState<Order | null>(null);
  const [trendyolStickerModalOpen, setTrendyolStickerModalOpen] = useState(false);
  const [selectedTrendyolStickerOrder, setSelectedTrendyolStickerOrder] = useState<Order | null>(null);
  const [hepsiburadaPrintModalOpen, setHepsiburadaPrintModalOpen] = useState(false);
  const [selectedHepsiburadaPrintOrder, setSelectedHepsiburadaPrintOrder] = useState<Order | null>(null);

  // WMS Transfer
  const [wmsStatuses, setWmsStatuses] = useState<Record<string, TransferStatus>>({});
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [wmsTransferModalOpen, setWmsTransferModalOpen] = useState(false);
  const [wmsTransferOrders, setWmsTransferOrders] = useState<{ id: string; orderNumber: string; customerName: string; platform?: string }[]>([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [ordersToInvoice, setOrdersToInvoice] = useState<Order[]>([]);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceDisplayOpen, setInvoiceDisplayOpen] = useState(false);
  const [selectedInvoiceForDisplay, setSelectedInvoiceForDisplay] = useState<InvoiceData | null>(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [eInvoiceSendOrder, setEInvoiceSendOrder] = useState<Order | null>(null);
  const [statsVisible, setStatsVisible] = useState(true);
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [guestOnly, setGuestOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'productCount'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [returnSortBy, setReturnSortBy] = useState<'date' | 'amount'>('date');
  const [returnSortDir, setReturnSortDir] = useState<'asc' | 'desc'>('desc');
  const [returnPage, setReturnPage] = useState(0);
  const [returnRowsPerPage, setReturnRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsMenuOrderId, setActionsMenuOrderId] = useState<string | null>(null);
  const knownOrderIdsRef = useRef<Set<string>>(new Set());
  const didHydrateOrdersRef = useRef(false);
  const lastMarketplaceToastKeyRef = useRef<string>('');
  const lastMarketplaceToastAtRef = useRef(0);
  // Realtime notification refresh debouncer: ardı ardına gelen webhooklar tek
  // refresh'e toplansin; backend commit/cache yarışlarına karşı 1500ms sonra
  // garanti yedek 2. fetch atılsın.
  const refreshDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshBackupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTrendyolSignalRAtRef = useRef<number>(Date.now());
  const lastOrdersRefreshAtByKeyRef = useRef<Record<string, number>>({});
  const headerMenuOpen = Boolean(headerMenuAnchorEl);

  const scheduleOrdersRefresh = (key: string, source: string): boolean => {
    const normalizedKey = (key || 'global').trim();
    const now = Date.now();
    const last = lastOrdersRefreshAtByKeyRef.current[normalizedKey] ?? 0;
    if (now - last < ORDERS_REFRESH_COOLDOWN_MS) {
      console.info(
        '%c[TY Webhook] Liste yenileme atlandi (yakin zamanda tetiklendi)',
        'color:#9e9e9e',
        { key: normalizedKey, source, msSinceLast: now - last }
      );
      return false;
    }
    lastOrdersRefreshAtByKeyRef.current[normalizedKey] = now;
    setRefreshNonce((prev) => prev + 1);
    return true;
  };
  


  // Kargo firmaları listesi
  const shippingCompanies = [
    { value: 'yurtici', label: 'Yurt İçi Kargo' },
    { value: 'ptt', label: 'PTT' },
    { value: 'aras', label: 'Aras Kargo' },
    { value: 'surat', label: 'Sürat Kargo' },
    { value: 'hepsijet', label: 'HepsiJet' },
    { value: 'kolaygelsin', label: 'Kolay Gelsin' },
    { value: 'dhl', label: 'DHL' },
    { value: 'ups', label: 'UPS' },
    { value: 'other', label: 'Diğer' }
  ];

  // Tab'lar. Siparişler > E-Ticaret (embedded) B2B akışıdır: onay adımı yok,
  // sevk sonrası durum "Sevkte" görünür. Normal e-ticaret ekranı aynı kalır.
  const tabs = [
    { label: t('ecommerceOrders.tabs.received'), status: 'received', color: 'success' },
    ...(!embedded
      ? [{ label: t('ecommerceOrders.tabs.pending'), status: 'pending', color: 'warning' }]
      : []),
    { label: t('ecommerceOrders.tabs.preparing'), status: 'confirmed', color: 'info' },
    {
      label: embedded ? t('ecommerceOrders.tabs.onWaybill') : t('ecommerceOrders.tabs.shipped'),
      status: 'shipped',
      color: 'primary',
    },
    { label: t('ecommerceOrders.tabs.delivered'), status: 'delivered', color: 'success' },
    { label: t('ecommerceOrders.tabs.cancelRequests'), status: 'cancel_requested', color: 'secondary' },
    { label: t('ecommerceOrders.tabs.cancelled'), status: 'cancelled', color: 'error' },
    ...(!embedded
      ? [{ label: t('ecommerceOrders.tabs.returnRequests'), status: 'return_requests', color: 'warning' }]
      : []),
  ];

  const goToStatusTab = (status: string) => {
    const index = tabs.findIndex((tab) => tab.status === status);
    if (index < 0) return;
    setActiveTab(index);
    setPage(0);
  };

  // Sipariş tutarları SİPARİŞİN para birimindedir (currency snapshot'ı; null = TL).
  // TRY dışı siparişte tutarın yanında kur snapshot'ından TL karşılığı gösterilir.
  const CURRENCY_SYMBOLS: Record<string, string> = { TRY: '₺', TL: '₺', USD: '$', EUR: '€', GBP: '£' };
  const resolveCurrencyCode = (currency?: string | null) => (currency || 'TRY').toUpperCase();
  const formatAmountWithCurrency = (value: number, currency?: string | null) => {
    const code = resolveCurrencyCode(currency);
    const formatted = (Number(value) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
    const symbol = CURRENCY_SYMBOLS[code];
    return symbol ? `${symbol}${formatted}` : `${formatted} ${code}`;
  };
  const orderTlEquivalent = (order: Order): number | null => {
    if (resolveCurrencyCode(order.currency) === 'TRY') return null;
    if (order.totalAmountTl && order.totalAmountTl > 0) return order.totalAmountTl;
    if (order.appliedExchangeRate && order.appliedExchangeRate > 0) {
      return Math.round(order.totalAmount * order.appliedExchangeRate * 100) / 100;
    }
    return null;
  };
  const formatTl = (value: number) =>
    `₺${(Number(value) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;

  const isN11Order = (order: Order): boolean =>
    order.id?.startsWith('n11_') || normalizeMarketplaceSource((order as any).marketplace) === 'n11';

  const isTrendyolOrder = (order: Order): boolean =>
    normalizeMarketplaceSource((order as any).marketplace) === 'trendyol';

  const isHepsiburadaOrder = (order: Order): boolean =>
    normalizeMarketplaceSource((order as any).marketplace) === 'hepsiburada' ||
    String(order.memberEmail || '').toLowerCase().includes('@hepsiburada.marketplace.local') ||
    String(order.orderNumber || '').toUpperCase().startsWith('HB-');

  const isPazaramaOrder = (order: Order): boolean =>
    normalizeMarketplaceSource((order as any).marketplace) === 'pazarama' ||
    String(order.orderNumber || '').toUpperCase().startsWith('PZ-');

  const isWebsiteOrder = (order: Order): boolean =>
    !isN11Order(order) &&
    !isTrendyolOrder(order) &&
    !isHepsiburadaOrder(order) &&
    normalizeMarketplaceSource(order.marketplace) === 'website';

  const isOrderPaid = (order: Order): boolean =>
    String(order.paymentInfo?.status || '').toLowerCase() === 'completed';

  const canInvoiceWebsiteOrder = (order: Order): boolean =>
    isWebsiteOrder(order) &&
    !order.invoiceId &&
    isOrderPaid(order) &&
    order.status !== 'cancelled' &&
    order.status !== 'cancel_requested' &&
    !isDemoOrder(order.id);

  const mapAccountingInvoice = (inv: any): InvoiceData => ({
    id: inv.id,
    invoiceNumber: inv.invoiceNumber,
    account: {
      companyName: inv.accountName || '',
      taxNumber: inv.accountTaxNumber || '',
      taxOffice: inv.accountTaxOffice || '',
      address: inv.accountAddress || '',
      phone: inv.accountPhone || '',
      email: inv.accountEmail || '',
    },
    date: inv.invoiceDate || inv.date,
    dueDate: inv.dueDate,
    items: inv.items?.map((item: any) => ({
      itemName: item.itemName || item.name,
      quantity: item.quantity,
      unit: item.unit,
      unitPrice: item.unitPrice,
      vatRate: item.vatRate || 0,
      sctRate: item.sctRate || 0,
      discountRate: item.discountRate || 0,
      lineTotal: item.netTotal || (item.quantity * item.unitPrice),
      vatAmount: item.vatAmount || item.vatTotal || 0,
      sctAmount: item.sctAmount || 0,
      grossTotal: item.grossTotal || 0,
    })) || [],
    notes: inv.notes,
    subtotal: inv.subtotal || 0,
    vatTotal: inv.vatTotal || 0,
    sctTotal: inv.sctTotal || 0,
    discountTotal: inv.discountTotal || 0,
    grandTotal: inv.grandTotal || 0,
    currency: inv.currency || 'TRY',
    type: 'sales',
    sellerCompanyName: inv.sellerCompanyName,
    sellerTaxNumber: inv.sellerTaxNumber,
    sellerTaxOffice: inv.sellerTaxOffice,
    sellerAddress: inv.sellerAddress,
    sellerPhone: inv.sellerPhone,
    sellerEmail: inv.sellerEmail,
  });

  const handleViewWebsiteInvoice = async (order: Order) => {
    if (!order.invoiceId) return;
    try {
      setLoadingInvoiceDetail(true);
      setInvoiceDisplayOpen(true);
      const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(order.invoiceId));
      const result = response.data;
      if (result.success && result.data) {
        setSelectedInvoiceForDisplay(mapAccountingInvoice(result.data));
      } else {
        setInvoiceDisplayOpen(false);
        showSnackbar(result.message || 'Fatura detayı yüklenemedi.', 'error');
      }
    } catch (err: any) {
      setInvoiceDisplayOpen(false);
      showSnackbar(err?.response?.data?.message || err?.message || 'Fatura detayı yüklenemedi.', 'error');
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const openInvoiceDialog = (targetOrders: Order[]) => {
    if (targetOrders.length === 0) return;
    setOrdersToInvoice(targetOrders);
    setInvoiceDialogOpen(true);
  };

  const handleConfirmInvoice = async () => {
    if (ordersToInvoice.length === 0) return;
    setInvoicing(true);
    let successCount = 0;
    let lastError = '';
    try {
      for (const order of ordersToInvoice) {
        try {
          const response = await ecommerceApi.post('/api/ecommerce/orders/posting/retry', {
            orderId: order.id,
          });
          const data = response.data;
          if (data?.success) {
            successCount += 1;
            setOrders((prev) =>
              prev.map((row) =>
                row.id === order.id
                  ? {
                      ...row,
                      invoiceId: data.invoiceId || row.invoiceId,
                      invoiceNumber: data.invoiceNumber || row.invoiceNumber,
                    }
                  : row
              )
            );
          } else {
            lastError = data?.message || 'Faturalama başarısız.';
          }
        } catch (err: any) {
          lastError =
            err?.response?.data?.message ||
            err?.message ||
            'Faturalama sırasında bir hata oluştu.';
        }
      }

      if (successCount > 0) {
        showSnackbar(
          successCount === 1 ? 'Sipariş faturalandı.' : `${successCount} sipariş faturalandı.`,
          'success'
        );
        setRefreshNonce((prev) => prev + 1);
      }
      if (lastError && successCount < ordersToInvoice.length) {
        showSnackbar(lastError, 'error');
      }
      setInvoiceDialogOpen(false);
      setSelectedOrderIds((prev) => prev.filter((id) => !ordersToInvoice.some((order) => order.id === id)));
      setOrdersToInvoice([]);
    } finally {
      setInvoicing(false);
    }
  };

  // Pazarama order-actions item bazli calisir. Bridge variantId'yi `pz-item-<orderItemId>`
  // olarak yaziyor; siparis kalemlerinden orderItemId listesini cozer.
  const resolvePazaramaOrderItemIds = (order?: Order): string[] => {
    const items = parseMarketplaceItems(order?.items);
    return items
      .map((item: any) => {
        const variantId = item?.variantId ?? item?.VariantId;
        const raw =
          item?.orderItemId ??
          item?.OrderItemId ??
          (typeof variantId === 'string' ? variantId.replace(/^pz-item-/i, '') : null);
        return raw ? String(raw) : null;
      })
      .filter((x: string | null): x is string => !!x);
  };

  // Pazarama fatura linki gonderme (order-actions/invoice-link).
  const handlePazaramaInvoiceLink = async (order: Order) => {
    // GTS_SUNUM_MOCK
    if (isDemoOrder(order.id)) {
      showSnackbar('Pazarama fatura linki gönderildi (sunum demo).', 'success');
      return;
    }

    const invoiceLink = window.prompt('Pazarama fatura linki (URL) giriniz:');
    if (!invoiceLink || !invoiceLink.trim()) return;

    const orderId =
      (order as any)?.marketplaceOrderId ||
      (order as any)?.marketplaceOrderNumber ||
      String(order.orderNumber || '').replace(/^PZ-/i, '').trim();

    setUpdating(order.id);
    try {
      await pazaramaApi.post('/api/pazarama/order-actions/invoice-link', {
        orderId,
        invoiceLink: invoiceLink.trim()
      });
      showSnackbar('Pazarama fatura linki gönderildi.', 'success');
    } catch (err) {
      console.error('❌ [ORDERS MANAGEMENT] Pazarama fatura linki hatası:', err);
      showSnackbar('Pazarama fatura linki gönderilemedi.', 'error');
    } finally {
      setUpdating(null);
    }
  };

  // Snackbar göster
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // GTS_SUNUM_MOCK: demo sipariş statü güncelleme (API yok)
  const applyDemoOrderStatusUpdate = (
    orderId: string,
    newStatus: string,
    message: string,
    extra?: Partial<Order>
  ) => {
    if (!companyId) return;
    const normalized = normalizeOrderStatus(newStatus);
    persistDemoOrderStatus(companyId, orderId, normalized);
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? {
              ...order,
              ...extra,
              status: normalized,
              n11Status: newStatus,
              shipmentPackageStatus: newStatus,
            }
          : order
      )
    );
    showSnackbar(message, 'success');
    setUpdating(null);
  };

  const showMarketplaceOrderToast = (
    label: 'Hepsiburada' | 'Trendyol' | 'Pazarama' | 'PttAVM',
    orderNumber: string,
    status?: string,
    options?: { isNew?: boolean; isCancelled?: boolean }
  ) => {
    const safeOrderNumber = orderNumber || '-';
    const dedupStatus = status || (options?.isNew ? 'new' : options?.isCancelled ? 'cancelled' : 'updated');
    const toastKey = `${label}:${safeOrderNumber}:${dedupStatus}:${options?.isNew ? '1' : '0'}:${options?.isCancelled ? '1' : '0'}`;
    const now = Date.now();

    if (lastMarketplaceToastKeyRef.current === toastKey && (now - lastMarketplaceToastAtRef.current) < 3000) {
      return;
    }

    lastMarketplaceToastKeyRef.current = toastKey;
    lastMarketplaceToastAtRef.current = now;

    const message = buildOrderStatusToastMessage(safeOrderNumber, status, options);
    const severity = resolveMarketplaceToastSeverity(status, options);
    showSnackbar(`${label}: ${message}`, severity);
  };

  // Snackbar kapat
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // MNG (DHL) kargo oluştur (yalnızca e-ticaret / website siparişleri için).
  const handleCreateMngShipment = async (order: Order) => {
    if (!companyId) {
      showSnackbar('Şirket bilgisi bulunamadı.', 'error');
      return;
    }

    setUpdating(order.id);
    try {
      const response = await mngCargoApi.post('/api/MngCargo/shipments', {
        ecommerceOrderId: order.id,
        totalParcels: 1
      });
      const data = response.data;
      const isSuccess = data?.success !== false && data?.Success !== false;
      const trackingNumber = data?.trackingNumber || data?.TrackingNumber;
      const referenceId = data?.referenceId || data?.ReferenceId;
      const message =
        data?.message ||
        data?.Message ||
        (isSuccess
          ? `MNG kargo oluşturuldu (#${order.orderNumber})`
          : 'MNG kargo oluşturulamadı.');
      showSnackbar(
        isSuccess && (trackingNumber || referenceId)
          ? `${message}${trackingNumber ? ` • Takip: ${trackingNumber}` : ''}${referenceId ? ` • Ref: ${referenceId}` : ''}`
          : message,
        isSuccess ? 'success' : 'error'
      );
      if (isSuccess) {
        setRefreshNonce(prev => prev + 1);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        'MNG kargo oluşturulurken bir hata oluştu.';
      showSnackbar(msg, 'error');
    } finally {
      setUpdating(null);
    }
  };

  // HepsiJet kargo oluştur (yalnızca e-ticaret / website siparişleri için).
  // Pazaryeri siparişlerinde kargo zaten pazaryeri tarafından yönetilir.
  const handleCreateHepsiJetShipment = async (order: Order) => {
    if (!companyId) {
      showSnackbar('Şirket bilgisi bulunamadı.', 'error');
      return;
    }

    setUpdating(order.id);
    try {
      const response = await hepsijetApi.post('/api/hepsijet/shipments', {
        ecommerceOrderId: order.id,
        totalParcels: 1
      });
      const data = response.data;
      const trackingUrl = data?.trackingUrl || data?.TrackingUrl;
      showSnackbar(
        `HepsiJet kargo oluşturuldu (#${order.orderNumber})${trackingUrl ? ' • Takip linki hazır' : ''}`,
        'success'
      );
      // Backend EcommerceOrder durumunu da günceller; listeyi tazele.
      setRefreshNonce(prev => prev + 1);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        'HepsiJet kargo oluşturulurken bir hata oluştu.';
      showSnackbar(msg, 'error');
    } finally {
      setUpdating(null);
    }
  };

  // Siparişleri yükle (TEK SEFERLİK - E-ticaret + N11)
  useEffect(() => {
    if (!companyId) return;

    const mapSqlOrder = (order: any): Order => {
      const firstName = String(order.customerFirstName || '').trim();
      const lastName = String(order.customerLastName || '').trim();
      const displayName = `${firstName} ${lastName}`.trim() || 'SQL Müşteri';

      // Kargo/fatura adresindeki eski isim snapshot'ı (örn. Aysel) üye adını ezmesin
      const syncAddressName = (raw: any) => {
        if (!raw) return undefined;
        let addr = raw;
        if (typeof raw === 'string') {
          try { addr = JSON.parse(raw); } catch { return { address: raw }; }
        }
        if (!addr || typeof addr !== 'object') return addr;
        return {
          ...addr,
          firstName: firstName || addr.firstName || addr.FirstName,
          lastName: lastName || addr.lastName || addr.LastName,
          FirstName: firstName || addr.FirstName || addr.firstName,
          LastName: lastName || addr.LastName || addr.lastName,
        };
      };

      return {
        id: order.id,
        orderId: order.id,
        orderNumber: order.orderNumber,
        memberId: order.memberId || 'unknown',
        memberName: displayName,
        customerName: displayName,
        isGuest: order.isGuest === true || !order.memberId,
        memberEmail: order.customerEmail || 'sql@example.com',
        date: order.createdAt || new Date().toISOString(),
        updatedAt: order.updatedAt,
        cancelledAt: order.cancelledAt,
        cancelReason: order.cancelReason,
        status: normalizeOrderStatus(order.orderStatus),
        totalAmount: order.totalAmount || 0,
        itemsTotal: order.subTotal ?? order.itemsTotal,
        appliedExchangeRate: order.appliedExchangeRate ?? null,
        totalAmountTl: order.totalAmountTl ?? null,
        currency: order.currency ?? null,
        items: order.items?.map((item: any) => ({
          productId: item.productId,
          name: item.productName,
          image: item.productImage || '/api/placeholder/80/80',
          price: item.unitPrice,
          quantity: item.quantity
        })) || [],
        shippingAddress: syncAddressName(order.shippingAddress),
        billingAddress: syncAddressName(order.billingAddress),
        cargoInfo: order.cargoInfo,
        paymentInfo: {
          method: order.paymentMethod || 'credit_card',
          status: order.paymentStatus || 'completed',
          transactionId: order.paymentTransactionId
        },
        invoiceId: order.invoiceId || order.InvoiceId || null,
        invoiceNumber: order.invoiceNumber || order.InvoiceNumber || null,
        eInvoiceStatus: order.eInvoiceStatus ?? order.EInvoiceStatus ?? null,
        eInvoiceEttn: order.eInvoiceEttn ?? order.EInvoiceEttn ?? null,
        paymentBreakdownMode: order.paymentBreakdownMode ?? order.PaymentBreakdownMode ?? null,
        accountId: order.accountId ?? order.AccountId ?? null,
        trackingNumber: order.trackingNumber,
        shippingCompany: order.shippingCompany,
        waybillId: order.waybillId || order.WaybillId || null,
        waybillNumber: order.waybillNumber || order.WaybillNumber || null,
        isInterCompanyB2B: !!(order.isInterCompanyB2B ?? order.IsInterCompanyB2B),
        receiptStatus: order.receiptStatus ?? order.ReceiptStatus ?? null,
        hasReceiptDiscrepancy: !!(order.hasReceiptDiscrepancy ?? order.HasReceiptDiscrepancy),
        receiptConfirmedAt: order.receiptConfirmedAt ?? order.ReceiptConfirmedAt ?? null,
        marketplace: order.marketplaceSource
          ? String(order.marketplaceSource).toLowerCase()
          : (String(order.orderNumber || '').startsWith('ORD') ? 'website' : undefined),
        dekontId: order.dekontId,
        islemId: order.islemId,
        paramPosData: order.paramPosData,
        cancelRequest: order.cancelReason
          ? {
              reason: order.cancelReason,
              requestDate: order.updatedAt || order.createdAt,
              requestedBy: order.customerEmail || 'unknown'
            }
          : undefined
      };
    };

    const mapMarketplaceOrder = (order: any): Order => {
      let parsedShippingAddress = order.shippingAddress;
      let parsedBillingAddress = order.billingAddress;
      let parsedCargoInfo = order.cargoInfo;
      let parsedPackageHistories = order.packageHistories;

      try {
        if (typeof parsedShippingAddress === 'string') parsedShippingAddress = JSON.parse(parsedShippingAddress);
      } catch { /* no-op */ }
      try {
        if (typeof parsedBillingAddress === 'string') parsedBillingAddress = JSON.parse(parsedBillingAddress);
      } catch { /* no-op */ }
      try {
        if (typeof parsedCargoInfo === 'string') parsedCargoInfo = JSON.parse(parsedCargoInfo);
      } catch { /* no-op */ }
      try {
        if (typeof parsedPackageHistories === 'string') parsedPackageHistories = JSON.parse(parsedPackageHistories);
      } catch { /* no-op */ }

      const marketplaceSource = String(order.marketplaceSource || 'website').toLowerCase();

      return {
        id: order.id,
        orderId: order.id,
        orderNumber: order.orderNumber || order.marketplaceOrderNumber || order.marketplaceOrderId || order.id,
        memberId: `${marketplaceSource}_customer_${(order.customerEmail || 'unknown').replace(/[@.#$\[\]]/g, '_')}`,
        memberName: order.customerName || `${order.customerFirstName || ''} ${order.customerLastName || ''}`.trim() || 'Pazaryeri Müşteri',
        memberEmail: order.customerEmail || 'marketplace@example.com',
        date: order.createdAt || new Date().toISOString(),
        status: normalizeOrderStatus(order.orderStatus || order.marketplaceStatus),
        totalAmount: order.totalAmount || 0,
        itemsTotal: order.subTotal || order.totalAmount || 0,
        items: parseMarketplaceItems(order.items),
        shippingAddress: parsedShippingAddress,
        billingAddress: parsedBillingAddress,
        trackingNumber: parsedCargoInfo?.cargoTrackingNumber || '',
        shippingCompany: parsedCargoInfo?.cargoProviderName || '',
        marketplace: marketplaceSource,
        n11OrderId: order.marketplaceOrderId,
        n11Status: order.marketplaceStatus,
        shipmentPackageStatus: order.shipmentPackageStatus,
        packageHistories: Array.isArray(parsedPackageHistories) ? parsedPackageHistories : [],
        cargoInfo: parsedCargoInfo,
        cargoBarcode:
          parsedCargoInfo?.cargoBarcode ||
          parsedCargoInfo?.cargoSenderNumber ||
          undefined,
        marketplaceLastModified: order.marketplaceLastModified || undefined,
        marketplaceImportedAt: order.marketplaceImportedAt || undefined,
        customerName: order.customerName || `${order.customerFirstName || ''} ${order.customerLastName || ''}`.trim(),
        customerEmail: order.customerEmail || undefined
      } as Order;
    };

    const loadOrphanPayments = async () => {
      try {
        const res = await ecommerceApi.get('/api/ecommerce/payments/orphans', {
          params: { minAgeMinutes: 10, page: 1, pageSize: 25 }
        });
        const list = res.data?.data ?? res.data?.Data;
        setOrphanPayments(Array.isArray(list) ? list.map(mapOrphanPayment) : []);
      } catch (err) {
        console.warn('[ORDERS MANAGEMENT] Orphan payments yüklenemedi:', err);
        setOrphanPayments([]);
      }
    };

    const loadOrders = async () => {
      // İlk yüklemede full loading; yenilemede arka planda soft refresh (5-10sn blokaj olmasın)
      const isInitialLoad = !didHydrateOrdersRef.current;
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      try {
        const cacheBuster = Date.now();
        void loadOrphanPayments();
        const [ordersResult, marketplaceResult] = await Promise.allSettled([
          ecommerceApi.get(`/api/ecommerce/orders?_t=${cacheBuster}`),
          embedded
            ? Promise.resolve({ data: { success: true, data: [] } })
            : ecommerceApi.get(`/api/ecommerce/orders/marketplace?_t=${cacheBuster}`)
        ]);

        let sqlOrders: Order[] = [];
        let sqlMarketplaceOrders: Order[] = [];

        if (ordersResult.status === 'fulfilled') {
          const sqlData = ordersResult.value.data;
          if (sqlData?.success && sqlData.data) {
            sqlOrders = sqlData.data.map(mapSqlOrder);
            if (embedded) {
              sqlOrders = sqlOrders.filter((order) => {
                const source = normalizeMarketplaceSource((order as any).marketplace);
                return !source || source === 'website';
              });
            }
          }
        } else {
          console.error('[ORDERS MANAGEMENT] SQL siparişleri yüklenirken hata:', ordersResult.reason);
        }

        if (marketplaceResult.status === 'fulfilled') {
          const marketplaceData = marketplaceResult.value.data;
          if (marketplaceData?.success && Array.isArray(marketplaceData.data)) {
            sqlMarketplaceOrders = marketplaceData.data.map(mapMarketplaceOrder);
          }
        } else {
          console.error('[ORDERS MANAGEMENT] Pazaryeri siparişleri yüklenirken hata:', marketplaceResult.reason);
        }

        const mergedOrders = [...sqlOrders, ...sqlMarketplaceOrders];
        const uniqueOrdersById = new Map<string, Order>();
        mergedOrders.forEach((order) => {
          uniqueOrdersById.set(order.id, order);
        });
        // GTS_SUNUM_MOCK: sunum firması için pazaryeri demo siparişleri
        // Muhasebe Siparişler sekmesinde pazaryeri karışmasın.
        if (!embedded && isDemoCompany(companyId)) {
          getDemoMarketplaceOrders(companyId).forEach((demoOrder) => {
            uniqueOrdersById.set(demoOrder.id, demoOrder as Order);
          });
        }

        const allOrders = Array.from(uniqueOrdersById.values())
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        if (didHydrateOrdersRef.current) {
          const newHepsiburadaOrders = allOrders.filter(
            (order) =>
              normalizeMarketplaceSource(order.marketplace) === 'hepsiburada' &&
              !knownOrderIdsRef.current.has(order.id)
          );

          if (newHepsiburadaOrders.length > 0) {
            const firstHepsiburadaOrder = newHepsiburadaOrders[0];
            if (isTerminalCancelledStatus(firstHepsiburadaOrder.status)) {
              showMarketplaceOrderToast(
                'Hepsiburada',
                firstHepsiburadaOrder.orderNumber,
                resolveOrderMarketplaceStatus(firstHepsiburadaOrder),
                { isCancelled: true }
              );
            } else {
              showMarketplaceOrderToast(
                'Hepsiburada',
                firstHepsiburadaOrder.orderNumber,
                resolveOrderMarketplaceStatus(firstHepsiburadaOrder),
                { isNew: true }
              );
            }
          }

          const newTrendyolOrders = allOrders.filter(
            (order) => isTrendyolOrder(order) && !knownOrderIdsRef.current.has(order.id)
          );
          if (newTrendyolOrders.length > 0) {
            console.info(
              '%c[TY Webhook] Listede YENI Trendyol siparis gorundu',
              'color:#f57c00;font-weight:bold;font-size:13px',
              newTrendyolOrders.map((o) => ({
                orderNumber: o.orderNumber,
                status: o.status,
                marketplaceStatus: (o as any).n11Status ?? (o as any).marketplaceStatus,
                id: o.id,
                sekme: o.status === 'received' ? 'Siparis Alindi' : o.status === 'confirmed' ? 'Hazirlaniyor' : o.status
              }))
            );
            const firstTy = newTrendyolOrders[0];
            if (!isTerminalCancelledStatus(firstTy.status)) {
              showMarketplaceOrderToast(
                'Trendyol',
                firstTy.orderNumber,
                resolveOrderMarketplaceStatus(firstTy),
                { isNew: true }
              );
            }
          }
        }

        knownOrderIdsRef.current = new Set(allOrders.map((order) => order.id));
        didHydrateOrdersRef.current = true;

        const trendyolOrders = allOrders.filter((o) => isTrendyolOrder(o));
        if (trendyolOrders.length === 0) {
          console.warn(
            '%c[TY Webhook] Bu firmada Trendyol siparisi yok',
            'color:#ef6c00;font-weight:bold',
            {
              companyId,
              ipucu: 'Simule webhook WxYC1aGY... firmasina yazar; gts-web oturum CompanyId ayni mi kontrol edin',
              ecommerceUrl: process.env.REACT_APP_ECOMMERCE_URL || '(env yok)'
            }
          );
        }

        setOrders(allOrders);
        setLoading(false);

        const orderIds = allOrders.map((o) => o.id).filter((id) => !isDemoOrder(id));
        if (orderIds.length > 0) {
          orderTransferService
            .getTransferStatusBulk(orderIds)
            .then((statuses) => {
              const statusMap: Record<string, TransferStatus> = {};
              statuses.forEach((s) => {
                statusMap[s.ecommerceOrderId] = s;
              });
              setWmsStatuses(statusMap);
              reconcileWmsCancelledOrders(allOrders, statusMap);
            })
            .catch(() => { /* WMS status load optional */ });
        }
      } catch (error) {
        console.error('[ORDERS MANAGEMENT] Siparişler yüklenirken hata:', error);
        setError('Siparişler yüklenirken bir hata oluştu.');
        setLoading(false);
      }
    };
    
    loadOrders();
  }, [companyId, refreshNonce, embedded]);

  useTrendyolWebhookMonitor({
    companyId,
    enabled: Boolean(companyId) && !embedded && process.env.NODE_ENV !== 'production' && TRENDYOL_MP_IS_LOCAL_BACKEND,
    lastSignalRAtRef: lastTrendyolSignalRAtRef,
    onWebhookDetected: (event: TrendyolWebhookEvent) => {
      const signalDelta = Date.now() - lastTrendyolSignalRAtRef.current;
      if (signalDelta < TRENDYOL_SIGNALR_GRACE_MS) {
        console.info(
          '%c[TY Webhook] Poll yenileme atlandi — SignalR zaten islendi',
          'color:#9e9e9e',
          { signalDeltaMs: signalDelta }
        );
        return;
      }
      const key = String(event.payload?.shipmentPackageId ?? event.payload?.orderNumber ?? 'ty-poll');
      if (scheduleOrdersRefresh(key, 'poll')) {
        console.info(
          '%c[TY Webhook] Poll tetiklemesi — siparis listesi yenileniyor',
          'color:#ef6c00;font-weight:bold',
          { key, companyId }
        );
      }
    },
  });

  useEffect(() => {
    if (!companyId || embedded) return;

    let connection: signalR.HubConnection | null = null;
    let stopConnection: (() => void) | null = null;
    let isCancelled = false;

    const handleMarketplaceSignal = (payload: any, forcedSource?: 'trendyol' | 'hepsiburada' | 'pazarama' | 'pttavm') => {
      const payloadCompanyId = payload?.companyId || payload?.CompanyId;
      if (payloadCompanyId && String(payloadCompanyId) !== String(companyId)) {
        console.warn(
          '%c[TY Webhook] Bildirim atlandi — farkli firma',
          'color:#e65100;font-weight:bold',
          {
            bildirimCompanyId: payloadCompanyId,
            gtsWebCompanyId: companyId,
            ipucu: 'trendyolUI dev CompanyId ile gts-web oturum CompanyId ayni olmali',
            payload
          }
        );
        return;
      }

      const source = String(
        forcedSource ||
        payload?.source ||
        payload?.type ||
        payload?.marketplaceSource ||
        payload?.order?.source ||
        payload?.order?.marketplaceSource ||
        ''
      ).toLowerCase();
      const isTrendyol = source.includes('trendyol');
      const isHepsiburada = source.includes('hepsiburada');
      const isPazarama = source.includes('pazarama');
      const isPttavm = source.includes('pttavm');
      if (!isTrendyol && !isHepsiburada && !isPazarama && !isPttavm) return;

      const orderNumber =
        payload?.orderNumber ||
        payload?.order?.orderNumber ||
        payload?.marketplaceOrderNumber ||
        payload?.order?.marketplaceOrderNumber ||
        '-';
      const label = isHepsiburada ? 'Hepsiburada' : isPazarama ? 'Pazarama' : isPttavm ? 'PttAVM' : 'Trendyol';

      const rawStatus =
        payload?.status ||
        payload?.orderStatus ||
        payload?.marketplaceStatus ||
        payload?.order?.status ||
        payload?.order?.orderStatus ||
        payload?.order?.marketplaceStatus ||
        '';
      const isCancelledStatus = isTerminalCancelledStatus(rawStatus);
      const isNew = Boolean(payload?.isNew ?? payload?.isNewOrder ?? payload?.order?.isNew);
      const statusChanged = Boolean(payload?.statusChanged ?? payload?.order?.statusChanged);

      if (isHepsiburada) {
        const packageNumber =
          payload?.packageNumber ||
          payload?.order?.packageNumber ||
          payload?.marketplaceOrderId ||
          '-';
        const status =
          rawStatus ||
          payload?.order?.marketplaceStatus ||
          payload?.shipmentPackageStatus ||
          '-';
        const via = statusChanged || isNew ? 'webhook' : 'sync';
        console.info(
          `%c[HB Webhook] SignalR bildirimi alindi (${via})`,
          'color:#ff6000;font-weight:bold',
          {
            zaman: new Date().toLocaleTimeString('tr-TR'),
            siparisNo: orderNumber,
            paketNo: packageNumber,
            durum: status,
            yeniSiparis: isNew,
            durumDegisti: statusChanged,
            payload
          }
        );
      }

      if (isTrendyol) {
        lastTrendyolSignalRAtRef.current = Date.now();
        const shipmentPackageId =
          payload?.shipmentPackageId ||
          payload?.order?.shipmentPackageId ||
          payload?.marketplaceOrderId ||
          payload?.order?.marketplaceOrderId ||
          '-';
        const status =
          rawStatus ||
          payload?.order?.marketplaceStatus ||
          payload?.shipmentPackageStatus ||
          payload?.order?.shipmentPackageStatus ||
          '-';
        const ecommerceOrderId =
          payload?.ecommerceOrderId ||
          payload?.order?.ecommerceOrderId ||
          '-';
        const via = statusChanged || isNew ? 'webhook/simüle' : 'sync';
        console.info(
          `%c[TY Webhook] SignalR bildirimi alindi (${via})`,
          'color:#f57c00;font-weight:bold;font-size:13px',
          {
            zaman: new Date().toLocaleTimeString('tr-TR'),
            companyId: payloadCompanyId || companyId,
            siparisNo: orderNumber,
            paketId: shipmentPackageId,
            durum: status,
            yeniSiparis: isNew,
            durumDegisti: statusChanged,
            ecommerceOrderId,
            sekmeIpucu: 'Trendyol filtresi + Siparis Alindi / Hazirlaniyor sekmesine bakin',
            payload
          }
        );
      }

      const toastStatus =
        payload?.shipmentPackageStatus ||
        payload?.order?.shipmentPackageStatus ||
        rawStatus ||
        '';

      if (isCancelledStatus) {
        showMarketplaceOrderToast(label, orderNumber, toastStatus, { isCancelled: true });
      } else if (isNew) {
        showMarketplaceOrderToast(label, orderNumber, toastStatus, { isNew: true });
      } else if (statusChanged) {
        showMarketplaceOrderToast(label, orderNumber, toastStatus);
      }

      // Debounced refresh: ardısık webhooklar tek fetch'e toplansin (300ms).
      // Trendyol: backup timer kaldirildi (poll fallback); diger pazaryerleri icin 1500ms yedek devam.
      const refreshKey = isTrendyol
        ? String(
            payload?.shipmentPackageId ||
            payload?.order?.shipmentPackageId ||
            orderNumber ||
            'trendyol'
          )
        : String(orderNumber || label);

      if (refreshDebounceTimerRef.current) {
        clearTimeout(refreshDebounceTimerRef.current);
      }
      if (refreshBackupTimerRef.current) {
        clearTimeout(refreshBackupTimerRef.current);
      }
      refreshDebounceTimerRef.current = setTimeout(() => {
        if (isTrendyol) {
          console.info(
            '%c[TY Webhook] Siparis listesi yenileniyor (webhook sonrasi)',
            'color:#ef6c00;font-weight:bold',
            { orderNumber, companyId, refreshKey }
          );
        }
        scheduleOrdersRefresh(refreshKey, isTrendyol ? 'signalr' : `${label}-signalr`);
        refreshDebounceTimerRef.current = null;
      }, 300);

      if (!isTrendyol) {
        refreshBackupTimerRef.current = setTimeout(() => {
          scheduleOrdersRefresh(refreshKey, `${label}-signalr-backup`);
          refreshBackupTimerRef.current = null;
        }, 1500);
      }
    };

    const handleHepsiJetSignal = (payload: any) => {
      const payloadCompanyId = payload?.companyId || payload?.CompanyId;
      if (payloadCompanyId && String(payloadCompanyId) !== String(companyId)) return;

      const shipment = payload?.shipment || payload?.Shipment || {};
      const orderNumber = shipment?.ecommerceOrderNumber || shipment?.customerDeliveryNo || '-';
      const cargoStatus = String(shipment?.cargoStatus || '').toLowerCase();

      const statusLabelMap: Record<string, string> = {
        created: 'oluşturuldu',
        sent: 'kargoya hazır',
        pickedup: 'kargoya verildi',
        intransit: 'yolda',
        delivered: 'teslim edildi',
        cancelled: 'iptal edildi',
        returned: 'iade edildi',
        failed: 'teslim edilemedi'
      };
      const statusText = statusLabelMap[cargoStatus] || 'güncellendi';
      const severity = cargoStatus === 'delivered' ? 'success'
        : (cargoStatus === 'cancelled' || cargoStatus === 'returned' || cargoStatus === 'failed') ? 'warning'
        : 'info';

      showSnackbar(`HepsiJet kargosu ${statusText} (#${orderNumber})`, severity as any);

      if (refreshDebounceTimerRef.current) clearTimeout(refreshDebounceTimerRef.current);
      if (refreshBackupTimerRef.current) clearTimeout(refreshBackupTimerRef.current);
      refreshDebounceTimerRef.current = setTimeout(() => {
        scheduleOrdersRefresh(String(orderNumber || 'hepsijet'), 'hepsijet-signalr');
        refreshDebounceTimerRef.current = null;
      }, 300);
      refreshBackupTimerRef.current = setTimeout(() => {
        scheduleOrdersRefresh(String(orderNumber || 'hepsijet'), 'hepsijet-signalr-backup');
        refreshBackupTimerRef.current = null;
      }, 1500);
    };

    const connectSignalR = async () => {
      const urls: string[] = [];
      const netApiHubUrl = `${NETAPI_URL}/ordershub`;
      urls.push(netApiHubUrl);

      // Lokal geliştirmede .env uyumsuz olsa bile gerçek zamanlı bildirim devam etsin.
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        const localHubUrl = 'http://localhost:5075/ordershub';
        if (!urls.includes(localHubUrl)) {
          urls.unshift(localHubUrl);
        }
      }

      const handleTrendyolSignal = (payload: any) => handleMarketplaceSignal(payload, 'trendyol');
      const handleHepsiburadaSignal = (payload: any) => handleMarketplaceSignal(payload, 'hepsiburada');
      const handlePazaramaSignal = (payload: any) => handleMarketplaceSignal(payload, 'pazarama');
      const handlePttavmSignal = (payload: any) => handleMarketplaceSignal(payload, 'pttavm');
      const handleNewOrderSignal = (payload: any) => handleMarketplaceSignal(payload);

      const attachHandlers = (conn: signalR.HubConnection) => {
        conn.on('TrendyolOrderUpdated', handleTrendyolSignal);
        conn.on('HepsiburadaOrderUpdated', handleHepsiburadaSignal);
        conn.on('PazaramaOrderUpdated', handlePazaramaSignal);
        conn.on('PttavmOrderUpdated', handlePttavmSignal);
        conn.on('HepsiJetShipmentUpdated', handleHepsiJetSignal);
        conn.on('NewMarketplaceOrder', handleMarketplaceSignal);
        conn.on('NewOrder', handleNewOrderSignal);
      };

      const detachHandlers = (conn: signalR.HubConnection) => {
        conn.off('TrendyolOrderUpdated', handleTrendyolSignal);
        conn.off('HepsiburadaOrderUpdated', handleHepsiburadaSignal);
        conn.off('PazaramaOrderUpdated', handlePazaramaSignal);
        conn.off('PttavmOrderUpdated', handlePttavmSignal);
        conn.off('HepsiJetShipmentUpdated', handleHepsiJetSignal);
        conn.off('NewMarketplaceOrder', handleMarketplaceSignal);
        conn.off('NewOrder', handleNewOrderSignal);
      };

      // Gruba katılma onConnected içinde: yeniden bağlanma yeni bir connection id
      // ürettiği için sunucudaki grup üyeliği kaybolur.
      const makeResilient = (conn: signalR.HubConnection) =>
        startResilientConnection(conn, {
          label: 'MarketplaceOrders',
          onConnected: () => conn.invoke('JoinCompanyGroup', companyId),
        });

      for (const url of urls) {
        if (isCancelled) return;

        const candidate = new signalR.HubConnectionBuilder()
          .withUrl(url)
          .withAutomaticReconnect(infiniteRetryPolicy)
          .build();

        attachHandlers(candidate);

        try {
          await candidate.start();
          await candidate.invoke('JoinCompanyGroup', companyId);
          connection = candidate;
          stopConnection = makeResilient(candidate);
          console.info(
            '%c[Marketplace Webhook] SignalR baglandi — Trendyol simule veya HB webhook sonrasi burada bildirim goreceksin',
            'color:#f57c00;font-weight:bold;font-size:13px',
            { hub: url, companyId, dinlenenOlaylar: ['TrendyolOrderUpdated', 'HepsiburadaOrderUpdated', 'NewMarketplaceOrder'] }
          );
          return;
        } catch (err) {
          detachHandlers(candidate);
          try { await candidate.stop(); } catch { /* noop */ }
          console.warn(`[TY Webhook] SignalR baglantisi basarisiz: ${url}`, err);
        }
      }

      console.error(
        '%c[TY Webhook] SignalR baglantisi kurulamadi — simule webhook DB\'ye yazilir ama canli bildirim gelmez. Main API (5075) calisiyor mu?',
        'color:#c62828;font-weight:bold',
        { companyId, denenenHub: urls }
      );

      // Hiçbir adres yanıt vermediyse vazgeçmiyoruz: birincil adrese süresiz
      // yeniden deneyen bir bağlantı bırakılıyor. Aksi halde sayfa açılırken API
      // kısa süre erişilemezse sekme kapanana kadar hiç canlı bildirim gelmiyordu.
      if (isCancelled) return;

      const retrying = new signalR.HubConnectionBuilder()
        .withUrl(netApiHubUrl)
        .withAutomaticReconnect(infiniteRetryPolicy)
        .build();

      attachHandlers(retrying);
      connection = retrying;
      stopConnection = makeResilient(retrying);
    };

    connectSignalR();

    return () => {
      isCancelled = true;
      if (refreshDebounceTimerRef.current) {
        clearTimeout(refreshDebounceTimerRef.current);
        refreshDebounceTimerRef.current = null;
      }
      if (refreshBackupTimerRef.current) {
        clearTimeout(refreshBackupTimerRef.current);
        refreshBackupTimerRef.current = null;
      }
      if (connection) {
        connection.off('TrendyolOrderUpdated');
        connection.off('HepsiburadaOrderUpdated');
        connection.off('PazaramaOrderUpdated');
        connection.off('PttavmOrderUpdated');
        connection.off('HepsiJetShipmentUpdated');
        connection.off('NewMarketplaceOrder');
        connection.off('NewOrder');
      }
      stopConnection?.();
    };
  }, [companyId, embedded]);

  // İade taleplerini pazaryerlerinden çek (N11 + Trendyol)
  useEffect(() => {
    if (!companyId || embedded) return;

    const loadReturns = async () => {
      try {
        const loadN11Returns = async (): Promise<Return[]> => {
          const response = await n11Api.get('/api/n11/returns?page=1&pageSize=250');
          const payload = response.data;

          return (payload?.data || []).map((item: any) => {
            const firstItem = Array.isArray(item.items) && item.items.length > 0 ? item.items[0] : null;
            return {
              id: String(item.id || item.returnClaimId || item.remoteOrderId),
              marketplace: 'n11',
              claimReturnId: item.returnClaimId || '',
              orderNumber: item.orderNumber || item.remoteOrderId || '',
              productName: firstItem?.productTitle || '-',
              buyerName: item.customerFullName || '-',
              buyerEmail: item.customerEmail || '-',
              buyerPhone: item.customerPhone || '-',
              quantity: firstItem?.quantity || 0,
              unitPrice: firstItem?.unitPrice || 0,
              finalPrice: item.totalAmount || firstItem?.totalPrice || 0,
              status: item.returnStatus || 'PENDING',
              executer: '',
              requestDate: item.updatedAt || item.remoteCreatedAt || item.createdAt || new Date().toISOString(),
              returnReasonType: item.returnReason || '-',
              returnReasonDescription: item.returnReason || '',
              trackingNumber: item.cargoTrackingNumber || '',
              sellerTrackingNumber: '',
              shipmentCompany: item.cargoCompany || '',
              campaignNumber: '',
              productId: firstItem?.productMainId || '',
              skuId: firstItem?.sku || '',
              attributesNames: '',
              deliveryFeeType: '',
              shipmentMethod: '',
              paymentDate: '',
              cancelledDate: ''
            };
          });
        };

        const loadTrendyolReturns = async (): Promise<Return[]> => {
          try {
            const response = await trendyolMpApi.get('/api/TrendyolClaims/claims?page=0&size=250');
            const payload = response.data;
            const claimList = Array.isArray(payload?.data?.content) ? payload.data.content : [];
            const result: Return[] = [];

          claimList.forEach((claim: any) => {
            const claimId = claim?.claimId || claim?.id || '';
            const orderNumber = claim?.orderNumber || '';
            const buyerName = `${claim?.customerFirstName || ''} ${claim?.customerLastName || ''}`.trim() || '-';
            const buyerEmail = '-';
            const baseDate = claim?.lastModifiedDate || claim?.claimDate || Date.now();
            const requestDate = new Date(Number(baseDate)).toISOString();
            const groupedItems = Array.isArray(claim?.items) ? claim.items : [];

            groupedItems.forEach((group: any) => {
              const orderLine = group?.orderLine || {};
              const claimItems = Array.isArray(group?.claimItems) ? group.claimItems : [];

              claimItems.forEach((claimItem: any) => {
                const claimItemId = String(claimItem?.id || '');
                const status = claimItem?.claimItemStatus?.name || 'Created';
                const reasonName = claimItem?.customerClaimItemReason?.name || claimItem?.trendyolClaimItemReason?.name || '-';
                const color = orderLine?.productColor || '';
                const size = orderLine?.productSize || '';
                const attributes = `${color} ${size}`.trim();

                result.push({
                  id: `${claimId}_${claimItemId}`,
                  marketplace: 'trendyol',
                  claimReturnId: claimId,
                  claimId,
                  claimItemIds: claimItemId ? [claimItemId] : [],
                  orderNumber,
                  productName: orderLine?.productName || '-',
                  buyerName,
                  buyerEmail,
                  buyerPhone: '-',
                  quantity: 1,
                  unitPrice: Number(orderLine?.price || 0),
                  finalPrice: Number(orderLine?.price || 0),
                  status,
                  executer: '',
                  requestDate,
                  returnReasonType: reasonName,
                  returnReasonDescription: claimItem?.customerNote || claimItem?.note || '',
                  trackingNumber: String(claim?.cargoTrackingNumber || ''),
                  sellerTrackingNumber: String(claim?.cargoSenderNumber || ''),
                  shipmentCompany: claim?.cargoProviderName || '',
                  campaignNumber: String(orderLine?.salesCampaignId || ''),
                  productId: String(orderLine?.id || ''),
                  skuId: orderLine?.merchantSku || '',
                  attributesNames: attributes,
                  deliveryFeeType: '',
                  shipmentMethod: '',
                  paymentDate: '',
                  cancelledDate: ''
                });
              });
            });
          });

          return result;
          } catch {
            return [];
          }
        };

        const loadHepsiburadaClaims = async (): Promise<Return[]> => {
          const response = await hepsiburadaApi.get('/api/hepsiburada/claims?page=0&pageSize=250');
          const payload = response.data;
          const rootData = payload?.Data || payload?.data || payload;
          const claimList = Array.isArray(rootData)
            ? rootData
            : (Array.isArray(rootData?.items)
              ? rootData.items
              : (Array.isArray(rootData?.content) ? rootData.content : []));

          return claimList.map((claim: any, index: number) => {
            const claimNo = claim?.number || claim?.claimNumber || claim?.id || `hb_claim_${index}`;
            const lineItemId = claim?.lineItemId ? String(claim.lineItemId) : '';
            const unitPrice = Number(claim?.priceAmount ?? claim?.price ?? claim?.unitPrice ?? 0);
            const totalPrice = Number(claim?.totalPriceAmount ?? claim?.totalPrice ?? 0);
            const quantity = Number(claim?.quantity || 1);

            return {
              id: String(claimNo),
              marketplace: 'hepsiburada',
              claimReturnId: String(claimNo),
              claimId: String(claimNo),
              claimItemIds: lineItemId ? [lineItemId] : [],
              orderNumber: String(claim?.orderNumber || claim?.packageNumber || ''),
              productName: claim?.RequestedProduct || claim?.productName || claim?.title || claim?.MerchantSku || claim?.sku || '-',
              buyerName: claim?.customerName || claim?.buyerName || '-',
              buyerEmail: claim?.customerEmail || '-',
              buyerPhone: claim?.customerPhone || '-',
              quantity,
              unitPrice,
              finalPrice: totalPrice > 0 ? totalPrice : unitPrice * quantity,
              status: claim?.status || 'ClaimAwaitingAction',
              executer: '',
              requestDate: claim?.claimDate || claim?.updatedAt || claim?.createdAt || new Date().toISOString(),
              returnReasonType: claim?.Reason || claim?.reason || claim?.claimType || claim?.claimReason || '-',
              returnReasonDescription: claim?.explanation || claim?.description || '',
              trackingNumber: String(claim?.trackingNumber || ''),
              sellerTrackingNumber: String(claim?.sellerTrackingNumber || ''),
              shipmentCompany: claim?.cargoCompany || '',
              campaignNumber: '',
              productId: String(claim?.productId || ''),
              skuId: claim?.MerchantSku || claim?.merchantSku || claim?.sku || claim?.hbSku || '',
              attributesNames: '',
              deliveryFeeType: '',
              shipmentMethod: '',
              paymentDate: '',
              cancelledDate: ''
            };
          });
        };

        const loadPazaramaRefunds = async (): Promise<Return[]> => {
          const now = new Date();
          const startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          const response = await pazaramaApi.post('/api/pazarama/order-actions/refunds/query', {
            pageNumber: 1,
            pageSize: 100,
            startDate: startDate.toISOString(),
            endDate: now.toISOString()
          });
          const payload = response.data;
          const root = payload?.data || payload?.Data || payload || {};
          const refundList = Array.isArray(root)
            ? root
            : (Array.isArray(root?.items)
              ? root.items
              : (Array.isArray(root?.content)
                ? root.content
                : (Array.isArray(root?.refunds) ? root.refunds : [])));

          return (refundList as any[]).map((item: any, index: number) => ({
            id: String(item?.refundId || item?.id || item?.orderItemId || `pz_refund_${index}`),
            marketplace: 'pazarama',
            claimReturnId: String(item?.refundId || item?.id || ''),
            claimId: String(item?.refundId || item?.id || ''),
            claimItemIds: item?.orderItemId ? [String(item.orderItemId)] : [],
            orderNumber: String(item?.orderNumber || item?.orderId || ''),
            productName: item?.productName || item?.name || '-',
            buyerName: item?.customerName || item?.buyerName || '-',
            buyerEmail: item?.customerEmail || '-',
            buyerPhone: item?.customerPhone || '-',
            quantity: Number(item?.quantity || 1),
            unitPrice: Number(item?.unitPrice || item?.price || 0),
            finalPrice: Number(item?.totalPrice || item?.amount || item?.price || 0),
            status: String(item?.refundStatus ?? item?.status ?? 'PENDING'),
            executer: '',
            requestDate: item?.refundDate || item?.createdDate || item?.updatedDate || new Date().toISOString(),
            returnReasonType: item?.refundReason || item?.reason || '-',
            returnReasonDescription: item?.description || '',
            trackingNumber: String(item?.trackingNumber || ''),
            sellerTrackingNumber: '',
            shipmentCompany: item?.cargoCompany || '',
            campaignNumber: '',
            productId: String(item?.productId || ''),
            skuId: item?.stockCode || item?.barcode || '',
            attributesNames: '',
            deliveryFeeType: '',
            shipmentMethod: '',
            paymentDate: '',
            cancelledDate: ''
          }));
        };

        // Sitenin kendi e-ticaret iadeleri (EcommerceOrderReturns): vitrinden açılan
        // talepler pazaryeri iadeleriyle aynı sekmede listelenip yönetilir.
        const loadSiteReturns = async (): Promise<Return[]> => {
          const response = await ecommerceApi.get('/api/ecommerce/returns');
          const list = response.data?.data || [];

          return (list as any[]).map((ret: any) => {
            const currency = (ret.orderCurrency || 'TRY').toUpperCase();
            const amount = Number(ret.refundAmount || ret.orderTotalAmount || 0);
            const rate = Number(ret.appliedExchangeRate || 0);
            const isCancelRequest = Boolean(ret.isCancelRequest)
              || String(ret.id || '').startsWith('cancel-req:')
              || String(ret.status || '').toLowerCase() === 'cancel_requested'
              || String(ret.returnMethod || '') === 'cancel_request';
            return {
              id: String(ret.id),
              marketplace: 'website',
              claimReturnId: ret.returnNumber || '',
              currency,
              amountTl: currency !== 'TRY' && rate > 0
                ? Math.round(amount * rate * 100) / 100
                : null,
              orderNumber: ret.orderNumber || ret.orderId || '',
              productName: ret.productName || '-',
              buyerName: (ret.customerName && String(ret.customerName).trim()) || '-',
              buyerEmail: ret.customerEmail || '-',
              buyerPhone: '-',
              quantity: 0,
              unitPrice: 0,
              finalPrice: amount,
              status: ret.status || 'Requested',
              executer: '',
              requestDate: ret.createdAt || ret.cancelRequestedAt || new Date().toISOString(),
              returnReasonType: ret.reason || '-',
              returnReasonDescription: ret.reasonDetail || ret.customerNote || '',
              trackingNumber: '',
              sellerTrackingNumber: '',
              shipmentCompany: '',
              campaignNumber: '',
              productId: '',
              skuId: '',
              attributesNames: '',
              deliveryFeeType: '',
              shipmentMethod: '',
              paymentDate: '',
              cancelledDate: '',
              refundMode: ret.refundMode || null,
              returnInvoiceId: ret.returnInvoiceId || null,
              expenseVoucherStatus: ret.expenseVoucherStatus || null,
              expenseVoucherNo: ret.expenseVoucherNo || null,
              billingTitle: ret.billingTitle || null,
              billingTaxNumber: ret.billingTaxNumber || null,
              billingEInvoiceTaxpayer: ret.billingEInvoiceTaxpayer ?? null,
              isCancelRequest,
              sourceOrderId: ret.orderId || (String(ret.id || '').startsWith('cancel-req:')
                ? String(ret.id).slice('cancel-req:'.length)
                : undefined),
              cancelRequestedAt: ret.cancelRequestedAt || ret.createdAt || undefined,
              orderCreatedAt: ret.orderCreatedAt || undefined
            };
          });
        };

        const [siteResult, n11Result, trendyolResult, hepsiburadaResult, pazaramaResult] = await Promise.allSettled([
          loadSiteReturns(),
          loadN11Returns(),
          loadTrendyolReturns(),
          loadHepsiburadaClaims(),
          loadPazaramaRefunds()
        ]);
        const returnsList: Return[] = [
          ...(siteResult.status === 'fulfilled' ? siteResult.value : []),
          ...(n11Result.status === 'fulfilled' ? n11Result.value : []),
          ...(trendyolResult.status === 'fulfilled' ? trendyolResult.value : []),
          ...(hepsiburadaResult.status === 'fulfilled' ? hepsiburadaResult.value : []),
          ...(pazaramaResult.status === 'fulfilled' ? pazaramaResult.value : [])
        ];

        returnsList.sort((a, b) => new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime());
        setReturns(returnsList);
        console.log(' [RETURNS MANAGEMENT] İade talepleri yüklendi:', returnsList.length);
        if (siteResult.status === 'rejected') {
          console.error(' [RETURNS MANAGEMENT] Site iade talepleri yüklenirken hata:', siteResult.reason);
        }
        if (n11Result.status === 'rejected') {
          console.error('[RETURNS MANAGEMENT] N11 iade talepleri yüklenirken hata:', n11Result.reason);
        }
        if (trendyolResult.status === 'rejected') {
          console.error('[RETURNS MANAGEMENT] Trendyol iade talepleri yüklenirken hata:', trendyolResult.reason);
        }
        if (hepsiburadaResult.status === 'rejected') {
          console.error('[RETURNS MANAGEMENT] Hepsiburada talep kayıtları yüklenirken hata:', hepsiburadaResult.reason);
        }
        if (pazaramaResult.status === 'rejected') {
          console.error('[RETURNS MANAGEMENT] Pazarama iade kayıtları yüklenirken hata:', pazaramaResult.reason);
        }
      } catch (error) {
        console.error('[RETURNS MANAGEMENT] İade talepleri yüklenirken hata:', error);
      }
    };
    
    loadReturns();
  }, [companyId, refreshNonce, embedded]);

  // Tab değişikliği
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
    setSearchQuery('');
  };

  // NOT: Eski `triggerHepsiburadaRefresh()` helper'i (HB API'ye /pull-missing POST atan)
  // kaldirildi. HB SIT'ten gelen `Packaged` statusunu DB'deki Pending paketlere yans\u0131tarak
  // GTS panelinde tum siparisleri yanlislikla `Hazirlaniyor` sekmesine atiyordu.
  // Webhook akisi zaten DB'yi otomatik gunceller; aksiyon sonras\u0131 sadece UI listesini
  // tazelemek icin setRefreshNonce yeterli. Manuel HB pull yalnizca "Pazaryerlerinden
  // Siparis Cek" butonu uzerinden tetiklenir (asagida).

  // WMS'te iptal edilmiş ama e-ticaret durumu güncellenmemiş siparişleri uzlaştır.
  // Depo iptali kesindir → e-ticaret siparişini de "cancelled" yaparak "İptal Edildi" sekmesine düşürür.
  // (Sipariş WMS senkron kodu eklenmeden/servis yeniden başlatılmadan önce iptal edilmişse gerekir.)
  const reconciledWmsCancelRef = useRef<Set<string>>(new Set());
  const reconcileWmsCancelledOrders = (
    currentOrders: Order[],
    statusMap: Record<string, TransferStatus>
  ) => {
    const terminal = new Set(['cancelled', 'cancel_requested', 'shipped', 'delivered']);
    const toCancel = currentOrders.filter((o) => {
      const wms = statusMap[o.id];
      const wmsCancelled = String(wms?.wmsStatus || '').toLowerCase() === 'cancelled';
      return (
        wmsCancelled &&
        !terminal.has(String(o.status || '').toLowerCase()) &&
        !isDemoOrder(o.id) &&
        !reconciledWmsCancelRef.current.has(o.id)
      );
    });
    if (toCancel.length === 0) return;

    // Anında listede "İptal Edildi" göster
    const ids = new Set(toCancel.map((o) => o.id));
    setOrders((prev) =>
      prev.map((o) => (ids.has(o.id) ? { ...o, status: 'cancelled' as const } : o))
    );

    // Kalıcı hale getir (idempotent; backend zaten cancelled ise no-op)
    toCancel.forEach((o) => {
      reconciledWmsCancelRef.current.add(o.id);
      ecommerceApi
        .post('/api/ecommerce/orders/status', {
          orderId: o.id,
          status: 'cancelled',
          cancelReason: o.cancelReason || 'Depo iptali',
        })
        .catch(() => {
          // Başarısızsa tekrar denenebilsin
          reconciledWmsCancelRef.current.delete(o.id);
        });
    });
  };

  // Sipariş durumunu güncelle
  const updateOrderStatus = async (orderId: string, memberId: string, newStatus: string) => {
    if (!companyId) return;

    // GTS_SUNUM_MOCK
    if (isDemoOrder(orderId)) {
      setUpdating(orderId);
      applyDemoOrderStatusUpdate(orderId, newStatus, 'Sipariş durumu güncellendi (sunum demo).');
      return;
    }

    setUpdating(orderId);
    try {
      // N11 siparişi mi kontrol et
      const order = orders.find(o => o.id === orderId);
      const isN11Order = orderId.startsWith('n11_') || order?.marketplace === 'n11';
      const isHepsiburadaOrder =
        normalizeMarketplaceSource(order?.marketplace) === 'hepsiburada' ||
        String(order?.memberEmail || '').toLowerCase().includes('@hepsiburada.marketplace.local') ||
        String(order?.orderNumber || '').toUpperCase().startsWith('HB-');
      
      if (isN11Order) {
        // N11 siparişleri için Firebase/HB-spesifik işlem yapılmaz;
        // sadece EcommerceOrders status güncellenir (aşağıda else bloğunun dışında).
      } else {
        if (isHepsiburadaOrder && (newStatus === 'confirmed' || newStatus === 'delivered')) {
          const rawOrderNumber = String(order?.orderNumber || '').replace(/^HB-/i, '').trim();
          const rawPackageCandidate =
            (order as any)?.n11OrderId ||
            (order as any)?.marketplaceOrderId ||
            rawOrderNumber;
          let packageNumber = String(rawPackageCandidate || '').replace(/^HB-/i, '').trim();

          // Bazi kayitlarda package yerine orderNumber tutulmus olabiliyor.
          // Operasyonlari dogru pakete atmak icin order detail'den package no coz.
          if (!packageNumber || (rawOrderNumber && packageNumber === rawOrderNumber)) {
            const detailResponse = await hepsiburadaApi.get(`/api/hepsiburada/orders/${encodeURIComponent(rawOrderNumber)}`);
            const detailRoot = detailResponse.data?.data || detailResponse.data?.Data || detailResponse.data || {};
            const resolvedPackageNumber = findFirstStringByKeys(detailRoot, ['packageNumber', 'packagenumber']);
            if (resolvedPackageNumber) {
              packageNumber = resolvedPackageNumber.trim();
            }
          }

          if (!packageNumber) {
            throw new Error('Hepsiburada package numarası bulunamadı.');
          }

          if (newStatus === 'confirmed') {
            const parsedItems = parseMarketplaceItems(order?.items);
            let lineItems = parsedItems
              .map((item: any) => {
                const lineItemId =
                  item?.lineItemId ||
                  item?.orderLineId ||
                  item?.lineId ||
                  item?.n11LineId ||
                  item?.n11OrderLineId ||
                  item?.id;
                const quantity = Number(item?.quantity || 1);
                if (!lineItemId) return null;
                return {
                  lineItemId: String(lineItemId),
                  quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
                };
              })
              .filter((x: any) => x !== null);

            if (lineItems.length === 0) {
              const detailResponse = await hepsiburadaApi.get(`/api/hepsiburada/orders/${encodeURIComponent(rawOrderNumber)}`);
              const detailRoot = detailResponse.data?.data || detailResponse.data?.Data || detailResponse.data || {};
              const detailItems = parseMarketplaceItems(
                detailRoot?.items || detailRoot?.Items || detailRoot
              );

              lineItems = detailItems
                .map((item: any) => {
                  const lineItemId =
                    item?.lineItemId ||
                    item?.orderLineId ||
                    item?.lineId ||
                    item?.n11LineId ||
                    item?.n11OrderLineId ||
                    item?.id;
                  const quantity = Number(item?.quantity || 1);
                  if (!lineItemId) return null;
                  return {
                    lineItemId: String(lineItemId),
                    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
                  };
                })
                .filter((x: any) => x !== null);
            }

            if (lineItems.length === 0) {
              throw new Error('Hepsiburada paketleme için lineItem bilgisi bulunamadı. Sipariş detayında da lineItemId yok.');
            }

            await hepsiburadaApi.post(`/api/hepsiburada/packages/${encodeURIComponent(packageNumber)}/create`, {
              lineItems
            });
          }

          if (newStatus === 'delivered') {
            await hepsiburadaApi.post(`/api/hepsiburada/packages/${encodeURIComponent(packageNumber)}/deliver`, {
              receivedDate: new Date().toISOString(),
              receivedBy: 'GTS Panel',
              barcode: (order as any)?.cargoBarcode || undefined
            });
          }

          // HB tarafina aksiyon iletildi; arka planda otomatik pull-missing ATILMAZ
          // (HB SIT statuleri tum paketlere yansitiyordu). Sadece UI listesini yenile.
          setRefreshNonce((prev) => prev + 1);
        }

        // Pazarama: onayla (12) ve teslim (11) item bazli status/bulk ile gonderilir.
        // Kargola (5) tracking gerektirdiginden updateOrderWithTracking icinde ele alinir.
        if (order && isPazaramaOrder(order) && (newStatus === 'confirmed' || newStatus === 'delivered')) {
          const orderItemIds = resolvePazaramaOrderItemIds(order);
          if (orderItemIds.length > 0) {
            await pazaramaApi.post('/api/pazarama/order-actions/status/bulk', {
              orderItemIds,
              status: newStatus === 'confirmed' ? 12 : 11
            });
          } else {
            showSnackbar('Pazarama kalem bilgisi bulunamadı; yalnızca yerel statü güncellenecek.', 'warning');
          }
          setRefreshNonce((prev) => prev + 1);
        }

        const response = await ecommerceApi.post('/api/ecommerce/orders/status', {
          orderId: orderId,
          status: newStatus
        });
        const data = response.data;
        
        if (!data.success) {
          throw new Error(data.message || 'Sipariş durumu güncellenirken hata oluştu');
        }
      }

      if (isN11Order) {
        // N11 siparişleri de EcommerceOrders tablosunda güncellenmeli.
        // id 'n11_' ile başlıyorsa EcommerceOrders'da kaydı olmayabilir; yoksa hata yutulur.
        try {
          const n11EcommerceId = !orderId.startsWith('n11_')
            ? orderId
            : (order as any)?.ecommerceOrderId ?? null;
          if (n11EcommerceId) {
            await ecommerceApi.post('/api/ecommerce/orders/status', {
              orderId: n11EcommerceId,
              status: newStatus
            });
          }
        } catch {
          // EcommerceOrders kaydı yoksa veya hata olursa sessizce devam et
        }
      }

      // Local state'i güncelle — anında sekme değişsin (full reload beklenmesin)
      const normalizedNewStatus = normalizeOrderStatus(newStatus);
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? {
              ...order,
              status: normalizedNewStatus,
              ...(normalizedNewStatus === 'cancelled' || normalizedNewStatus === 'confirmed'
                ? { cancelRequest: undefined }
                : {})
            }
          : order
      ));
      if (normalizedNewStatus === 'cancelled' || normalizedNewStatus === 'confirmed') {
        setReturns((prev) =>
          prev.filter(
            (r) =>
              r.sourceOrderId !== orderId &&
              r.id !== `cancel-req:${orderId}` &&
              !String(r.id).endsWith(orderId)
          )
        );
      }

      showSnackbar('Sipariş durumu başarıyla güncellendi!', 'success');

      // Backend tarafı auto transfer tetikliyor; burada sadece transfer durumunu tazele
      try {
        const updatedStatus = await orderTransferService.getTransferStatus(orderId);
        if (updatedStatus?.isTransferred) {
          setWmsStatuses(prev => ({ ...prev, [orderId]: updatedStatus }));
          showSnackbar('Sipariş WMS aktarım durumu güncellendi.', 'info');
        } else {
          const normalizedStatus = (newStatus || '').trim().toLowerCase();
          if (['confirmed', 'paid', 'received'].includes(normalizedStatus)) {
            // Fallback: Backend tarafı tetikleyemezse (ör. deploy bekleniyorsa),
            // uygun auto-transfer ayarlı depoyu bulup frontend'den transfer dene.
            const smartWarehouses = await smartWarehouseService.getAllList();
            const activeSmartWarehouses = smartWarehouses.filter(w => w.isActive !== false);

            let targetWarehouseId: string | null = null;
            for (const wh of activeSmartWarehouses) {
              try {
                const settings = await orderTransferService.getAutoTransferSettings(wh.id);
                const trigger = (settings?.triggerStatus || '').trim().toLowerCase();
                if (settings?.enabled && trigger === normalizedStatus) {
                  targetWarehouseId = wh.id;
                  break;
                }
              } catch {
                // warehouse bazlı ayar okunamazsa bir sonrakine geç
              }
            }

            if (targetWarehouseId) {
              const transferResult = await orderTransferService.transfer({
                ecommerceOrderId: orderId,
                warehouseId: targetWarehouseId
              });

              if (transferResult?.success) {
                const refreshedStatus = await orderTransferService.getTransferStatus(orderId);
                if (refreshedStatus?.isTransferred) {
                  setWmsStatuses(prev => ({ ...prev, [orderId]: refreshedStatus }));
                  showSnackbar('Sipariş WMS havuzuna aktarıldı.', 'success');
                }
              }
            }
          }
        }
      } catch { /* status refresh optional */ }

    } catch (error) {
      console.error('❌ [ORDERS MANAGEMENT] Sipariş durumu güncellenirken hata:', error);
      setError('Sipariş durumu güncellenirken bir hata oluştu.');
      showSnackbar(`Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  const buildTrendyolLines = (order: Order): Array<{ lineId: number; quantity: number }> => {
    const parsedItems = parseMarketplaceItems(order.items);
    return parsedItems
      .map((item: any) => {
        const rawLineId = item.lineId || item.n11LineId || item.n11OrderLineId;
        const lineId = Number(rawLineId);
        const quantity = Number(item.quantity || 0);
        if (!Number.isFinite(lineId) || lineId <= 0 || !Number.isFinite(quantity) || quantity <= 0) {
          return null;
        }

        return { lineId, quantity };
      })
      .filter((line: { lineId: number; quantity: number } | null): line is { lineId: number; quantity: number } => line !== null);
  };

  const callTrendyolAction = async (
    endpoint: string,
    payload: Record<string, any>,
    successMessage: string,
    orderIdToRefresh: string
  ) => {
    // GTS_SUNUM_MOCK
    if (isDemoOrder(orderIdToRefresh)) {
      setUpdating(orderIdToRefresh);
      let nextStatus = 'confirmed';
      if (endpoint.includes('invoiced')) nextStatus = 'shipped';
      if (endpoint.includes('unsupplied')) nextStatus = 'cancelled';
      applyDemoOrderStatusUpdate(orderIdToRefresh, nextStatus, `${successMessage} (sunum demo)`);
      return;
    }

    setUpdating(orderIdToRefresh);
    try {
      let response: any = null;
      try {
        response = await trendyolMpApi.post(endpoint, payload);
      } catch (error: any) {
        const shouldTryFallback =
          error?.code === 'ERR_NETWORK' ||
          error?.message?.includes?.('Network Error') ||
          error?.response?.status === 404;

        if (!shouldTryFallback) {
          throw error;
        }

        let fallbackSucceeded = false;
        let lastError = error;
        for (const fallbackBaseUrl of TRENDYOL_ACTION_FALLBACK_BASE_URLS) {
          try {
            response = await trendyolMpApi.request({
              method: 'POST',
              url: endpoint,
              data: payload,
              baseURL: fallbackBaseUrl,
            });
            fallbackSucceeded = true;
            break;
          } catch (fallbackError) {
            lastError = fallbackError;
          }
        }

        if (!fallbackSucceeded) {
          throw lastError;
        }
      }

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Trendyol aksiyonu başarısız.');
      }

      showSnackbar(successMessage, 'success');
      setRefreshNonce((prev) => prev + 1);
    } catch (error) {
      console.error('[ORDERS MANAGEMENT] Trendyol aksiyonu hatası:', error);
      showSnackbar(`Trendyol aksiyon hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  const handleTrendyolPicking = async (order: Order) => {
    const shipmentPackageId = Number(order.n11OrderId || (order as any).marketplaceOrderId);
    const lines = buildTrendyolLines(order);
    if (!Number.isFinite(shipmentPackageId) || shipmentPackageId <= 0 || lines.length === 0) {
      showSnackbar('Trendyol picking için package/line bilgisi eksik.', 'error');
      return;
    }

    await callTrendyolAction(
      '/api/TrendyolOrderActions/picking',
      { shipmentPackageId, lines },
      'Trendyol Picking bildirimi gönderildi.',
      order.id
    );
  };

  const handleTrendyolInvoiced = async (order: Order) => {
    const shipmentPackageId = Number(order.n11OrderId || (order as any).marketplaceOrderId);
    const lines = buildTrendyolLines(order);
    if (!Number.isFinite(shipmentPackageId) || shipmentPackageId <= 0 || lines.length === 0) {
      showSnackbar('Trendyol invoiced için package/line bilgisi eksik.', 'error');
      return;
    }

    const invoiceNumber = window.prompt('Fatura numarası giriniz:');
    if (!invoiceNumber || !invoiceNumber.trim()) {
      return;
    }

    await callTrendyolAction(
      '/api/TrendyolOrderActions/invoiced',
      { shipmentPackageId, lines, invoiceNumber: invoiceNumber.trim() },
      'Trendyol Invoiced bildirimi gönderildi.',
      order.id
    );
  };

  const handleTrendyolUnsupplied = async (order: Order) => {
    const shipmentPackageId = Number(order.n11OrderId || (order as any).marketplaceOrderId);
    const lines = buildTrendyolLines(order);
    if (!Number.isFinite(shipmentPackageId) || shipmentPackageId <= 0 || lines.length === 0) {
      showSnackbar('Trendyol unsupplied için package/line bilgisi eksik.', 'error');
      return;
    }

    const reasonIdInput = window.prompt('Tedarik edememe reasonId giriniz (500/501/502/504/505/506):', '500');
    const reasonId = Number(reasonIdInput);
    if (!Number.isFinite(reasonId)) {
      return;
    }

    await callTrendyolAction(
      '/api/TrendyolOrderActions/unsupplied',
      { shipmentPackageId, lines, reasonId },
      'Trendyol tedarik edememe bildirimi gönderildi.',
      order.id
    );
  };

  // Sipariş detaylarını göster
  const showOrderDetails = (order: Order) => {
    setSelectedOrder(order);

    if (isN11Order(order)) {
      setShowN11OrderModal(true);
    } else if (isHepsiburadaOrder(order)) {
      setShowHepsiburadaOrderModal(true);
    } else if (isTrendyolOrder(order)) {
      setShowTrendyolOrderModal(true);
    } else {
      setShowOrderModal(true);
    }
  };

  // Sipariş detaylarını yazdır
  const printOrderDetails = (order: Order) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Pop-up engelleyici nedeniyle yazdırma penceresi açılamadı.');
      return;
    }

    const orderDate = new Date(order.date).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Tutarlar siparişin para birimiyle basılır; TRY dışıysa TL karşılığı eklenir
    const currencyCode = resolveCurrencyCode(order.currency);
    const sym = CURRENCY_SYMBOLS[currencyCode] || `${currencyCode} `;
    const tlEquivalent = orderTlEquivalent(order);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Sipariş Detayları - ${order.orderNumber}</title>
        <meta charset="utf-8">
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
          .company-name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .order-title { font-size: 18px; color: #666; }
          .section { margin-bottom: 25px; }
          .section-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .info-item { margin-bottom: 8px; }
          .info-label { font-weight: bold; display: inline-block; width: 120px; }
          .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .items-table th { background-color: #f5f5f5; font-weight: bold; }
          .total-row { font-weight: bold; background-color: #f9f9f9; }
          .status-badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
          .status-confirmed { background-color: #e3f2fd; color: #1976d2; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">MT TEKNOLOJİ</div>
        </div>

        <div class="section">
          <div class="section-title">Sipariş Bilgileri</div>
          <div class="info-grid">
            <div>
              <div class="info-item">
                <span class="info-label">Sipariş No:</span> #${order.orderNumber}
              </div>
              <div class="info-item">
                <span class="info-label">Tarih:</span> ${orderDate}
              </div>
              <div class="info-item">
                <span class="info-label">Durum:</span> 
                <span class="status-badge status-confirmed">Onaylandı</span>
              </div>
            </div>
            <div>
              <div class="info-item">
                <span class="info-label">Müşteri:</span> ${order.memberName || 'Belirtilmemiş'}
              </div>
              <div class="info-item">
                <span class="info-label">E-posta:</span> ${order.memberEmail || 'Belirtilmemiş'}
              </div>
              <div class="info-item">
                <span class="info-label">Toplam Tutar:</span> ${sym}${order.totalAmount}${tlEquivalent != null ? ` (≈ ₺${tlEquivalent.toLocaleString('tr-TR', { minimumFractionDigits: 2 })})` : ''}
              </div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Sipariş Edilen Ürünler</div>
          <table class="items-table">
            <thead>
              <tr>
                <th>Ürün Adı</th>
                <th>Adet</th>
                <th>Birim Fiyat</th>
                <th>Toplam</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${sym}${item.price}</td>
                  <td>${sym}${item.price * item.quantity}</td>
                </tr>
              `).join('')}
              ${(() => {
                const itemsTotal = order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                const shippingCost = order.totalAmount - itemsTotal;
                
                if (shippingCost > 0) {
                  return `
                    <tr>
                      <td colspan="3">Kargo Ücreti</td>
                      <td>${sym}${shippingCost}</td>
                    </tr>
                  `;
                } else {
                  return `
                    <tr>
                      <td colspan="3">Kargo Ücreti</td>
                      <td>Ücretsiz</td>
                    </tr>
                  `;
                }
              })()}
              <tr class="total-row">
                <td colspan="3">GENEL TOPLAM</td>
                <td>${sym}${order.totalAmount}${tlEquivalent != null ? ` (≈ ₺${tlEquivalent.toLocaleString('tr-TR', { minimumFractionDigits: 2 })})` : ''}</td>
              </tr>
            </tbody>
          </table>
        </div>

        ${order.shippingAddress ? `
        <div class="section">
          <div class="section-title">Teslimat Adresi</div>
          <div class="info-item">
            <span class="info-label">Ad Soyad:</span> ${order.shippingAddress.firstName} ${order.shippingAddress.lastName}
          </div>
          <div class="info-item">
            <span class="info-label">Adres:</span> ${order.shippingAddress.address}
          </div>
          <div class="info-item">
            <span class="info-label">İl/İlçe:</span> ${order.shippingAddress.district}, ${order.shippingAddress.city}
          </div>
          <div class="info-item">
            <span class="info-label">Telefon:</span> ${order.shippingAddress.phone}
          </div>
        </div>
        ` : ''}

        <div class="footer">
          <p>Bu belge ${new Date().toLocaleDateString('tr-TR')} tarihinde oluşturulmuştur.</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Kargo takip numarası modal'ını aç
  const openTrackingModal = (order: Order) => {
    setOrderToShip(order);
    setTrackingNumber('');
    setShippingCompany('');
    setHepsiburadaCargoCompanies([]);
    setTrackingModalOpen(true);

    const isHepsiburadaOrder =
      normalizeMarketplaceSource(order.marketplace) === 'hepsiburada' ||
      String(order.memberEmail || '').toLowerCase().includes('@hepsiburada.marketplace.local') ||
      String(order.orderNumber || '').toUpperCase().startsWith('HB-');
    if (!isHepsiburadaOrder) {
      return;
    }

    (async () => {
      const rawOrderNumber = String(order?.orderNumber || '').replace(/^HB-/i, '').trim();
      const rawPackageCandidate =
        (order as any)?.n11OrderId ||
        (order as any)?.marketplaceOrderId ||
        rawOrderNumber;
      let packageNumber = String(rawPackageCandidate || '').replace(/^HB-/i, '').trim();

      if (!packageNumber || (rawOrderNumber && packageNumber === rawOrderNumber)) {
        const detailResponse = await hepsiburadaApi.get(`/api/hepsiburada/orders/${encodeURIComponent(rawOrderNumber)}`);
        const detailRoot = detailResponse.data?.data || detailResponse.data?.Data || detailResponse.data || {};
        const resolvedPackageNumber = findFirstStringByKeys(detailRoot, ['packageNumber', 'packagenumber']);
        if (resolvedPackageNumber) {
          packageNumber = resolvedPackageNumber.trim();
        }
      }

      if (!packageNumber) {
        return;
      }

      let response;
      try {
        response = await hepsiburadaApi.get(`/api/hepsiburada/packages/${encodeURIComponent(packageNumber)}/cargo-companies`);
      } catch {
        // Bazi siparislerde marketplaceOrderId/packageNumber farkli gelebiliyor.
        // Paket endpointi basarisizsa lineItem bazli endpoint'e fallback yap.
        const detailResponse = await hepsiburadaApi.get(`/api/hepsiburada/orders/${encodeURIComponent(rawOrderNumber)}`);
        const detailRoot = detailResponse.data?.data || detailResponse.data?.Data || detailResponse.data || {};
        const detailItems = (detailRoot?.items || detailRoot?.Items || []) as any[];
        const firstLineItem = Array.isArray(detailItems) ? detailItems[0] : null;
        const lineItemId = String(
          firstLineItem?.lineItemId ??
          firstLineItem?.id ??
          ''
        ).trim();

        if (!lineItemId) {
          throw new Error('Hepsiburada lineItemId bulunamadı.');
        }

        response = await hepsiburadaApi.get(`/api/hepsiburada/lineitems/${encodeURIComponent(lineItemId)}/cargo-companies`);
      }

      const root = response.data?.data || response.data?.Data || response.data || {};
      const rawList =
        (Array.isArray(root) && root) ||
        root?.items ||
        root?.Items ||
        root?.data ||
        root?.Data ||
        root?.content ||
        root?.Content ||
        [];
        const normalized = (Array.isArray(rawList) ? rawList : [])
          .map((item: any) => {
            const value = String(
              item?.shortName ??
              item?.ShortName ??
              item?.cargoCompanyId ??
              item?.id ??
              item?.Id ??
              item?.code ??
              item?.Code ??
              item?.value ??
              ''
            ).trim();
            const label = String(
              item?.cargoCompanyName ??
              item?.Name ??
              item?.name ??
              item?.shortName ??
              item?.ShortName ??
              item?.label ??
              item?.text ??
              value
            ).trim();
            if (!value) return null;
            return { value, label: label || value };
          })
          .filter((x): x is { value: string; label: string } => x !== null);

        if (normalized.length > 0) {
          setHepsiburadaCargoCompanies(normalized);
        }
      })().catch((error) => {
        console.error('❌ [ORDERS MANAGEMENT] Hepsiburada kargo firmaları alınamadı:', error);
      });
  };

  // İptal onay modalını aç
  const openCancelModal = (order: Order) => {
    setOrderToCancel(order);
    setCancelModalOpen(true);
  };

  // Siparişi iptal et
  const cancelOrder = async () => {
    if (!companyId || !orderToCancel) return;

    const isN11Order = orderToCancel.id?.startsWith('n11_') || orderToCancel.marketplace === 'n11';
    const isHepsiburadaOrder =
      normalizeMarketplaceSource(orderToCancel.marketplace) === 'hepsiburada' ||
      String(orderToCancel.memberEmail || '').toLowerCase().includes('@hepsiburada.marketplace.local') ||
      String(orderToCancel.orderNumber || '').toUpperCase().startsWith('HB-');

    setUpdating(orderToCancel.id);
    try {
      if (isHepsiburadaOrder) {
        const packageNumber = orderToCancel.orderNumber;
        try {
          await hepsiburadaApi.post(
            `/api/hepsiburada/packages/${encodeURIComponent(packageNumber)}/cancelbymerchant`
          );
        } catch (hbError: any) {
          const hbMsg: string = hbError?.response?.data?.Message || hbError?.message || 'Bilinmeyen HB hatası';
          // 400/406: sipariş durumu iptal için uygun değil (packed/shipped vb.)
          if (hbError?.response?.status === 400 || hbError?.response?.status === 406) {
            showSnackbar(
              `Hepsiburada bu siparişi iptal etmeye izin vermiyor: ${hbMsg}. Yerel kayıt güncellendi, HB panelinden manuel iptal edin.`,
              'warning'
            );
          } else {
            showSnackbar(`HB iptal bildirimi başarısız: ${hbMsg}. Yerel kayıt yine güncellendi.`, 'warning');
          }
        }

        // Yerel DB de güncelle
        const localResponse = await ecommerceApi.post('/api/ecommerce/orders/status', {
          orderId: orderToCancel.id,
          status: 'cancelled'
        });
        if (!localResponse.data.success) {
          throw new Error(localResponse.data.message || 'Yerel sipariş iptal edilirken hata oluştu');
        }
      } else if (isN11Order) {
        // N11 iptal: yalnizca yerel statu guncellenir.
      } else {
        const response = await ecommerceApi.post('/api/ecommerce/orders/status', {
          orderId: orderToCancel.id,
          status: 'cancelled'
        });
        const data = response.data;
        
        if (!data.success) {
          throw new Error(data.message || 'Sipariş iptal edilirken hata oluştu');
        }
      }

      setOrders(prev => prev.map(order =>
        order.id === orderToCancel.id ? { 
          ...order, 
          status: 'cancelled' as any
        } : order
      ));

      setCancelModalOpen(false);
      setOrderToCancel(null);
      
      showSnackbar('Sipariş başarıyla iptal edildi!', 'success');
    } catch (error) {
      console.error('❌ [ORDERS MANAGEMENT] Sipariş iptal edilirken hata:', error);
      setError('Sipariş iptal edilirken bir hata oluştu.');
      showSnackbar(`Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  // Kargo takip numarası ile birlikte kargolandı durumuna güncelle
  const updateOrderWithTracking = async () => {
    if (!companyId || !orderToShip || !trackingNumber.trim() || !shippingCompany) {
      showSnackbar('Lütfen kargo firması seçin ve takip numarası girin.', 'warning');
      return;
    }

    // GTS_SUNUM_MOCK
    if (isDemoOrder(orderToShip.id)) {
      setUpdating(orderToShip.id);
      applyDemoOrderStatusUpdate(orderToShip.id, 'shipped', 'Sipariş kargoya verildi (sunum demo).', {
        trackingNumber: trackingNumber.trim(),
        shippingCompany,
        cargoInfo: {
          cargoTrackingNumber: trackingNumber.trim(),
          cargoProviderName: shippingCompany,
        },
      });
      setTrackingModalOpen(false);
      setOrderToShip(null);
      setTrackingNumber('');
      setShippingCompany('');
      return;
    }

    // N11 siparişi mi kontrol et
    const isN11Order = orderToShip.id?.startsWith('n11_') || orderToShip.marketplace === 'n11';
    const isHepsiburadaOrder =
      normalizeMarketplaceSource(orderToShip.marketplace) === 'hepsiburada' ||
      String(orderToShip.memberEmail || '').toLowerCase().includes('@hepsiburada.marketplace.local') ||
      String(orderToShip.orderNumber || '').toUpperCase().startsWith('HB-');

    if (isHepsiburadaOrder && hepsiburadaCargoCompanies.length === 0) {
      showSnackbar('Hepsiburada kargo firmaları alınamadı. Lütfen tekrar deneyin.', 'warning');
      return;
    }

    setUpdating(orderToShip.id);
    try {
      if (isN11Order) {
        // N11 kargo: yalnizca yerel takip bilgisi guncellenir.
      } else {
        if (isHepsiburadaOrder) {
          const rawOrderNumber = String(orderToShip?.orderNumber || '').replace(/^HB-/i, '').trim();
          const rawPackageCandidate =
            (orderToShip as any)?.n11OrderId ||
            (orderToShip as any)?.marketplaceOrderId ||
            rawOrderNumber;
          let packageNumber = String(rawPackageCandidate || '').replace(/^HB-/i, '').trim();

          if (!packageNumber || (rawOrderNumber && packageNumber === rawOrderNumber)) {
            const detailResponse = await hepsiburadaApi.get(`/api/hepsiburada/orders/${encodeURIComponent(rawOrderNumber)}`);
            const detailRoot = detailResponse.data?.data || detailResponse.data?.Data || detailResponse.data || {};
            const resolvedPackageNumber = findFirstStringByKeys(detailRoot, ['packageNumber', 'packagenumber']);
            if (resolvedPackageNumber) {
              packageNumber = resolvedPackageNumber.trim();
            }
          }

          if (!packageNumber) {
            throw new Error('Hepsiburada package numarası bulunamadı.');
          }

          await hepsiburadaApi.post(
            `/api/hepsiburada/packages/${encodeURIComponent(packageNumber)}/cargo-company/change`,
            {
              cargoCompanyId: shippingCompany,
              trackingNumber: trackingNumber.trim()
            }
          );

          await hepsiburadaApi.post(
            `/api/hepsiburada/packages/${encodeURIComponent(packageNumber)}/intransit`,
            {
              trackingInfoCode: trackingNumber.trim(),
              shippedDate: new Date().toISOString()
            }
          );

          // HB tarafina iletildi; otomatik pull-missing ATILMAZ. Sadece UI yenile.
          setRefreshNonce((prev) => prev + 1);
        }

        // Pazarama: kargola = item bazli status 5 + tracking + kargo firmasi.
        if (isPazaramaOrder(orderToShip)) {
          const orderItemIds = resolvePazaramaOrderItemIds(orderToShip);
          if (orderItemIds.length === 0) {
            throw new Error('Pazarama sipariş kalemi (orderItemId) bulunamadı.');
          }
          await pazaramaApi.post('/api/pazarama/order-actions/status/bulk', {
            orderItemIds,
            status: 5,
            trackingNumber: trackingNumber.trim(),
            cargoCompanyId: shippingCompany
          });
          setRefreshNonce((prev) => prev + 1);
        }

        const response = await ecommerceApi.post('/api/ecommerce/orders/tracking', {
          orderId: orderToShip.id,
          trackingNumber: trackingNumber.trim(),
          shippingCompany: shippingCompany
        });
        const data = response.data;
        
        if (!data.success) {
          throw new Error(data.message || 'Kargo bilgileri güncellenirken hata oluştu');
        }
      }

      // Local state'i güncelle
      setOrders(prev => prev.map(order => 
        order.id === orderToShip.id ? { 
          ...order, 
          status: 'shipped' as any,
          trackingNumber: trackingNumber.trim(),
          shippingCompany: shippingCompany
        } : order
      ));

      setTrackingModalOpen(false);
      setOrderToShip(null);
      setTrackingNumber('');
      setShippingCompany('');
      
      showSnackbar('Kargo bilgileri başarıyla kaydedildi!', 'success');
    } catch (error) {
      console.error('❌ [ORDERS MANAGEMENT] Kargo takip numarası eklenirken hata:', error);

      const httpStatus = (error as any)?.response?.status;
      const backendMessage = String(
        (error as any)?.response?.data?.message ??
        (error as any)?.response?.data?.Message ??
        (error as any)?.message ??
        ''
      );
      const isHepsiburada403 = isHepsiburadaOrder && httpStatus === 403;
      const isDeliveryForbidden =
        isHepsiburada403 &&
        /DeliveryManipulationForbidden/i.test(backendMessage);

      if (isHepsiburada403) {
        const friendlyMessage = isDeliveryForbidden
          ? 'Hepsiburada bu paket için manuel kargo bildirimine izin vermiyor (entegre kargo / gönderime hazır aşaması). HB panelinden veya kargo firmasının kendi akışından kargoya verin; GTS statüsü reconcile ile güncellenir.'
          : 'Hepsiburada bu sipariş için kargo işlemini şu anda kabul etmedi. HB tarafına yansımadı ve sipariş durumu değiştirilmedi.';
        showSnackbar(friendlyMessage, 'warning');
        return;
      }

      setError('Kargo takip numarası eklenirken bir hata oluştu.');
      showSnackbar(`Hata: ${backendMessage || 'Bilinmeyen hata'}`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  // Firmalar arası B2B: "Hazırlanıyor" adımındaki siparişi tek hamlede sevke alır
  // (Akıllı Depo OPSİYONEL). Sevk irsaliyesi kesilir, sipariş "Sevkte" (shipped) olur
  // ve alıcının B2B ekranına yansır — WMS'e girmeden. Onay uygulama modalındadır.
  // Menü kapanışı ile modal aynı tıklamada açılırsa ilk basış menünün artığına gider
  // ve aktarmaz. Modal, menü kapandıktan sonra açılır.
  const openSendToShipmentModal = (order: Order) => {
    orderToSendShipmentRef.current = order;
    setOrderToSendShipment(order);
    window.setTimeout(() => setShipmentConfirmOpen(true), 0);
  };

  const closeSendToShipmentModal = () => {
    if (updating && updating === orderToSendShipmentRef.current?.id) return;
    setShipmentConfirmOpen(false);
    setOrderToSendShipment(null);
    orderToSendShipmentRef.current = null;
  };

  const handleSendToShipment = async () => {
    const order = orderToSendShipmentRef.current ?? orderToSendShipment;
    if (!companyId || !order) return;
    if (updating === order.id) return;
    setUpdating(order.id);
    try {
      const response = await ecommerceApi.post(
        `/api/ecommerce/orders/${encodeURIComponent(order.id)}/sevkiyata-gonder`,
        {}
      );
      const data = response.data;
      if (!data?.success) {
        throw new Error(data?.message || 'Sevkiyata gönderilemedi.');
      }

      const newWaybill = data?.data?.waybillNumber ?? order.waybillNumber ?? null;
      const newWaybillId = data?.data?.waybillId ?? data?.data?.WaybillId ?? order.waybillId ?? null;
      const newStatus = (data?.data?.orderStatus as Order['status']) || 'shipped';
      setOrders(prev => prev.map(o =>
        o.id === order.id
          ? { ...o, status: newStatus, waybillNumber: newWaybill, waybillId: newWaybillId }
          : o
      ));
      setShipmentConfirmOpen(false);
      setOrderToSendShipment(null);
      orderToSendShipmentRef.current = null;
      setRefreshNonce((prev) => prev + 1);
      showSnackbar(data?.message || 'Sipariş sevkiyata gönderildi.', 'success');
    } catch (error: any) {
      const backendMessage = String(
        error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.message ?? ''
      );
      console.error('❌ [ORDERS MANAGEMENT] B2B sevkiyata gönderme hatası:', error);
      showSnackbar(`Hata: ${backendMessage || 'Sevkiyata gönderilemedi.'}`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  const resolveMarketplaceSource = (order: Order): string =>
    order.orderNumber?.startsWith('ORD')
      ? 'website'
      : normalizeMarketplaceSource((order as any).marketplace);

  const orderCounts = useMemo(
    () => ({
      pending: orders.filter((o) => o.status === 'pending').length,
      confirmed: orders.filter((o) => o.status === 'confirmed').length,
      shipped: orders.filter((o) => o.status === 'shipped').length,
      delivered: orders.filter((o) => o.status === 'delivered').length,
      cancel_requested: orders.filter((o) => o.status === 'cancel_requested').length,
      cancelled: orders.filter((o) => o.status === 'cancelled').length,
      received: orders.filter((o) => o.status === 'received').length,
    }),
    [orders]
  );

  // Duruma göre filtrelenmiş siparişler
  const activeStatusFilter = tabs[activeTab].status;
  const filteredOrders = useMemo(() => {
    if (activeStatusFilter === 'return_requests') return [];
    let result = orders.filter((order) => order.status === activeStatusFilter);
    if (platformFilter !== 'all') {
      result = result.filter((order) => resolveMarketplaceSource(order) === platformFilter);
    }
    if (guestOnly) {
      result = result.filter((order) => order.isGuest === true);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((order) => {
        const sa = (order.shippingAddress || {}) as any;
        const haystack = [
          order.orderNumber,
          order.customerName,
          order.memberName,
          sa.firstName,
          sa.lastName,
          `${sa.firstName || ''} ${sa.lastName || ''}`,
          sa.phone,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    return result;
  }, [orders, activeStatusFilter, platformFilter, searchQuery, guestOnly]);

  const handleSort = (col: 'date' | 'amount' | 'productCount') => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
    setPage(0);
  };

  const sortedOrders = useMemo(() => {
    const copy = [...filteredOrders];
    copy.sort((a, b) => {
      let valA: number;
      let valB: number;
      if (sortBy === 'date') {
        valA = new Date(a.date || 0).getTime();
        valB = new Date(b.date || 0).getTime();
      } else if (sortBy === 'amount') {
        valA = a.totalAmount ?? 0;
        valB = b.totalAmount ?? 0;
      } else {
        valA = (a.items?.length ?? (a as any).itemCount ?? 0);
        valB = (b.items?.length ?? (b as any).itemCount ?? 0);
      }
      return sortDir === 'asc' ? valA - valB : valB - valA;
    });
    return copy;
  }, [filteredOrders, sortBy, sortDir]);

  const paginatedOrders = useMemo(
    () => sortedOrders.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [sortedOrders, page, rowsPerPage]
  );

  const handleReturnSort = (col: 'date' | 'amount') => {
    if (returnSortBy === col) {
      setReturnSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setReturnSortBy(col);
      setReturnSortDir('desc');
    }
    setReturnPage(0);
  };

  const filteredReturns = useMemo(() => {
    if (platformFilter === 'all') return returns;
    return returns.filter(
      (item) => normalizeMarketplaceSource(item.marketplace) === platformFilter
    );
  }, [returns, platformFilter]);

  const sortedReturns = useMemo(() => {
    const copy = [...filteredReturns];
    copy.sort((a, b) => {
      const valA = returnSortBy === 'date'
        ? new Date(a.requestDate || 0).getTime()
        : (a.finalPrice ?? 0);
      const valB = returnSortBy === 'date'
        ? new Date(b.requestDate || 0).getTime()
        : (b.finalPrice ?? 0);
      return returnSortDir === 'asc' ? valA - valB : valB - valA;
    });
    return copy;
  }, [filteredReturns, returnSortBy, returnSortDir]);

  const paginatedReturns = useMemo(
    () => sortedReturns.slice(returnPage * returnRowsPerPage, (returnPage + 1) * returnRowsPerPage),
    [sortedReturns, returnPage, returnRowsPerPage]
  );

  const getTabOrderCount = (status: string): number => {
    if (status === 'return_requests') return filteredReturns.length;
    return orders.filter((order) => {
      if (order.status !== status) return false;
      if (platformFilter !== 'all' && resolveMarketplaceSource(order) !== platformFilter) return false;
      if (guestOnly && order.isGuest !== true) return false;
      return true;
    }).length;
  };

  // Pazaryeri bilgisini al
  const getMarketplaceInfo = (order: Order) => {
    return getMarketplaceVisualInfo(resolveMarketplaceSource(order), t);
  };

  // Durum chip'i
  const getStatusChip = (status: string) => {
    const normalizedStatus = normalizeOrderStatus(status);
    const statusConfig = {
      received: { label: t('ecommerceOrders.status.received'), color: 'success' as const }, // ✅ Sipariş alındı (OrderStatus)
      pending: { label: t('ecommerceOrders.status.pending'), color: 'warning' as const }, // Sipariş beklemede
      confirmed: { label: t('ecommerceOrders.status.preparing'), color: 'info' as const }, // Sipariş onaylandı, hazırlanıyor
      shipped: {
        label: embedded ? t('ecommerceOrders.status.onWaybill') : t('ecommerceOrders.status.shipped'),
        color: 'primary' as const,
      },
      delivered: { label: t('ecommerceOrders.status.delivered'), color: 'success' as const }, // Teslim edildi
      cancel_requested: { label: t('ecommerceOrders.status.cancelRequested'), color: 'warning' as const }, // İptal talebi var
      cancelled: { label: t('ecommerceOrders.status.cancelled'), color: 'error' as const }, // İptal edildi
      // Eski sistem uyumluluğu:
      paid: { label: t('ecommerceOrders.status.received'), color: 'success' as const } // Eski siparişler için
    };

    const config = statusConfig[normalizedStatus as keyof typeof statusConfig] || statusConfig.received;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Durum değiştirme butonları (3 nokta menü)
  const getStatusActions = (order: Order) => {
    const n11Order = isN11Order(order);
    const trendyolOrder = isTrendyolOrder(order);
    const hepsiburadaOrder = isHepsiburadaOrder(order);
    const pazaramaOrder = isPazaramaOrder(order);
    // E-ticaret (website) siparişi: pazaryeri kaynaklı değilse HepsiJet kargosu oluşturulabilir.
    const websiteOrder = !n11Order && !trendyolOrder && !hepsiburadaOrder &&
      normalizeMarketplaceSource((order as any).marketplace) === 'website';
    const menuOpen = Boolean(actionsMenuAnchorEl) && actionsMenuOrderId === order.id;

    type ActionMenuItem = {
      key: string;
      label: string;
      icon: React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
    };

    const actionItems: ActionMenuItem[] = [];

    actionItems.push({
      key: 'view',
      label: 'Detayları Görüntüle',
      icon: <VisibilityIcon fontSize="small" />,
      onClick: () => showOrderDetails(order),
    });

    if (websiteOrder && order.invoiceId) {
      actionItems.push({
        key: 'view-invoice',
        label: order.invoiceNumber ? `Fatura Göster (${order.invoiceNumber})` : 'Fatura Göster',
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => handleViewWebsiteInvoice(order),
      });
      if (
        order.isInterCompanyB2B &&
        !['SENT', 'APPROVED'].includes((order.eInvoiceStatus || '').toUpperCase())
      ) {
        actionItems.push({
          key: 'send-einvoice',
          label: 'E-Fatura Gönder',
          icon: <SendIcon fontSize="small" />,
          onClick: () => setEInvoiceSendOrder(order),
        });
      }
    } else if (canInvoiceWebsiteOrder(order)) {
      actionItems.push({
        key: 'invoice',
        label: 'Faturala',
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => openInvoiceDialog([order]),
        disabled: updating === order.id || invoicing,
      });
    }

    const cargoTrackingLink =
      order.cargoInfo?.cargoTrackingLink ||
      (order as any).cargoTrackingLink ||
      '';
    if (n11Order) {
      actionItems.push({
        key: 'track-cargo-link',
        label: 'Kargo Takip Linkini Aç',
        icon: <LocalShippingIcon fontSize="small" />,
        onClick: () => {
          if (cargoTrackingLink) {
            window.open(cargoTrackingLink, '_blank', 'noopener,noreferrer');
            return;
          }
          showSnackbar('Bu sipariş için henüz kargo takip linki bulunamadı', 'info');
        },
      });
    }

    const wmsStatus = wmsStatuses[order.id];
    const isAwaitingStock =
      String(wmsStatus?.wmsStatus || '').toLowerCase() === 'awaitingstock';
    // GTS_SUNUM_MOCK: pazaryeri demo siparişlerinde WMS aksiyonu yok (e-ticaret canlı gösterilir)
    // AwaitingStock: mal kabul sonrası tekrar "Havuza Al" (stok promote) göster.
    if (
      !embedded &&
      !isDemoOrder(order.id) &&
      (!wmsStatus?.isTransferred || isAwaitingStock) &&
      ['confirmed', 'paid', 'received'].includes(order.status)
    ) {
      actionItems.push({
        key: 'wms-transfer',
        label: isAwaitingStock ? 'Havuza Al (stok kontrolü)' : "WMS'e Aktar",
        icon: <WarehouseIcon fontSize="small" />,
        onClick: () => {
          setWmsTransferOrders([{
            id: order.id,
            orderNumber: order.orderNumber,
            customerName: order.customerName || `${(order as any).firstName || ''} ${(order as any).lastName || ''}`.trim() || t('ecommerceOrders.marketplaces.unknown'),
            platform: order.marketplace || 'website',
          }]);
          setWmsTransferModalOpen(true);
        },
      });
    }

    if (n11Order) {
      actionItems.push({
        key: 'print-n11',
        label: 'N11 Kargo Bilgilerini Yazdır',
        icon: <PrintIcon fontSize="small" />,
        onClick: () => handleN11Print(order),
      });
    } else if (trendyolOrder) {
      actionItems.push({
        key: 'print-trendyol',
        label: 'Trendyol Kargo Etiketi Yazdır',
        icon: <PrintIcon fontSize="small" />,
        onClick: () => handleTrendyolPrint(order),
      });
      actionItems.push({
        key: 'print-trendyol-sticker',
        label: 'Trendyol Kargo Sticker Yazdır',
        icon: <PrintIcon fontSize="small" />,
        onClick: () => handleTrendyolStickerPrint(order),
      });
    } else if (hepsiburadaOrder) {
      actionItems.push({
        key: 'print-hepsiburada',
        label: 'Hepsiburada Kargo Etiketi Yazdır',
        icon: <PrintIcon fontSize="small" />,
        onClick: () => handleHepsiburadaPrint(order),
      });
    }

    if (pazaramaOrder) {
      actionItems.push({
        key: 'pazarama-invoice-link',
        label: 'Pazarama Fatura Linki Gönder',
        icon: <PrintIcon fontSize="small" />,
        onClick: () => handlePazaramaInvoiceLink(order),
        disabled: updating === order.id,
      });
    }

    if (order.status === 'received' || order.status === 'pending' || order.status === 'paid') {
      if (n11Order) {
        actionItems.push({
          key: 'approve-n11',
          label: 'N11 Siparişini Onayla',
          icon: <CheckCircleIcon fontSize="small" />,
          onClick: () => approveN11Order(order),
          disabled: updating === order.id,
        });
      } else if (trendyolOrder) {
        actionItems.push({
          key: 'trendyol-picking',
          label: 'Trendyol Picking',
          icon: <CheckCircleIcon fontSize="small" />,
          onClick: () => handleTrendyolPicking(order),
          disabled: updating === order.id,
        });
        actionItems.push({
          key: 'trendyol-unsupplied',
          label: 'Trendyol Tedarik Edememe',
          icon: <CancelIcon fontSize="small" />,
          onClick: () => handleTrendyolUnsupplied(order),
          disabled: updating === order.id,
        });
      } else {
        actionItems.push({
          key: 'confirm',
          label: 'Hazırla',
          icon: <CheckCircleIcon fontSize="small" />,
          onClick: () => updateOrderStatus(order.id, order.memberId, 'confirmed'),
          disabled: updating === order.id,
        });
      }
    }

    if (order.status === 'confirmed') {
      if (!embedded && !n11Order && !trendyolOrder && !hepsiburadaOrder) {
        actionItems.push({
          key: 'print',
          label: 'Sipariş Detaylarını Yazdır',
          icon: <PrintIcon fontSize="small" />,
          onClick: () => printOrderDetails(order),
        });
      }

      if (trendyolOrder) {
        actionItems.push({
          key: 'trendyol-invoiced',
          label: 'Trendyol Faturalandı',
          icon: <LocalShippingIcon fontSize="small" />,
          onClick: () => handleTrendyolInvoiced(order),
          disabled: updating === order.id,
        });
      } else if (!n11Order) {
        // Kargo ve depo bu sekmede yok; B2B sipariş irsaliye kesilerek sevke alınır.
        if (!embedded && websiteOrder) {
          actionItems.push({
            key: 'hepsijet-ship',
            label: 'HepsiJet Kargo Oluştur',
            icon: <LocalShippingIcon fontSize="small" />,
            onClick: () => handleCreateHepsiJetShipment(order),
            disabled: updating === order.id,
          });
          actionItems.push({
            key: 'mng-ship',
            label: 'MNG (DHL) Kargo Oluştur',
            icon: <LocalShippingIcon fontSize="small" />,
            onClick: () => handleCreateMngShipment(order),
            disabled: updating === order.id,
          });
        }
        // Firmalar arası B2B: tek hamlede irsaliye kes ve Sevkte sekmesine aktar.
        if (order.isInterCompanyB2B) {
          actionItems.push({
            key: 'send-to-shipment',
            label: embedded ? t('ecommerceOrders.actions.transferToWaybill') : 'Sevkiyata Gönder (B2B)',
            icon: <WarehouseIcon fontSize="small" />,
            onClick: () => openSendToShipmentModal(order),
            disabled: updating === order.id,
          });
        }
        if (!embedded) {
          actionItems.push({
            key: 'ship',
            label: 'Kargola',
            icon: <LocalShippingIcon fontSize="small" />,
            onClick: () => openTrackingModal(order),
            disabled: updating === order.id,
          });
        }
      }
    }

    if (order.status === 'shipped' && !n11Order && !trendyolOrder) {
      actionItems.push({
        key: 'deliver',
        label: 'Teslim Et',
        icon: <DoneIcon fontSize="small" />,
        onClick: () => updateOrderStatus(order.id, order.memberId, 'delivered'),
        disabled: updating === order.id,
      });
    }

    if (order.status === 'cancel_requested') {
      actionItems.push({
        key: 'approve-cancel',
        label: 'İptal Talebini Onayla',
        icon: <CheckCircleIcon fontSize="small" />,
        onClick: () => updateOrderStatus(order.id, order.memberId, 'cancelled'),
        disabled: updating === order.id,
      });

      actionItems.push({
        key: 'reject-cancel',
        label: 'İptal Talebini Reddet',
        icon: <CancelIcon fontSize="small" />,
        onClick: () => {
          setOrderToAdminCancel(order);
          setAdminCancelReason('');
          setAdminCancelModalOpen(true);
        },
        disabled: updating === order.id,
      });
    }

    // İlk aşamadaki (received/pending) siparişler de dahil — sipariş düştüğü gibi iptal edilebilmeli
    if ((order.status === 'received' || order.status === 'pending' || order.status === 'paid' || order.status === 'confirmed')
        && !(n11Order && order.status === 'confirmed') && !trendyolOrder) {
      actionItems.push({
        key: 'cancel',
        label: 'İptal Et',
        icon: <CancelIcon fontSize="small" />,
        onClick: () => openCancelModal(order),
        disabled: updating === order.id,
      });
    }

    return (
      <>
        <IconButton
          size="small"
          onClick={(event) => {
            setActionsMenuAnchorEl(event.currentTarget);
            setActionsMenuOrderId(order.id);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={actionsMenuAnchorEl}
          open={menuOpen}
          disableRestoreFocus
          onClose={() => {
            setActionsMenuAnchorEl(null);
            setActionsMenuOrderId(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actionItems.map((item) => (
            <MenuItem
              key={item.key}
              disabled={item.disabled}
              onClick={() => {
                setActionsMenuAnchorEl(null);
                setActionsMenuOrderId(null);
                item.onClick();
              }}
            >
              <Box sx={{ mr: 1.25, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                {item.icon}
              </Box>
              <Typography variant="body2">{item.label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  // N11 siparişini onayla
  const approveN11Order = async (order: Order) => {
    if (!companyId) {
      showSnackbar('Şirket bilgisi bulunamadı', 'error');
      return;
    }

    // N11 siparişi mi kontrol et
    const isN11Order = order.id?.startsWith('n11_') || (order as any).marketplace === 'n11';
    if (!isN11Order) {
      showSnackbar('Bu sipariş N11 siparişi değil', 'error');
      return;
    }

    // GTS_SUNUM_MOCK
    if (isDemoOrder(order.id)) {
      setUpdating(order.id);
      applyDemoOrderStatusUpdate(order.id, 'confirmed', `N11 siparişi ${order.orderNumber} onaylandı (sunum demo).`);
      return;
    }

    setUpdating(order.id);
    try {
      const mapApproveItems = (raw: any): Array<{ n11LineId: string; n11OrderLineId: string }> =>
        parseMarketplaceItems(raw)
          .map((item: any) => {
            const lineId = firstNonEmptyText(item.n11OrderLineId, item.n11LineId, item.orderLineId, item.lineId);
            if (!lineId) return null;
            return { n11LineId: lineId, n11OrderLineId: lineId };
          })
          .filter((item): item is { n11LineId: string; n11OrderLineId: string } => item !== null);

      let orderItems = mapApproveItems(order.items);

      if (orderItems.length === 0) {
        const fallbackSources = [
          (order as any).cargoInfo,
          (order as any).packageHistories,
          extractN11RawItems(order),
          extractN11RawItems((order as any).cargoInfo)
        ];
        for (const source of fallbackSources) {
          orderItems = mapApproveItems(source);
          if (orderItems.length > 0) break;
        }
      }

      const response = await n11Api.post('/api/n11/orders/approve', {
        orderId: order.n11OrderId || order.orderNumber || order.id,
        orderItems
      });
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Sipariş onaylanırken hata oluştu');
      }

      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: 'confirmed' as any } : o
      ));

      showSnackbar(`N11 siparişi ${order.orderNumber} başarıyla onaylandı!`, 'success');

    } catch (error: any) {
      console.error('[N11 APPROVAL] Sipariş onaylama hatası:', error);
      const isNetwork = error?.code === 'ERR_NETWORK' || error?.message === 'Network Error';
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.Error ||
        error?.response?.data?.message ||
        error?.response?.data?.Message;
      const message = isNetwork
        ? 'N11 API’ye bağlanılamadı. N11 servisinin localhost:5013 üzerinde çalıştığından emin olun.'
        : (apiMessage || (error instanceof Error ? error.message : 'Bilinmeyen hata'));
      showSnackbar(`Hata: ${message}`, 'error');
    } finally {
      setUpdating(null);
    }
  };

  // Pazaryerlerinden siparişleri çek
  // N11 sipariş yazdırma
  const handleN11Print = (order: Order) => {
    setSelectedPrintOrder(order);
    setPrintModalOpen(true);
  };

  const handleTrendyolPrint = (order: Order) => {
    setSelectedTrendyolPrintOrder(order);
    setTrendyolPrintModalOpen(true);
  };

  const handleTrendyolStickerPrint = (order: Order) => {
    setSelectedTrendyolStickerOrder(order);
    setTrendyolStickerModalOpen(true);
  };

  const handleHepsiburadaPrint = (order: Order) => {
    setSelectedHepsiburadaPrintOrder(order);
    setHepsiburadaPrintModalOpen(true);
  };

  // İade durumunu Türkçe'ye çevir
  const getReturnStatusText = (status: string) => {
    const normalized = (status || '').replace(/[_\s-]/g, '').toUpperCase();
    switch (normalized) {
      case 'REQUESTED':
      case 'CREATED':
        return t('ecommerceOrders.returnStatus.requested');
      case 'CANCELREQUESTED':
        return t('ecommerceOrders.status.cancelRequested') || 'İptal Talebi';
      case 'WAITINGINACTION':
        return 'Aksiyon Bekliyor';
      case 'WAITINGFRAUDCHECK':
        return 'Fraud Kontrolünde';
      case 'APPROVED':
      case 'ACCEPTED':
        return t('ecommerceOrders.returnStatus.approved');
      case 'REFUNDED':
        return 'İade Edildi';
      case 'PICKUPSCHEDULED':
        return 'Kargo Planlandı';
      case 'PICKEDUP':
        return 'Kargoya Verildi';
      case 'RECEIVED':
        return 'Teslim Alındı';
      case 'INSPECTED':
        return 'İncelendi';
      case 'CANCELLED':
        return t('ecommerceOrders.returnStatus.cancelled');
      case 'PENDING':
        return t('ecommerceOrders.returnStatus.pending');
      case 'REJECTED':
        return t('ecommerceOrders.returnStatus.rejected');
      case 'UNRESOLVED':
        return 'İhtilaflı';
      case 'INANALYSIS':
        return 'Analizde';
      default:
        return status;
    }
  };

  const isWebsiteCancelReturn = (item: Return) =>
    Boolean(item.isCancelRequest)
    || String(item.id || '').startsWith('cancel-req:')
    || String(item.status || '').toLowerCase() === 'cancel_requested';

  const formatReturnDateTime = (value?: string) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const openReturnDetail = (item: Return) => {
    setReturnDetailItem(item);
    setReturnDetailOpen(true);
  };

  // N11 İade talebini reddet
  const handleRejectReturn = async () => {
    if (!companyId || !returnToReject || !rejectReason.trim()) {
      showSnackbar('Lütfen reddetme nedeni girin', 'warning');
      return;
    }

    if (rejectReason.trim().length < 5) {
      showSnackbar('Reddetme nedeni en az 5 karakter olmalıdır', 'warning');
      return;
    }

    setRejectingReturn(true);
    try {
      console.log('❌ [RETURN REJECT] İade talebi reddediliyor:', returnToReject.claimReturnId);

      if (normalizeMarketplaceSource(returnToReject.marketplace) === 'website' && isWebsiteCancelReturn(returnToReject)) {
        // Vitrin iptal talebi: siparişi confirmed'a geri al
        const orderId = returnToReject.sourceOrderId
          || String(returnToReject.id).replace(/^cancel-req:/, '');
        const response = await ecommerceApi.post('/api/ecommerce/orders/status', {
          orderId,
          status: 'confirmed',
          rejectReason: rejectReason.trim()
        });
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'İptal talebi reddedilirken hata oluştu');
        }
        setReturns((prev) => prev.filter((r) => r.id !== returnToReject.id && r.sourceOrderId !== orderId));
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId ? { ...o, status: 'confirmed' as const, cancelRequest: undefined } : o
          )
        );
        setRefreshNonce((prev) => prev + 1);
      } else if (normalizeMarketplaceSource(returnToReject.marketplace) === 'website') {
        // Sitenin kendi iadesi: ret nedeni müşteriye vitrinde gösterilir
        const response = await ecommerceApi.post('/api/ecommerce/returns/update-status', {
          returnId: returnToReject.id,
          status: 'Rejected',
          rejectionReason: rejectReason.trim()
        });
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'İade reddedilirken hata oluştu');
        }
        setRefreshNonce((prev) => prev + 1);
        console.log('✅ [RETURN REJECT] Site iadesi reddedildi');
      } else if (normalizeMarketplaceSource(returnToReject.marketplace) === 'trendyol') {
        const claimId = returnToReject.claimId || returnToReject.claimReturnId;
        const claimItemIds = returnToReject.claimItemIds || [];
        if (!claimId || claimItemIds.length === 0) {
          throw new Error('Trendyol iade reddi için claimId ve claimItemId gerekli');
        }

        const reasonsResp = await trendyolMpApi.get('/api/TrendyolClaims/issue-reasons');
        const reasons = Array.isArray(reasonsResp.data?.data) ? reasonsResp.data.data : [];
        const preferredReasonIds = [451, 1651, 2101];
        const selectedReason = reasons.find((r: any) => preferredReasonIds.includes(Number(r?.id))) || reasons[0];
        if (!selectedReason?.id) {
          throw new Error('Trendyol iade red nedeni bulunamadı');
        }

        const params = new URLSearchParams();
        params.set('claimIssueReasonId', String(selectedReason.id));
        claimItemIds.forEach((claimItemId) => params.append('claimItemIdList', claimItemId));
        params.set('description', rejectReason.trim());

        await trendyolMpApi.post(`/api/TrendyolClaims/claims/${encodeURIComponent(claimId)}/issue?${params.toString()}`);
      } else if (normalizeMarketplaceSource(returnToReject.marketplace) === 'hepsiburada') {
        const claimNumber = returnToReject.claimId || returnToReject.claimReturnId;
        if (!claimNumber) {
          throw new Error('Hepsiburada talep reddi için claimNumber gerekli');
        }

        await hepsiburadaApi.post('/api/hepsiburada/claims/reject', {
          claimNumber,
          claimRejectionReason: rejectReason.trim(),
          merchantStatement: rejectReason.trim()
        });
      } else if (normalizeMarketplaceSource(returnToReject.marketplace) === 'pazarama') {
        const refundId = returnToReject.claimId || returnToReject.claimReturnId;
        if (!refundId) {
          throw new Error('Pazarama iade reddi için refundId gerekli');
        }

        // Pazarama refund status: 3 = Tedarikci Tarafindan Reddedildi, refundRejectType 1..12.
        await pazaramaApi.post('/api/pazarama/order-actions/refunds/update', {
          refundId,
          status: 3,
          refundRejectType: 1,
          description: rejectReason.trim()
        });
      } else {
        const response = await n11Api.post('/api/n11/returns/reject', {
          claimReturnId: returnToReject.claimReturnId,
          denyReasonNote: rejectReason.trim(),
          denyReasonId: 1,
          returnShipmentType: '',
          shipmentCompanyId: '',
          trackingNumber: '',
          deliveryReceiptNumber: ''
        });
        const data = response.data;

        if (!data.success) {
          throw new Error(data.error || 'İade reddedilirken hata oluştu');
        }
      }

      setRejectReturnModalOpen(false);
      setReturnToReject(null);
      setRejectReason('');

      showSnackbar(`İade talebi ${returnToReject.claimReturnId} başarıyla reddedildi!`, 'success');

    } catch (error) {
      console.error('[RETURN REJECT] İade reddetme hatası:', error);
      showSnackbar(`Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
    } finally {
      setRejectingReturn(false);
    }
  };

  // N11 İade talebini onayla
  const handleApproveReturn = async (returnItem: Return) => {
    if (!companyId) {
      showSnackbar('Şirket bilgisi bulunamadı', 'error');
      return;
    }

    // Vitrin iptal talebi (İADE TALEPLERİ'ne düşen cancel_requested): doğrudan iptal onayı
    if (normalizeMarketplaceSource(returnItem.marketplace) === 'website' && isWebsiteCancelReturn(returnItem)) {
      const orderId = returnItem.sourceOrderId
        || String(returnItem.id).replace(/^cancel-req:/, '');
      setApprovingReturn(returnItem.id);
      try {
        const response = await ecommerceApi.post('/api/ecommerce/orders/status', {
          orderId,
          status: 'cancelled'
        });
        if (!response.data?.success) {
          throw new Error(response.data?.message || 'İptal talebi onaylanırken hata oluştu');
        }

        // Optimistic UI: anında listeden düşür + İptal Edildi'ye ekle (ürün kalemi korunur)
        setReturns((prev) => prev.filter((r) => r.id !== returnItem.id && r.sourceOrderId !== orderId));
        setOrders((prev) => {
          const existing = prev.find((o) => o.id === orderId);
          if (existing) {
            return prev.map((o) =>
              o.id === orderId ? { ...o, status: 'cancelled' as const, cancelRequest: undefined } : o
            );
          }
          const fallbackItems =
            returnItem.productName && returnItem.productName !== '-'
              ? [{
                  productId: returnItem.productId || orderId,
                  name: returnItem.productName,
                  image: '/api/placeholder/80/80',
                  price: returnItem.finalPrice || 0,
                  quantity: returnItem.quantity || 1,
                }]
              : [];
          return [
            {
              id: orderId,
              orderId,
              orderNumber: returnItem.orderNumber,
              memberId: 'unknown',
              memberName: returnItem.buyerName,
              memberEmail: returnItem.buyerEmail,
              customerName: returnItem.buyerName,
              date: returnItem.requestDate || new Date().toISOString(),
              status: 'cancelled' as const,
              totalAmount: returnItem.finalPrice || 0,
              currency: returnItem.currency ?? null,
              items: fallbackItems,
              marketplace: 'website',
            } as Order,
            ...prev,
          ];
        });

        // Arka planda soft sync
        setRefreshNonce((prev) => prev + 1);
        showSnackbar(`İptal talebi onaylandı: ${returnItem.orderNumber}`, 'success');
      } catch (error) {
        showSnackbar(`Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
      } finally {
        setApprovingReturn(null);
      }
      return;
    }

    // Sitenin kendi iadesi: doğrudan işlemek yerine seçenekli onay diyaloğu açılır
    // (iade yöntemi bakiye/sağlayıcı, kargo iadesi, e-Fatura mükellefi iade faturası).
    if (normalizeMarketplaceSource(returnItem.marketplace) === 'website') {
      setSiteApproveTarget(returnItem);
      setSiteRefundMode('balance');
      setSiteRefundShipping(false);
      setSiteCustomerIssuesInvoice(false);
      setMukellefResult(null);
      setMukellefVerified(false);
      setSiteApproveDialogOpen(true);
      // VKN/TCKN varsa e-Fatura mükellefiyeti GİB'den OTOMATİK sorgulanır (kesin kaynak);
      // sonuç "müşteri iade faturası kesecek" seçeneğini ön-işaretler, elle değiştirilebilir.
      if (returnItem.billingTaxNumber?.trim()) {
        void handleMukellefSorgula(returnItem.billingTaxNumber.trim());
      }
      return;
    }

    setApprovingReturn(returnItem.id);
    try {
      if (normalizeMarketplaceSource(returnItem.marketplace) === 'trendyol') {
        const claimId = returnItem.claimId || returnItem.claimReturnId;
        const claimItemIds = returnItem.claimItemIds || [];
        if (!claimId || claimItemIds.length === 0) {
          throw new Error('Trendyol iade onayı için claimId ve claimItemId gerekli');
        }

        await trendyolMpApi.put(`/api/TrendyolClaims/claims/${encodeURIComponent(claimId)}/items/approve`, {
          claimLineItemIdList: claimItemIds,
          params: {}
        });
      } else if (normalizeMarketplaceSource(returnItem.marketplace) === 'hepsiburada') {
        const claimNumber = returnItem.claimId || returnItem.claimReturnId;
        if (!claimNumber) {
          throw new Error('Hepsiburada talep onayı için claimNumber gerekli');
        }

        await hepsiburadaApi.post('/api/hepsiburada/claims/accept', {
          claimNumber,
          finalizedWith: 'Refund',
          acceptionReason: 'ApprovedFromPanel'
        });
      } else if (normalizeMarketplaceSource(returnItem.marketplace) === 'pazarama') {
        const refundId = returnItem.claimId || returnItem.claimReturnId;
        if (!refundId) {
          throw new Error('Pazarama iade onayı için refundId gerekli');
        }

        // Pazarama refund status: 2 = Tedarikci Tarafindan Onaylandi.
        await pazaramaApi.post('/api/pazarama/order-actions/refunds/update', {
          refundId,
          status: 2
        });
      } else {
        const response = await n11Api.post('/api/n11/returns/approve', {
          claimReturnId: returnItem.claimReturnId
        });
        const data = response.data;

        if (!data.success) {
          throw new Error(data.error || 'İade onaylanırken hata oluştu');
        }
      }

      showSnackbar(`İade talebi ${returnItem.claimReturnId} başarıyla onaylandı!`, 'success');

    } catch (error) {
      console.error('[RETURN APPROVAL] İade onaylama hatası:', error);
      showSnackbar(`Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
    } finally {
      setApprovingReturn(null);
    }
  };

  // Site iadesi: seçilen opsiyonlarla ters kaydı işle (İADE faturası + cari alacak +
  // stok RETURN + seçilen para iadesi yöntemi). e-Fatura mükellefi müşteri seçiliyse
  // sistem iade faturası KESMEZ (müşteri kesecek), yalnız para iadesi işaretlenir.
  const handleSiteApproveConfirm = async () => {
    if (!siteApproveTarget) return;
    setSiteApproveSaving(true);
    try {
      const response = await ecommerceApi.post('/api/ecommerce/returns/process-refund', {
        returnId: siteApproveTarget.id,
        refundMode: siteRefundMode,
        refundShipping: siteRefundShipping,
        customerIssuesReturnInvoice: siteCustomerIssuesInvoice,
        processedBy: 'orders_panel'
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'İade işlenirken hata oluştu');
      }
      const invoiceNo = response.data?.returnInvoiceNumber;
      showSnackbar(
        invoiceNo
          ? `İade işlendi. İade faturası: ${invoiceNo}`
          : 'İade işlendi.' + (siteCustomerIssuesInvoice ? ' Müşterinin iade faturası bekleniyor.' : ''),
        'success'
      );
      setSiteApproveDialogOpen(false);
      setSiteApproveTarget(null);
      setRefreshNonce((prev) => prev + 1);
    } catch (error: any) {
      // Backend 500 yanıtında asıl sebep "error" alanında döner; teşhis için onu da göster.
      const data = error?.response?.data;
      const msg = data?.error || data?.message || (error instanceof Error ? error.message : 'Bilinmeyen hata');
      showSnackbar(`Hata: ${msg}`, 'error');
    } finally {
      setSiteApproveSaving(false);
    }
  };

  // GİB mükellef sorgulama: müşterinin VKN/TCKN'si e-Fatura kullanıcısı mı?
  // GİB kesin kaynaktır: sorgu başarılıysa karar otomatik uygulanır ve kutu kilitlenir;
  // yalnız sorgu başarısız olursa elle karar verilir. Diyalog açılışında otomatik çağrılır.
  const handleMukellefSorgula = async (vknOverride?: string) => {
    const vkn = vknOverride ?? siteApproveTarget?.billingTaxNumber?.trim();
    if (!vkn) return;
    setMukellefQuerying(true);
    setMukellefResult(null);
    setMukellefVerified(false);
    try {
      const resp = await einvoiceApi.post(EINVOICE_API.EDM_MUKELLEF_SORGULA, { vknTckn: vkn });
      const isUser = !!resp.data?.isEInvoiceUser;
      setSiteCustomerIssuesInvoice(isUser);
      setMukellefVerified(true);
      setMukellefResult(isUser
        ? 'GİB kaydına göre müşteri e-Fatura mükellefi — iade faturasını müşteri kesecek.'
        : 'GİB kaydına göre müşteri e-Fatura mükellefi DEĞİL (e-Arşiv) — sistem iade faturası keser, gider pusulası takibi açılır.');
    } catch (error: any) {
      const msg = error?.response?.data?.message || 'Mükellef sorgulanamadı (e-Fatura entegrasyonu yapılandırılmamış olabilir).';
      setMukellefResult(`Sorgulama başarısız: ${msg}`);
    } finally {
      setMukellefQuerying(false);
    }
  };

  // Site iadesi gider pusulası belge no kaydı (expenseVoucherStatus: pending → issued)
  const handleVoucherSave = async () => {
    if (!voucherTarget || !voucherNo.trim()) return;
    setVoucherSaving(true);
    try {
      const response = await ecommerceApi.post('/api/ecommerce/returns/expense-voucher', {
        returnId: voucherTarget.id,
        documentNo: voucherNo.trim()
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Gider pusulası kaydedilemedi');
      }
      showSnackbar('Gider pusulası bilgisi kaydedildi.', 'success');
      setVoucherDialogOpen(false);
      setVoucherTarget(null);
      setVoucherNo('');
      setRefreshNonce((prev) => prev + 1);
    } catch (error: any) {
      const msg = error?.response?.data?.message || (error instanceof Error ? error.message : 'Bilinmeyen hata');
      showSnackbar(`Hata: ${msg}`, 'error');
    } finally {
      setVoucherSaving(false);
    }
  };

  const syncMarketplaceOrders = async () => {
    if (!companyId) {
      showSnackbar('Şirket bilgisi bulunamadı', 'error');
      return;
    }

    const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) =>
          setTimeout(() => reject(new Error(`${label} zaman aşımı (${Math.round(ms / 1000)}sn)`)), ms)
        )
      ]);
    };

    setSyncingOrders(true);
    showSnackbar('Pazaryerlerinden siparişler çekiliyor...', 'info');
    try {
      let n11Saved = 0;
      let n11Updated = 0;
      const n11Task = (async () => {
        try {
          const n11Response = await withTimeout(
            n11Api.post('/api/n11/orders/sync', { size: 50 }),
            25000,
            'N11 sipariş çekme'
          );
          const n11Data = n11Response.data;
          if (n11Data?.success) {
            const n11Orders = n11Data.orders || [];
            if (n11Orders.length > 0) {
              const ordersToSync = n11Orders.map((order: any) => {
                const rawItems = extractN11RawItems(order);
                let dateValue = null;
                let lastModifiedValue = null;
                if (order.date) {
                  try { dateValue = new Date(order.date).toISOString(); } catch { dateValue = null; }
                }
                if (order.n11LastModified) {
                  try { lastModifiedValue = new Date(order.n11LastModified).toISOString(); } catch { lastModifiedValue = null; }
                }
                return {
                  OrderId: order.id || '',
                  OrderNumber: order.orderNumber || '',
                  MarketplaceOrderId: order.id || order.n11OrderId || '',
                  MarketplaceOrderNumber: order.n11OrderNumber || order.orderNumber || '',
                  MarketplaceStatus: order.n11Status || null,
                  Date: dateValue,
                  Status: order.status || order.shipmentPackageStatus || order.n11Status || 'received',
                  TotalAmount: order.totalAmount || 0,
                  ItemsTotal: order.itemsTotal || 0,
                  CustomerName:
                    order.customerName ||
                    order.customerfullName ||
                    order.customerFullName ||
                    order.shippingAddress?.fullName ||
                    order.billingAddress?.fullName ||
                    null,
                  CustomerEmail: order.customerEmail || null,
                  CustomerPhone: order.shippingAddress?.phone || null,
                  ShippingAddress: JSON.stringify(order.shippingAddress || {}),
                  BillingAddress: JSON.stringify(order.billingAddress || {}),
                  CargoInfo: JSON.stringify(order),
                  ShipmentPackageStatus: order.shipmentPackageStatus || null,
                  PackageHistories: JSON.stringify(order.packageHistories || []),
                  MarketplaceLastModified: lastModifiedValue,
                  Items: JSON.stringify(rawItems)
                };
              });

              const syncResponse = await withTimeout(
                ecommerceApi.post('/api/ecommerce/orders/marketplace/sync', {
                  MarketplaceSource: 'n11',
                  Orders: ordersToSync
                }),
                25000,
                'N11 SQL sync'
              );
              n11Saved = syncResponse.data?.data?.saved || 0;
              n11Updated = syncResponse.data?.data?.updated || 0;
            }
          }
        } catch (n11Error: any) {
          console.error('❌ [ORDERS SYNC] N11 senkron hatası:', n11Error);
        }
      })();

      let trendyolProcessed = 0;
      let trendyolFailed = 0;
      let trendyolPullError: string | null = null;
      const trendyolTask = (async () => {
        try {
          const trendyolResponse = await withTimeout(
            postTrendyolMpWithFallback('/api/TrendyolOrderActions/pull-missing-orders', {
              maxPages: 20,
              size: 100
            }),
            25000,
            'Trendyol sipariş çekme'
          );
          const trendData = trendyolResponse.data?.data;
          trendyolProcessed = Number(trendData?.processed || 0);
          trendyolFailed = Number(trendData?.failed || 0);
          // 0 işlenmesi hata DEĞİLDİR: webhook siparişleri anlık düşürdüğü için
          // seçilen aralıkta çekilecek yeni/güncel paket bulunmaması normaldir.
        } catch (trendyolError: any) {
          const apiMsg =
            trendyolError?.response?.data?.message ||
            trendyolError?.response?.data?.Message ||
            trendyolError?.message ||
            '';
          trendyolPullError = /credentials/i.test(String(apiMsg))
            ? 'Bu şirkette Trendyol entegrasyonu tanımlı değil. Siparişler için önce Trendyol mağaza bağlantısını (satıcı/API bilgileri) ekleyin.'
            : apiMsg || 'Trendyol sipariş çekme başarısız';
          console.error('❌ [ORDERS SYNC] Trendyol manuel çekme hatası:', trendyolError);
        }
      })();

      let hepsiburadaProcessed = 0;
      let hepsiburadaFailed = 0;
      const hepsiburadaTask = (async () => {
        try {
          const hbResponse = await withTimeout(
            hepsiburadaApi.post('/api/hepsiburada/orders/pull-missing', {
              hours: 6,
              limit: 100
            }),
            25000,
            'Hepsiburada sipariş çekme'
          );
          const hbData = hbResponse.data?.data || hbResponse.data?.Data || {};
          hepsiburadaProcessed = Number(hbData?.processed || 0);
          hepsiburadaFailed = Number(hbData?.failed || 0);
        } catch (hbError) {
          console.error('❌ [ORDERS SYNC] Hepsiburada manuel çekme hatası:', hbError);
        }
      })();

      let pazaramaProcessed = 0;
      let pazaramaFailed = 0;
      const pazaramaTask = (async () => {
        try {
          const pazaramaResponse = await withTimeout(
            pazaramaApi.post('/api/pazarama/orders/pull', {
              hours: 24,
              maxPages: 10,
              size: 100
            }),
            25000,
            'Pazarama sipariş çekme'
          );
          const pzData = pazaramaResponse.data?.data || pazaramaResponse.data?.Data || pazaramaResponse.data || {};
          pazaramaProcessed = Number(pzData?.processed ?? pzData?.Processed ?? 0);
          pazaramaFailed = Number(pzData?.failed ?? pzData?.Failed ?? 0);
        } catch (pazaramaError) {
          console.error('❌ [ORDERS SYNC] Pazarama manuel çekme hatası:', pazaramaError);
        }
      })();

      let pttavmProcessed = 0;
      let pttavmFailed = 0;
      const pttavmTask = (async () => {
        try {
          const pttavmResponse = await withTimeout(
            pttavmApi.post('/api/pttavm/orders/sync', {}),
            25000,
            'PttAVM sipariş çekme'
          );
          const pttData = pttavmResponse.data?.data || pttavmResponse.data?.Data || pttavmResponse.data || {};
          pttavmProcessed = Number(pttData?.processed ?? pttData?.Processed ?? 0);
          pttavmFailed = Number(pttData?.failed ?? pttData?.Failed ?? 0);
        } catch (pttavmError) {
          console.error('❌ [ORDERS SYNC] PttAVM manuel çekme hatası:', pttavmError);
        }
      })();

      const returnsTask = (async () => {
        try {
          const returnsResponse = await withTimeout(
            n11Api.post('/api/n11/returns/sync?status=ALL'),
            15000,
            'N11 iade sync'
          );
          const returnsData = returnsResponse.data;
        } catch (returnsError) {
          console.error('❌ [ORDERS SYNC] N11 iade senkron hatası:', returnsError);
        }
      })();

      await Promise.allSettled([n11Task, trendyolTask, hepsiburadaTask, pazaramaTask, pttavmTask, returnsTask]);

      setRefreshNonce((prev) => prev + 1);

      showSnackbar(
        `N11: ${n11Saved} yeni / ${n11Updated} güncel, Trendyol: ${trendyolProcessed} işlendi${trendyolFailed > 0 ? ` (${trendyolFailed} hatalı)` : ''}, Hepsiburada: ${hepsiburadaProcessed} işlendi${hepsiburadaFailed > 0 ? ` (${hepsiburadaFailed} hatalı)` : ''}, Pazarama: ${pazaramaProcessed} işlendi${pazaramaFailed > 0 ? ` (${pazaramaFailed} hatalı)` : ''}, PttAVM: ${pttavmProcessed} işlendi${pttavmFailed > 0 ? ` (${pttavmFailed} hatalı)` : ''}`,
        'success'
      );
      if (trendyolPullError) {
        showSnackbar(`Trendyol: ${trendyolPullError}`, 'warning');
      } else if (trendyolProcessed > 0 && platformFilter === 'trendyol') {
        setActiveTab(0);
      }

    } catch (error) {
      console.error('❌ [ORDERS SYNC] Pazaryeri sipariş senkronizasyonu hatası:', error);
      showSnackbar(`Hata: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`, 'error');
    } finally {
      setSyncingOrders(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <ShoppingCartIcon sx={{ mr: 1.5, color: 'primary.main' }} />
        {t('ecommerceOrders.title')}
      </Typography>
      </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {orders.filter(canInvoiceWebsiteOrder).length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={invoicing}
              onClick={() => openInvoiceDialog(orders.filter(canInvoiceWebsiteOrder))}
            >
              Hepsini faturala
            </Button>
          }
        >
          {orders.filter(canInvoiceWebsiteOrder).length} ödenmiş e-ticaret siparişi henüz muhasebe faturasına işlenmedi.
        </Alert>
      )}

      {orphanPayments.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void (async () => {
                  try {
                    const res = await ecommerceApi.get('/api/ecommerce/payments/orphans', {
                      params: { minAgeMinutes: 10, page: 1, pageSize: 25 }
                    });
                    const list = res.data?.data ?? res.data?.Data;
                    setOrphanPayments(Array.isArray(list) ? list.map(mapOrphanPayment) : []);
                  } catch { /* noop */ }
                })();
              }}
            >
              Yenile
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            Orphan ödemeler ({orphanPayments.length}) — para alındı, sipariş oluşmamış
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Otomatik iade yok. Consume tekrar deneyin; başarısızsa PayTR panelinden manuel iade gerekir.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {orphanPayments.slice(0, 8).map((op) => (
              <Box
                key={op.id}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'space-between'
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {op.merchantOid} · {op.amount} {op.currency || 'TRY'}
                  {op.userEmail ? ` · ${op.userEmail}` : ''} · {op.ageMinutes} dk
                  {typeof op.reconcileAttemptCount === 'number' && op.reconcileAttemptCount > 0
                    ? ` · reconcile×${op.reconcileAttemptCount}`
                    : ''}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={retryingOrphanOid === op.merchantOid}
                  onClick={() => {
                    void (async () => {
                      setRetryingOrphanOid(op.merchantOid);
                      try {
                        const res = await mainApi.post('/api/payments/consume', {
                          MerchantOid: op.merchantOid
                        });
                        const ok = res.data?.success ?? res.data?.Success;
                        const payload = res.data?.data ?? res.data?.Data;
                        if (ok) {
                          showSnackbar(
                            `Sipariş oluşturuldu: ${payload?.orderNumber || payload?.OrderNumber || payload?.orderId || payload?.OrderId || op.merchantOid}`,
                            'success'
                          );
                          setOrphanPayments((prev) =>
                            prev.filter((x) => x.merchantOid !== op.merchantOid)
                          );
                          // sipariş listesini yenile
                          const cacheBuster = Date.now();
                          const ordersRes = await ecommerceApi.get(
                            `/api/ecommerce/orders?_t=${cacheBuster}`
                          );
                          if (ordersRes.data?.success && Array.isArray(ordersRes.data.data)) {
                            // loadOrders zaten map ediyor; soft refresh için mevcut load tetikle
                          }
                        } else {
                          showSnackbar(
                            res.data?.message || res.data?.Message || 'Consume başarısız — stok/üye kontrol edin veya manuel iade yapın.',
                            'warning'
                          );
                        }
                      } catch (err: any) {
                        showSnackbar(
                          err?.response?.data?.message || err?.message || 'Consume hatası',
                          'error'
                        );
                      } finally {
                        setRetryingOrphanOid(null);
                      }
                    })();
                  }}
                >
                  {retryingOrphanOid === op.merchantOid ? 'Deniyor…' : 'Consume dene'}
                </Button>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      {statsVisible && (
      <StatsCardsContainer>
        {!embedded && (
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<PendingActionsIcon />}
            color={STATS_COLORS.orange}
            title={String(orderCounts.pending)}
            subtitle="Onay Bekleyen"
            onClick={() => goToStatusTab('pending')}
          />
        </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<Inventory2Icon />}
            color={STATS_COLORS.blue}
            title={String(orderCounts.confirmed)}
            subtitle="Hazırlanıyor"
            onClick={() => goToStatusTab('confirmed')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<LocalShippingIcon />}
            color={STATS_COLORS.grey}
            title={String(orderCounts.shipped)}
            subtitle={embedded ? t('ecommerceOrders.tabs.onWaybill') : 'Kargoda'}
            onClick={() => goToStatusTab('shipped')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<DoneIcon />}
            color={STATS_COLORS.green}
            title={String(orderCounts.delivered)}
            subtitle="Teslim Edildi"
            onClick={() => goToStatusTab('delivered')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<CancelIcon />}
            color={STATS_COLORS.purple}
            title={String(orderCounts.cancel_requested)}
            subtitle="İptal Talepleri"
            onClick={() => goToStatusTab('cancel_requested')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatsCardItem
            icon={<CancelIcon />}
            color={STATS_COLORS.red}
            title={String(orderCounts.cancelled)}
            subtitle="İptal Edildi"
            onClick={() => goToStatusTab('cancelled')}
          />
        </Grid>
      </StatsCardsContainer>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        <TextField
          size="small"
          placeholder="Sipariş no veya müşteri..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {!embedded && PLATFORM_BUTTONS.map((btn) => {
          const active = platformFilter === btn.id;
          return (
            <Button
              key={btn.id}
              size="small"
              variant={active ? 'contained' : 'outlined'}
              color={active ? 'primary' : 'inherit'}
              onClick={() => { setPlatformFilter(btn.id); setPage(0); }}
              sx={{
                borderRadius: 2,
                minWidth: 96,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: active ? 'primary.main' : (theme) => alpha(theme.palette.primary.main, 0.28),
                color: active ? 'primary.contrastText' : 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: active ? 'primary.dark' : (theme) => alpha(theme.palette.primary.main, 0.06),
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.2,
                  mr: 0.75,
                  bgcolor: btn.bg,
                  color: btn.fg,
                }}
              >
                {btn.short}
              </Box>
              {btn.label}
            </Button>
          );
        })}
        {!embedded && (
        <Button
          size="small"
          variant={guestOnly ? 'contained' : 'outlined'}
          color={guestOnly ? 'primary' : 'inherit'}
          onClick={() => { setGuestOnly((v) => !v); setPage(0); }}
          sx={{
            borderRadius: 2,
            minWidth: 96,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: guestOnly ? 'primary.main' : (theme) => alpha(theme.palette.primary.main, 0.28),
          }}
        >
          Misafir
        </Button>
        )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!embedded && selectedOrderIds.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<WarehouseIcon />}
              onClick={() => {
                const selected = orders.filter(o => selectedOrderIds.includes(o.id));
                setWmsTransferOrders(selected.map(o => ({
                  id: o.id,
                  orderNumber: o.orderNumber,
                  customerName: o.customerName || `${(o as any).firstName || ''} ${(o as any).lastName || ''}`.trim() || t('ecommerceOrders.marketplaces.unknown'),
                  platform: o.marketplace || 'website',
                })));
                setWmsTransferModalOpen(true);
              }}
            >
              {t('ecommerceOrders.actions.transferToWms', { count: selectedOrderIds.length })}
            </Button>
          )}
          {selectedOrderIds.some((id) => {
            const selected = orders.find((order) => order.id === id);
            return selected ? canInvoiceWebsiteOrder(selected) : false;
          }) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={invoicing ? <CircularProgress size={16} /> : <ReceiptLongIcon />}
              disabled={invoicing}
              onClick={() =>
                openInvoiceDialog(orders.filter((order) => selectedOrderIds.includes(order.id) && canInvoiceWebsiteOrder(order)))
              }
            >
              Faturala ({orders.filter((order) => selectedOrderIds.includes(order.id) && canInvoiceWebsiteOrder(order)).length})
            </Button>
          )}
          {/* Pazaryeri çekimi yalnız e-ticaret modülünde; muhasebe sipariş sekmesini etkilemez. */}
          {!embedded && !isDemoCompany(companyId) && (
            <Button
              variant="outlined"
              size="small"
              startIcon={syncingOrders ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={syncMarketplaceOrders}
              disabled={syncingOrders}
            >
              {syncingOrders ? t('ecommerceOrders.actions.syncingOrders') : t('ecommerceOrders.actions.pullMarketplaceOrders')}
            </Button>
          )}
          <IconButton
            onClick={(event) => setHeaderMenuAnchorEl(event.currentTarget)}
            size="small"
            sx={{ border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`, borderRadius: 1.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={headerMenuAnchorEl}
            open={headerMenuOpen}
            onClose={() => setHeaderMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {!embedded && (
            <MenuItem
              onClick={() => {
                setHeaderMenuAnchorEl(null);
                setWebhookSettingsOpen(true);
              }}
            >
              <Box sx={{ mr: 1.25, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                <SettingsIcon fontSize="small" />
              </Box>
              <Typography variant="body2">Sipariş Bildirim Ayarları</Typography>
            </MenuItem>
            )}
            <MenuItem onClick={() => { setHeaderMenuAnchorEl(null); setStatsVisible(v => !v); }}>
              <Box sx={{ mr: 1.25, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                {statsVisible
                  ? <VisibilityIcon fontSize="small" />
                  : <VisibilityIcon fontSize="small" sx={{ opacity: 0.4 }} />}
              </Box>
              <Typography variant="body2">{statsVisible ? 'Özet Kartları Gizle' : 'Özet Kartları Göster'}</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Tab'lar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, bgcolor: 'grey.50', borderRadius: 1, px: 1 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {tab.label}
                  <Chip
                    label={getTabOrderCount(tab.status)}
                    size="small"
                    color={tab.color as any}
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Sipariş Listesi veya İade Talepleri */}
      {tabs[activeTab].status === 'return_requests' ? (
        // İade Talepleri Tablosu
      <Paper variant="outlined" sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.15), overflowX: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {([
                { key: 'marketplace', label: t('ecommerceOrders.table.marketplace'), sortKey: null },
                { key: 'returnNumber', label: 'İade No', sortKey: null },
                { key: 'orderNumber', label: t('ecommerceOrders.table.orderNumber'), sortKey: null },
                { key: 'customer', label: t('ecommerceOrders.table.customer'), sortKey: null },
                { key: 'product', label: 'Ürün', sortKey: null },
                { key: 'date', label: t('ecommerceOrders.table.date'), sortKey: 'date' as const },
                { key: 'amount', label: t('ecommerceOrders.table.amount'), sortKey: 'amount' as const },
                { key: 'status', label: t('ecommerceOrders.table.status'), sortKey: null },
                { key: 'reason', label: 'İade Nedeni', sortKey: null },
                { key: 'actions', label: t('ecommerceOrders.table.actions'), sortKey: null },
              ] as { key: string; label: string; sortKey: 'date' | 'amount' | null }[]).map((col) => (
                <TableCell
                  key={col.key}
                  sx={{ color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1, px: 1.5, whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.15)', '&:last-child': { borderRight: 'none' } }}
                >
                  {col.sortKey ? (
                    <TableSortLabel
                      active={returnSortBy === col.sortKey}
                      direction={returnSortBy === col.sortKey ? returnSortDir : 'desc'}
                      onClick={() => handleReturnSort(col.sortKey!)}
                      sx={{
                        color: 'inherit !important',
                        '&.MuiTableSortLabel-root': { color: 'inherit !important' },
                        '&.Mui-active': { color: 'inherit !important' },
                        '& .MuiTableSortLabel-icon': { color: 'inherit !important', opacity: returnSortBy === col.sortKey ? 1 : 0.5 },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReturns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('ecommerceOrders.returnTable.noReturns')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedReturns.map((returnItem) => {
                const marketplaceInfo = getMarketplaceVisualInfo(returnItem.marketplace, t);
                const normalizedStatus = (returnItem.status || '').replace(/[_\s-]/g, '').toUpperCase();
                const canManage = normalizeMarketplaceSource(returnItem.marketplace) === 'trendyol'
                  ? normalizedStatus === 'WAITINGINACTION'
                  : normalizedStatus === 'REQUESTED' || normalizedStatus === 'CANCELREQUESTED'
                    || Boolean(returnItem.isCancelRequest);
                return (
                  <TableRow
                    key={returnItem.id}
                    hover
                    onClick={() => openReturnDetail(returnItem)}
                    sx={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                  >
                    <TableCell>
                      {marketplaceInfo.hasLogo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: marketplaceInfo.bgColor, border: `1px solid ${marketplaceInfo.color}`, borderRadius: '16px', px: 1, py: 0.5, width: 'fit-content' }}>
                          <img src={marketplaceInfo.logoSrc} alt={marketplaceInfo.name} style={{ height: '20px', width: 'auto' }} />
                        </Box>
                      ) : (
                        <Chip label={marketplaceInfo.name} size="small" sx={{ backgroundColor: marketplaceInfo.bgColor, color: marketplaceInfo.color, fontWeight: 'bold', fontSize: '0.75rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{returnItem.claimReturnId || '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{returnItem.orderNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28 }}><PersonIcon /></Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{returnItem.buyerName || '-'}</Typography>
                          {returnItem.buyerEmail && returnItem.buyerEmail !== '-' && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                              {returnItem.buyerEmail}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {returnItem.productName}
                      </Typography>
                      {returnItem.attributesNames && (
                        <Typography variant="caption" color="text.secondary">{returnItem.attributesNames}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {returnItem.requestDate && returnItem.requestDate !== 'Invalid Date'
                          ? new Date(returnItem.requestDate).toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' })
                          : '-'}
                      </Typography>
                      {returnItem.requestDate && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {new Date(returnItem.requestDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatAmountWithCurrency(returnItem.finalPrice ?? 0, returnItem.currency)}
                      </Typography>
                      {returnItem.amountTl != null && returnItem.amountTl > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ≈ {formatTl(returnItem.amountTl)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getReturnStatusText(returnItem.status)}
                        size="small"
                        color={
                          normalizedStatus === 'REQUESTED' || normalizedStatus === 'CREATED' || normalizedStatus === 'WAITINGINACTION' || normalizedStatus === 'CANCELREQUESTED' ? 'warning'
                          : normalizedStatus === 'APPROVED' || normalizedStatus === 'ACCEPTED' || normalizedStatus === 'REFUNDED' ? 'success'
                          : normalizedStatus === 'CANCELLED' || normalizedStatus === 'REJECTED' ? 'error'
                          : 'default'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={returnItem.returnReasonDescription || ''} arrow placement="top">
                        <Typography variant="body2" sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: returnItem.returnReasonDescription ? 'help' : 'default' }}>
                          {returnItem.returnReasonType || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Detayları gör" arrow>
                          <IconButton size="small" onClick={() => openReturnDetail(returnItem)} sx={{ mr: 0.25 }}>
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canManage ? (
                          <>
                            <Button variant="contained" size="small" color="success"
                              onClick={() => handleApproveReturn(returnItem)}
                              disabled={approvingReturn === returnItem.id}
                              startIcon={approvingReturn === returnItem.id ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                              sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}
                            >
                              {approvingReturn === returnItem.id ? t('ecommerceOrders.returnActions.approving') : t('ecommerceOrders.returnActions.approve')}
                            </Button>
                            <Button variant="outlined" size="small" color="error"
                              onClick={() => { setReturnToReject(returnItem); setRejectReason(''); setRejectReturnModalOpen(true); }}
                              disabled={approvingReturn === returnItem.id}
                              startIcon={<CancelIcon />}
                              sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}
                            >
                              {t('ecommerceOrders.returnActions.reject')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              {normalizedStatus === 'APPROVED' || normalizedStatus === 'ACCEPTED' ? t('ecommerceOrders.returnStatus.approved')
                                : normalizedStatus === 'CANCELLED' ? t('ecommerceOrders.returnStatus.cancelled')
                                : normalizedStatus === 'REJECTED' ? t('ecommerceOrders.returnStatus.rejected')
                                : t('ecommerceOrders.returnStatus.completed')}
                            </Typography>
                            {/* Site iadesi: gider pusulası takibi (e-Arşiv/bireysel müşteri) */}
                            {normalizeMarketplaceSource(returnItem.marketplace) === 'website' && returnItem.expenseVoucherStatus === 'pending' && (
                              <Button variant="outlined" size="small" color="warning"
                                onClick={() => { setVoucherTarget(returnItem); setVoucherNo(''); setVoucherDialogOpen(true); }}
                                sx={{ minWidth: 'auto', px: 1, fontSize: '0.7rem' }}
                              >
                                Gider Pusulası Gir
                              </Button>
                            )}
                            {normalizeMarketplaceSource(returnItem.marketplace) === 'website' && returnItem.expenseVoucherStatus === 'issued' && (
                              <Tooltip title={`Gider pusulası: ${returnItem.expenseVoucherNo || '-'}`} arrow>
                                <Chip label="G.Pusulası ✓" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredReturns.length}
        page={returnPage}
        onPageChange={(_, p) => setReturnPage(p)}
        rowsPerPage={returnRowsPerPage}
        onRowsPerPageChange={(e) => { setReturnRowsPerPage(parseInt(e.target.value, 10)); setReturnPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Sayfa başına:"
        labelDisplayedRows={({ from, to, count }) => `${count} kayıttan ${from}-${to} arası`}
      />
      </Paper>
      ) : (
        // Normal sipariş listesi
        <Paper variant="outlined" sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, 0.15), overflowX: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell padding="checkbox" sx={{ color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1, px: 1.5, borderRight: '1px solid rgba(255,255,255,0.15)' }}>
                  <Checkbox
                    indeterminate={selectedOrderIds.length > 0 && selectedOrderIds.length < filteredOrders.length}
                    checked={filteredOrders.length > 0 && selectedOrderIds.length === filteredOrders.length}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedOrderIds(filteredOrders.map(o => o.id));
                      else setSelectedOrderIds([]);
                    }}
                    size="small"
                    sx={{ color: 'primary.contrastText', '&.Mui-checked': { color: 'primary.contrastText' } }}
                  />
                </TableCell>
                <TableCell sx={{ color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1, px: 1.5, whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.15)', width: 56, textAlign: 'center' }}>
                  {t('ecommerceOrders.table.rowNumber', { defaultValue: 'Sıra' })}
                </TableCell>
                {([
                  { key: 'marketplace', label: t('ecommerceOrders.table.marketplace'), sortKey: null },
                  { key: 'orderNumber', label: t('ecommerceOrders.table.orderNumber'), sortKey: null },
                  { key: 'customer', label: t('ecommerceOrders.table.customer'), sortKey: null },
                  { key: 'date', label: t('ecommerceOrders.table.date'), sortKey: 'date' as const },
                  { key: 'productCount', label: t('ecommerceOrders.table.productCount'), sortKey: 'productCount' as const },
                  { key: 'amount', label: t('ecommerceOrders.table.amount'), sortKey: 'amount' as const },
                  { key: 'status', label: t('ecommerceOrders.table.status'), sortKey: null },
                  { key: 'invoice', label: t('ecommerceOrders.table.invoice'), sortKey: null },
                  { key: 'eInvoice', label: t('ecommerceOrders.table.eInvoice'), sortKey: null },
                  { key: 'wms', label: t('ecommerceOrders.table.wms'), sortKey: null },
                  ...(tabs[activeTab]?.status === 'cancel_requested' ? [{ key: 'cancelReason', label: t('ecommerceOrders.table.cancelReason'), sortKey: null }] : []),
                  { key: 'actions', label: t('ecommerceOrders.table.actions'), sortKey: null },
                ] as { key: string; label: string; sortKey: 'date' | 'amount' | 'productCount' | null }[]).map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{ color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1, px: 1.5, whiteSpace: 'nowrap', borderRight: '1px solid rgba(255,255,255,0.15)', '&:last-child': { borderRight: 'none' } }}
                  >
                    {col.sortKey ? (
                      <TableSortLabel
                        active={sortBy === col.sortKey}
                        direction={sortBy === col.sortKey ? sortDir : 'desc'}
                        onClick={() => handleSort(col.sortKey!)}
                        sx={{
                          color: 'inherit !important',
                          '&.MuiTableSortLabel-root': { color: 'inherit !important' },
                          '&.Mui-active': { color: 'inherit !important' },
                          '& .MuiTableSortLabel-icon': { color: 'inherit !important', opacity: sortBy === col.sortKey ? 1 : 0.5 },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length === 0 ? (
              <TableRow>
                  <TableCell colSpan={tabs[activeTab]?.status === 'cancel_requested' ? 14 : 13} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('ecommerceOrders.table.noOrdersInStatus')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
                paginatedOrders.map((order, index) => {
                  const marketplaceInfo = getMarketplaceInfo(order);
                  const rowNumber = page * rowsPerPage + index + 1;
                  
                  return (
                <TableRow key={order.id} hover selected={selectedOrderIds.includes(order.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedOrderIds.includes(order.id)}
                        onChange={(e) => {
                          setSelectedOrderIds(prev =>
                            e.target.checked ? [...prev, order.id] : prev.filter(id => id !== order.id)
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'text.secondary', fontWeight: 600, width: 56 }}>
                      {rowNumber}
                    </TableCell>
                    <TableCell>
                      {marketplaceInfo.hasLogo ? (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          backgroundColor: marketplaceInfo.bgColor,
                          border: `1px solid ${marketplaceInfo.color}`,
                          borderRadius: '16px',
                          px: 1,
                          py: 0.5,
                          width: 'fit-content'
                        }}>
                          <img 
                            src={marketplaceInfo.logoSrc}
                            alt={marketplaceInfo.name}
                            style={{ 
                              height: '20px',
                              width: 'auto'
                            }}
                          />
                        </Box>
                      ) : (
                        <Chip
                          label={marketplaceInfo.name}
                          size="small"
                          sx={{
                            backgroundColor: marketplaceInfo.bgColor,
                            color: marketplaceInfo.color,
                            fontWeight: 'bold',
                            fontSize: '0.75rem'
                          }}
                        />
                      )}
                    </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      #{order.orderNumber}
                    </Typography>
                    {order.isInterCompanyB2B && (
                      <Chip
                        label="B2B"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                            {order.customerName || order.memberName || t('ecommerceOrders.table.customerNameUnknown')}
                        </Typography>
                        {order.isGuest && (
                          <Chip label="Misafir" size="small" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                        )}
                        
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatShortDate(order.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {t('ecommerceOrders.table.productCountValue', { count: order.items.length })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                      {/* N11 siparişleri için çift fiyat göster */}
                      {order.marketplace === 'n11' ? (
                        <Box>
                          <Typography variant="body2" fontWeight="medium" color="primary">
                            ₺{order.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('ecommerceOrders.table.itemsPrice')}: ₺{(order.itemsTotal || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {t('ecommerceOrders.table.totalPrice')}: ₺{order.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </Typography>
                          {/* Kargo ücreti hesapla ve göster */}
                          {order.totalAmount > (order.itemsTotal || 0) && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 'medium' }}>
                              {t('ecommerceOrders.table.shipping')}: ₺{(order.totalAmount - (order.itemsTotal || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {formatAmountWithCurrency(order.totalAmount, order.currency)}
                      </Typography>
                      {(() => {
                        const tl = orderTlEquivalent(order);
                        return tl != null ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            ≈ {formatTl(tl)}
                          </Typography>
                        ) : null;
                      })()}
                    </Box>
                      )}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(order.status)}
                  </TableCell>
                  <TableCell>
                    {order.invoiceId ? (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                        onClick={() => handleViewWebsiteInvoice(order)}
                      >
                        {order.invoiceNumber || t('ecommerceOrders.table.invoiced')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {order.isInterCompanyB2B && order.invoiceId ? (
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {['SENT', 'APPROVED'].includes((order.eInvoiceStatus || '').toUpperCase())
                          ? (order.eInvoiceStatus || t('ecommerceOrders.table.eInvoiceSent'))
                          : t('ecommerceOrders.table.eInvoiceDraft')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const wms = wmsStatuses[order.id];
                      if (!wms || !wms.isTransferred) return <Typography variant="caption" color="text.disabled">-</Typography>;
                      const statusMap: Record<string, { label: string; color: 'info' | 'warning' | 'success' | 'default' | 'error' }> = {
                        Pending: { label: t('ecommerceOrders.wms.pending'), color: 'info' },
                        AwaitingStock: { label: t('ecommerceOrders.wms.awaitingStock'), color: 'warning' },
                        InWave: { label: t('ecommerceOrders.wms.inWave'), color: 'info' },
                        Picking: { label: t('ecommerceOrders.wms.picking'), color: 'warning' },
                        Sorting: { label: t('ecommerceOrders.wms.sorting'), color: 'default' },
                        Packing: { label: t('ecommerceOrders.wms.packing'), color: 'info' },
                        Packed: { label: t('ecommerceOrders.wms.packed'), color: 'success' },
                        Shipped: { label: t('ecommerceOrders.wms.shipped'), color: 'default' },
                        Completed: { label: t('ecommerceOrders.wms.completed'), color: 'success' },
                        Cancelled: { label: t('ecommerceOrders.wms.cancelled'), color: 'default' },
                      };
                      const s = statusMap[wms.wmsStatus || ''] || { label: wms.wmsStatus || t('ecommerceOrders.wms.transferred'), color: 'info' as const };
                      return <Chip label={s.label} size="small" color={s.color} variant="outlined" />;
                    })()}
                  </TableCell>
                  {tabs[activeTab]?.status === 'cancel_requested' && (
                    <TableCell>
                      <Tooltip 
                        title={order.cancelRequest?.reason || 'Neden belirtilmemiş'} 
                        arrow
                        placement="top"
                      >
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 200, 
                            cursor: 'help',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {order.cancelRequest?.reason ? 
                            (order.cancelRequest.reason.length > 50 ? 
                              `${order.cancelRequest.reason.substring(0, 50)}...` : 
                              order.cancelRequest.reason
                            ) : 
                            'Neden belirtilmemiş'
                          }
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {updating === order.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        getStatusActions(order)
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredOrders.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Sayfa başına:"
          labelDisplayedRows={({ from, to, count }) =>
            `${count !== -1 ? count : `more than ${to}`} kayıttan ${from}-${to} arası`
          }
        />
      </Paper>
      )}

      {/* Sipariş Detayları Modal */}
      <OrderDetailsModal
        open={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        order={selectedOrder}
        getStatusChip={getStatusChip}
      />

      {/* Kargo Takip Numarası Modal */}
      <Dialog
        open={trackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalShippingIcon color="primary" />
            <Box>
              <Typography variant="h6">
                Kargo Takip Numarası Ekle
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sipariş #{orderToShip?.orderNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Siparişi kargoya verdiğinizde kargo firmasını seçin ve takip numarasını girin.
            </Typography>
            
            {/* Kargo Firması Seçimi */}
            <FormControl fullWidth>
              <InputLabel>Kargo Firması</InputLabel>
              <Select
                value={shippingCompany}
                label="Kargo Firması"
                onChange={(e) => setShippingCompany(e.target.value)}
              >
                {(normalizeMarketplaceSource(orderToShip?.marketplace) === 'hepsiburada' && hepsiburadaCargoCompanies.length > 0
                  ? hepsiburadaCargoCompanies
                  : shippingCompanies).map((company) => (
                  <MenuItem key={company.value} value={company.value}>
                    {company.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Takip Numarası */}
            <TextField
              fullWidth
              label="Kargo Takip Numarası"
              value={trackingNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value)}
              placeholder="Örn: 1234567890123"
              variant="outlined"
              helperText="Takip numarası girildikten sonra sipariş durumu 'Kargolandı' olarak güncellenecektir."
            />

            {/* Sipariş Bilgileri */}
            {orderToShip && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sipariş Bilgileri:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sipariş No: #{orderToShip.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri: {orderToShip.memberName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tutar: {formatAmountWithCurrency(orderToShip.totalAmount, orderToShip.currency)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingModalOpen(false)}>
            İptal
          </Button>
          <Button 
            onClick={updateOrderWithTracking}
            variant="contained"
            disabled={!trackingNumber.trim() || !shippingCompany || updating === orderToShip?.id}
            startIcon={updating === orderToShip?.id ? <CircularProgress size={16} /> : <LocalShippingIcon />}
          >
            {updating === orderToShip?.id ? 'Kaydediliyor...' : 'Kargola'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* B2B Sevkiyata Gönder Onay Modal */}
      <Dialog
        open={shipmentConfirmOpen}
        onClose={closeSendToShipmentModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalShippingIcon color="primary" />
            <Box>
              <Typography variant="h6">
                {embedded ? t('ecommerceOrders.actions.transferToWaybill') : 'Sevkiyata Gönder'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sipariş #{orderToSendShipment?.orderNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Sevk irsaliyesi kesilecek, sipariş <strong>{embedded ? t('ecommerceOrders.tabs.onWaybill') : 'Kargoda'}</strong> durumuna geçecek ve alıcının
                Aquasoft panelindeki <strong>Gelen Alış Siparişleri</strong> ekranına yansıyacak.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {orderToSendShipment
                ? `"${orderToSendShipment.orderNumber}" numaralı B2B siparişi sevkiyata gönderilsin mi?`
                : 'Bu B2B siparişi sevkiyata gönderilsin mi?'}
            </Typography>

            {orderToSendShipment && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sipariş Bilgileri:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sipariş No: #{orderToSendShipment.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri: {orderToSendShipment.customerName || orderToSendShipment.memberName || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tutar: {formatAmountWithCurrency(orderToSendShipment.totalAmount, orderToSendShipment.currency)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSendToShipmentModal} disabled={updating === orderToSendShipment?.id}>
            İptal
          </Button>
          <Button
            onClick={() => { void handleSendToShipment(); }}
            variant="contained"
            disabled={!orderToSendShipment || updating === orderToSendShipment?.id}
            startIcon={updating === orderToSendShipment?.id ? <CircularProgress size={16} /> : <LocalShippingIcon />}
          >
            {updating === orderToSendShipment?.id
              ? 'Gönderiliyor...'
              : (embedded ? t('ecommerceOrders.actions.transferToWaybill') : 'Sevkiyata Gönder')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sipariş İptal Onay Modal */}
      <Dialog
        open={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CancelIcon color="error" />
            <Box>
              <Typography variant="h6">
                Siparişi İptal Et
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sipariş #{orderToCancel?.orderNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Dikkat!</strong> Bu işlem geri alınamaz. Sipariş iptal edildiğinde müşteri bilgilendirilecek ve ödeme iade sürecine alınacaktır.
              </Typography>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Aşağıdaki siparişi iptal etmek istediğinizden emin misiniz?
            </Typography>

            {/* Sipariş Bilgileri */}
            {orderToCancel && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sipariş Bilgileri:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sipariş No: #{orderToCancel.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri: {orderToCancel.memberName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  E-posta: {orderToCancel.memberEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tutar: {formatAmountWithCurrency(orderToCancel.totalAmount, orderToCancel.currency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ürün Sayısı: {orderToCancel.items.length} ürün
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelModalOpen(false);
              setOrderToCancel(null);
            }}
          >
            Vazgeç
          </Button>
          <Button 
            onClick={cancelOrder}
            variant="contained"
            color="error"
            disabled={updating === orderToCancel?.id}
            startIcon={updating === orderToCancel?.id ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {updating === orderToCancel?.id ? 'İptal Ediliyor...' : 'Siparişi İptal Et'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin İptal Reddi Modal */}
      <Dialog
        open={adminCancelModalOpen}
        onClose={() => {
          setAdminCancelModalOpen(false);
          setOrderToAdminCancel(null);
          setAdminCancelReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CancelIcon color="warning" />
            <Box>
              <Typography variant="h6">
                İptal Talebini Reddet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sipariş #{orderToAdminCancel?.orderNumber}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                İptal talebini reddediyorsanız, lütfen müşteriye açıklayıcı bir neden belirtin. 
                Sipariş tekrar "Onaylandı" durumuna geçecektir.
              </Typography>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              İptal talebini neden reddediyorsunuz?
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Ret Nedeni"
              value={adminCancelReason}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setAdminCancelReason(e.target.value);
                }
              }}
              placeholder="Örn: Ürün zaten kargoya verildi, iptal işlemi yapılamaz..."
              variant="outlined"
              helperText={`${adminCancelReason.length}/500 karakter`}
              required
            />

            {/* Sipariş Bilgileri */}
            {orderToAdminCancel && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sipariş Bilgileri:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sipariş No: #{orderToAdminCancel.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri: {orderToAdminCancel.memberName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tutar: {formatAmountWithCurrency(orderToAdminCancel.totalAmount, orderToAdminCancel.currency)}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setAdminCancelModalOpen(false);
              setOrderToAdminCancel(null);
              setAdminCancelReason('');
            }}
          >
            Vazgeç
          </Button>
          <Button 
            onClick={async () => {
              if (!orderToAdminCancel || !companyId || adminCancelReason.trim().length < 10) {
                showSnackbar('Lütfen en az 10 karakter ret nedeni yazın.', 'warning');
                return;
              }

              setAdminCancelSubmitting(true);
              try {
                // SQL sipariş durumu: iptal talebini reddet → confirmed (+ CancelReason temizlenir)
                const response = await ecommerceApi.post('/api/ecommerce/orders/status', {
                  orderId: orderToAdminCancel.id,
                  status: 'confirmed',
                  rejectReason: adminCancelReason.trim()
                });

                if (!response.data?.success) {
                  throw new Error(response.data?.message || 'İptal talebi reddedilemedi');
                }

                setOrders(prev => prev.map(order =>
                  order.id === orderToAdminCancel.id
                    ? {
                        ...order,
                        status: 'confirmed' as any,
                        cancelRequest: undefined
                      }
                    : order
                ));

                setAdminCancelModalOpen(false);
                setOrderToAdminCancel(null);
                setAdminCancelReason('');

                showSnackbar('İptal talebi reddedildi ve sipariş onaylandı durumuna geçti!', 'success');
              } catch (error) {
                console.error('İptal talebi reddedilirken hata:', error);
                showSnackbar(
                  `Hata: ${error instanceof Error ? error.message : 'İptal talebi reddedilemedi!'}`,
                  'error'
                );
              } finally {
                setAdminCancelSubmitting(false);
              }
            }}
            variant="contained"
            color="warning"
            disabled={adminCancelSubmitting || adminCancelReason.trim().length < 10}
            startIcon={adminCancelSubmitting ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {adminCancelSubmitting ? 'Reddediliyor...' : 'İptal Talebini Reddet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Bildirimleri */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* N11 Yazdırma Modal */}
      <N11PrintModal
        open={printModalOpen}
        onClose={() => {
          setPrintModalOpen(false);
          setSelectedPrintOrder(null);
        }}
        order={selectedPrintOrder}
        companyId={companyId || undefined}
      />

      <TrendyolPrintModal
        open={trendyolPrintModalOpen}
        onClose={() => {
          setTrendyolPrintModalOpen(false);
          setSelectedTrendyolPrintOrder(null);
        }}
        order={selectedTrendyolPrintOrder}
        companyId={companyId || undefined}
      />

      <TrendyolCargoStickerModal
        open={trendyolStickerModalOpen}
        onClose={() => {
          setTrendyolStickerModalOpen(false);
          setSelectedTrendyolStickerOrder(null);
        }}
        order={selectedTrendyolStickerOrder}
        companyId={companyId || undefined}
      />

      <HepsiburadaPrintModal
        open={hepsiburadaPrintModalOpen}
        onClose={() => {
          setHepsiburadaPrintModalOpen(false);
          setSelectedHepsiburadaPrintOrder(null);
        }}
        order={selectedHepsiburadaPrintOrder}
        companyId={companyId || undefined}
      />

      {/* N11 Sipariş Detayları Modal */}
      <N11OrderDetailsModal
        open={showN11OrderModal}
        onClose={() => {
          setShowN11OrderModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder as any}
        getStatusChip={getStatusChip}
      />

      {/* Hepsiburada Sipariş Detayları Modal */}
      <HepsiburadaOrderDetailsModal
        open={showHepsiburadaOrderModal}
        onClose={() => {
          setShowHepsiburadaOrderModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder as any}
        getStatusChip={getStatusChip}
      />

      {/* Trendyol Sipariş Detayları Modal */}
      <TrendyolOrderDetailsModal
        open={showTrendyolOrderModal}
        onClose={() => {
          setShowTrendyolOrderModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder as any}
        getStatusChip={getStatusChip}
      />

      {/* İade / İptal detay popup — soft Fade + Grow */}
      <Dialog
        open={returnDetailOpen}
        onClose={() => {
          setReturnDetailOpen(false);
          setReturnDetailItem(null);
        }}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={{ enter: 280, exit: 180 }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.35)',
            backdropFilter: 'blur(4px)',
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.18)',
          },
        }}
      >
        {returnDetailItem && (
          <Grow in={returnDetailOpen} timeout={320}>
            <Box>
              <DialogTitle sx={{ pb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                      color: 'warning.dark',
                      flexShrink: 0,
                    }}
                  >
                    <AccessTimeIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                      {isWebsiteCancelReturn(returnDetailItem) ? 'İptal Talebi Detayı' : 'İade Talebi Detayı'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {returnDetailItem.claimReturnId || returnDetailItem.orderNumber}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={getReturnStatusText(returnDetailItem.status)}
                  />
                </Box>
              </DialogTitle>
              <DialogContent dividers sx={{ py: 2.5 }}>
                <Box
                  sx={{
                    mb: 2.5,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    İptal / talep zamanı
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {formatReturnDateTime(returnDetailItem.cancelRequestedAt || returnDetailItem.requestDate)}
                  </Typography>
                  {returnDetailItem.orderCreatedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      Sipariş tarihi: {formatReturnDateTime(returnDetailItem.orderCreatedAt)}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Sipariş No</Typography>
                    <Typography variant="body2" fontWeight={600}>{returnDetailItem.orderNumber || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Tutar</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatAmountWithCurrency(returnDetailItem.finalPrice ?? 0, returnDetailItem.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Müşteri</Typography>
                    <Typography variant="body2" fontWeight={600}>{returnDetailItem.buyerName || '-'}</Typography>
                    {returnDetailItem.buyerEmail && returnDetailItem.buyerEmail !== '-' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {returnDetailItem.buyerEmail}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Ürün</Typography>
                    <Typography variant="body2" fontWeight={600}>{returnDetailItem.productName || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      {isWebsiteCancelReturn(returnDetailItem) ? 'İptal nedeni' : 'İade nedeni'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.25 }}>
                      {returnDetailItem.returnReasonType || '-'}
                    </Typography>
                    {returnDetailItem.returnReasonDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {returnDetailItem.returnReasonDescription}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                  onClick={() => {
                    setReturnDetailOpen(false);
                    setReturnDetailItem(null);
                  }}
                >
                  Kapat
                </Button>
                {isWebsiteCancelReturn(returnDetailItem) && (
                  <>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setReturnDetailOpen(false);
                        setReturnToReject(returnDetailItem);
                        setRejectReason('');
                        setRejectReturnModalOpen(true);
                      }}
                    >
                      Reddet
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => {
                        setReturnDetailOpen(false);
                        void handleApproveReturn(returnDetailItem);
                      }}
                    >
                      Onayla
                    </Button>
                  </>
                )}
              </DialogActions>
            </Box>
          </Grow>
        )}
      </Dialog>

      {/* İade Reddetme Modal */}
      <Dialog
        open={rejectReturnModalOpen}
        onClose={() => {
          setRejectReturnModalOpen(false);
          setReturnToReject(null);
          setRejectReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CancelIcon color="error" />
            <Box>
              <Typography variant="h6">
                İade Talebini Reddet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                İade No: {returnToReject?.claimReturnId}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Dikkat!</strong> İade talebini reddediyorsanız, lütfen müşteriye açıklayıcı bir neden belirtin. 
                Bu işlem geri alınamaz.
              </Typography>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              İade talebini neden reddediyorsunuz?
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reddetme Nedeni"
              value={rejectReason}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setRejectReason(e.target.value);
                }
              }}
              placeholder="Örn: Ürün hasarlı değil, iade koşulları sağlanmıyor..."
              variant="outlined"
              helperText={`${rejectReason.length}/500 karakter (En az 5 karakter gerekli)`}
              required
              error={rejectReason.trim().length > 0 && rejectReason.trim().length < 5}
            />

            {/* İade Bilgileri */}
            {returnToReject && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  İade Talebi Bilgileri:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  İade No: {returnToReject.claimReturnId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sipariş No: {returnToReject.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ürün: {returnToReject.productName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri: {returnToReject.buyerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tutar: {formatAmountWithCurrency(returnToReject.finalPrice, returnToReject.currency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  İade Nedeni: {returnToReject.returnReasonType}
                </Typography>
                {returnToReject.returnReasonDescription && (
                  <Typography variant="body2" color="text.secondary">
                    Açıklama: {returnToReject.returnReasonDescription}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRejectReturnModalOpen(false);
              setReturnToReject(null);
              setRejectReason('');
            }}
          >
            Vazgeç
          </Button>
          <Button 
            onClick={handleRejectReturn}
            variant="contained"
            color="error"
            disabled={rejectingReturn || rejectReason.trim().length < 5}
            startIcon={rejectingReturn ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {rejectingReturn ? 'Reddediliyor...' : 'İade Talebini Reddet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Site İadesi Onay Diyaloğu — ters kayıt seçenekleri */}
      <Dialog open={siteApproveDialogOpen} onClose={() => !siteApproveSaving && setSiteApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>İadeyi Onayla ve İşle</DialogTitle>
        <DialogContent>
          {siteApproveTarget && (
            <Box sx={{ pt: 1 }}>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  İade No: <strong>{siteApproveTarget.claimReturnId}</strong> · Sipariş: <strong>{siteApproveTarget.orderNumber}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Müşteri: {siteApproveTarget.buyerName} · Tutar: {formatAmountWithCurrency(siteApproveTarget.finalPrice ?? 0, siteApproveTarget.currency)}
                  {siteApproveTarget.amountTl != null && siteApproveTarget.amountTl > 0 && ` (≈ ${formatTl(siteApproveTarget.amountTl)})`}
                </Typography>
                {siteApproveTarget.billingTitle && (
                  <Typography variant="body2" color="text.secondary">
                    Fatura: {siteApproveTarget.billingTitle}
                    {siteApproveTarget.billingTaxNumber ? ` (VKN/TCKN: ${siteApproveTarget.billingTaxNumber})` : ''}
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom>Para İadesi Yöntemi</Typography>
              <RadioGroup
                value={siteRefundMode}
                onChange={(e) => setSiteRefundMode(e.target.value as 'balance' | 'provider')}
                sx={{ mb: 1.5 }}
              >
                <FormControlLabel value="balance" control={<Radio size="small" />}
                  label="Cari bakiyeye alacak yaz (müşteri sonraki alışverişte kullanır)" />
                <FormControlLabel value="provider" control={<Radio size="small" />}
                  label="Ödeme sağlayıcıdan iade (banka çıkışı kaydı yazılır; sağlayıcı iadesini panelden yürütün)" />
              </RadioGroup>

              <FormControlLabel
                control={<Checkbox size="small" checked={siteRefundShipping} onChange={(e) => setSiteRefundShipping(e.target.checked)} />}
                label="Kargo bedeli de iade edilsin"
                sx={{ display: 'block', mb: 1.5 }}
              />

              <Divider sx={{ my: 1.5 }} />

              {siteApproveTarget.billingTaxNumber ? (
                <>
                  {/* GİB kesin kaynak: sorgu başarılıysa kutu kilitli (elle hata önlenir),
                      yalnız sorgu başarısızsa elle karar verilebilir */}
                  <FormControlLabel
                    control={
                      <Checkbox size="small" checked={siteCustomerIssuesInvoice}
                        disabled={mukellefVerified || mukellefQuerying}
                        onChange={(e) => setSiteCustomerIssuesInvoice(e.target.checked)} />
                    }
                    label={
                      'Müşteri e-Fatura mükellefi — iade faturasını MÜŞTERİ kesecek (sistem iade faturası kesmez)'
                      + (mukellefVerified ? ' · GİB\'den doğrulandı' : '')
                    }
                  />
                  <Box sx={{ mt: 1, ml: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button variant="outlined" size="small"
                      onClick={() => handleMukellefSorgula()}
                      disabled={mukellefQuerying}
                      startIcon={mukellefQuerying ? <CircularProgress size={14} /> : <SearchIcon />}
                      sx={{ fontSize: '0.72rem' }}
                    >
                      {mukellefQuerying ? 'GİB Sorgulanıyor...' : 'GİB\'de Tekrar Sorgula'}
                    </Button>
                  </Box>
                  {mukellefResult && (
                    <Alert severity={mukellefResult.startsWith('Sorgulama başarısız') ? 'warning' : 'info'} sx={{ mt: 1.5 }}>
                      {mukellefResult}
                      {mukellefResult.startsWith('Sorgulama başarısız') && ' Kararı yukarıdaki kutudan elle verebilirsiniz.'}
                    </Alert>
                  )}
                </>
              ) : (
                // VKN/TCKN yok (bireysel müşteri): mükellefiyet söz konusu değil — soru
                // hiç gösterilmez, iade faturasını her zaman sistem keser (e-Arşiv yolu).
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Siparişte VKN/TCKN yok (bireysel müşteri): iade faturasını sistem keser, gider pusulası takibi otomatik açılır.
                </Typography>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                {siteCustomerIssuesInvoice
                  ? 'Sistem iade faturası KESMEZ ve cari ters kayıt YAZMAZ; yalnız para iadesi işlenir. Müşterinin iade faturası muhasebeye ulaşınca kayıt tamamlanır.'
                  : 'Sistem İADE faturası keser, cari ters kayıt ve stok girişini işler. Bireysel (e-Arşiv) müşteride gider pusulası takibi otomatik açılır.'}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSiteApproveDialogOpen(false); setSiteApproveTarget(null); }} disabled={siteApproveSaving}>
            Vazgeç
          </Button>
          <Button
            onClick={handleSiteApproveConfirm}
            variant="contained"
            color="success"
            disabled={siteApproveSaving}
            startIcon={siteApproveSaving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {siteApproveSaving ? 'İşleniyor...' : 'Onayla ve İşle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Site İadesi Gider Pusulası Girişi */}
      <Dialog open={voucherDialogOpen} onClose={() => !voucherSaving && setVoucherDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Gider Pusulası Bilgisi</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {voucherTarget && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                İade No: <strong>{voucherTarget.claimReturnId}</strong> · Sipariş: <strong>{voucherTarget.orderNumber}</strong>
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              label="Gider Pusulası Belge No"
              value={voucherNo}
              onChange={(e) => setVoucherNo(e.target.value)}
              placeholder="Örn: GP-2026-000123"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Belge muhasebede düzenlendikten sonra numarasını buraya girin; iade kaydındaki
              gider pusulası durumu "girildi" olarak işaretlenir.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVoucherDialogOpen(false); setVoucherTarget(null); setVoucherNo(''); }} disabled={voucherSaving}>
            Vazgeç
          </Button>
          <Button
            onClick={handleVoucherSave}
            variant="contained"
            disabled={voucherSaving || !voucherNo.trim()}
            startIcon={voucherSaving ? <CircularProgress size={16} /> : <DoneIcon />}
          >
            {voucherSaving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={invoiceDialogOpen}
        onClose={() => !invoicing && setInvoiceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Siparişi faturalandır</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {ordersToInvoice.length === 1
              ? `#${ordersToInvoice[0].orderNumber} siparişi ön muhasebe satış faturasına dönüştürülecek. Cari yoksa fatura bilgilerinden otomatik açılır.`
              : `${ordersToInvoice.length} sipariş ön muhasebe satış faturasına dönüştürülecek. Cari yoksa fatura bilgilerinden otomatik açılır.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceDialogOpen(false)} disabled={invoicing}>
            Vazgeç
          </Button>
          <Button
            onClick={handleConfirmInvoice}
            color="primary"
            variant="contained"
            disabled={invoicing}
            startIcon={invoicing ? <CircularProgress size={16} /> : <ReceiptLongIcon />}
          >
            {invoicing ? 'Faturalanıyor...' : 'Faturala'}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceDisplayModal
        open={invoiceDisplayOpen}
        onClose={() => {
          setInvoiceDisplayOpen(false);
          setSelectedInvoiceForDisplay(null);
        }}
        invoice={selectedInvoiceForDisplay}
        companyId={companyId}
        loading={loadingInvoiceDetail}
      />

      {/* WMS Transfer Modal */}
      <WmsTransferModal
        open={wmsTransferModalOpen}
        onClose={() => { setWmsTransferModalOpen(false); setWmsTransferOrders([]); }}
        orders={wmsTransferOrders}
        onTransferred={async () => {
          setSelectedOrderIds([]);
          try {
            const ids = orders.map(o => o.id);
            if (ids.length > 0) {
              const statuses = await orderTransferService.getTransferStatusBulk(ids);
              const statusMap: Record<string, TransferStatus> = {};
              statuses.forEach(s => { statusMap[s.ecommerceOrderId] = s; });
              setWmsStatuses(statusMap);
            }
          } catch { /* ignore */ }
        }}
      />
      <TrendyolWebhookSettingsModal
        open={webhookSettingsOpen}
        onClose={() => setWebhookSettingsOpen(false)}
        integrationLabel="Trendyol"
      />

      <EInvoiceSendModal
        open={!!eInvoiceSendOrder?.invoiceId}
        invoiceId={eInvoiceSendOrder?.invoiceId || null}
        companyId={companyId}
        onClose={() => setEInvoiceSendOrder(null)}
        onSuccess={() => {
          setEInvoiceSendOrder(null);
          setRefreshNonce((prev) => prev + 1);
        }}
      />

    </Box>
  );
};

export default OrdersManagement;
